import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  WheelEvent as ReactWheelEvent,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccessTime,
  ArrowOutward,
  Apartment,
  Block,
  CheckCircle,
  ContactPhone,
  DataUsage,
  GraphicEq,
  HelpOutline,
  KeyboardArrowDown,
  PersonOff,
  PhoneInTalk,
  Psychology,
  Public,
  QueryStats,
  Refresh,
  Speed,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
} from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import ButtonTabs from '../components/ButtonTabs';
import CampaignStatusChip from '../components/CampaignStatusChip';
import SystemCockpitTab from '../components/dashboard/SystemCockpitTab';
import InsightPanel from '../components/InsightPanel';
import { useAuth } from '../contexts/AuthContext';
import useDashboardCockpit, {
  DASHBOARD_CARRIER_ANALYTICS_ENABLED,
} from '../hooks/useDashboardCockpit';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import { fetchDashboardCockpitTodayAiAgent } from '../services/dashboard';
import type {
  DashboardCockpitAnalyticsSliceName,
  DashboardCockpitAiAgentPatternSeries,
  DashboardCockpitCampaignBaseHealthCampaign,
  DashboardCockpitCampaignBaseHealthPoint,
  DashboardCockpitCampaignBaseHealthSlice,
  DashboardCockpitCarrierRow,
  DashboardCockpitCarrierSlice,
  DashboardCockpitCountRow,
  DashboardCockpitConsumptionEfficiencyPoint,
  DashboardCockpitConsumptionEfficiencySlice,
  DashboardCockpitConsumptionEvolutionSlice,
  DashboardCockpitConversationQualityPoint,
  DashboardCockpitConversationQualitySlice,
  DashboardCockpitHumanAttendanceAttentionPoint,
  DashboardCockpitHumanAttendanceOperator,
  DashboardCockpitHumanAttendanceOperatorPoint,
  DashboardCockpitHumanAttendanceOperatorSeries,
  DashboardCockpitHumanAttendancePatternsSlice,
  DashboardCockpitHumanAttendancePerformancePoint,
  DashboardCockpitHumanAttendancePerformanceSlice,
  DashboardCockpitPerformanceEvolutionSlice,
  DashboardCockpitReverseIvrAudioSeries,
  DashboardCockpitReverseIvrPerformancePoint,
  DashboardCockpitTerritoryLevel,
  DashboardCockpitTerritoryPattern,
  DashboardCockpitTerritoryPatterns,
  DashboardCockpitTimeHeatmapPoint,
  DashboardCockpitTodayAiAgentSummary,
  DashboardCockpitTodayAiSummary,
  DashboardCockpitTodayHumanAttendance,
  DashboardCockpitTodayReverseIvrSummary,
  DashboardCockpitTopCampaign,
} from '../types';
import { formatDateOnly, formatDateTime } from '../utils/date';
import {
  buildTodayAiCostReturn,
  buildTodayAiEfficiency,
  buildTodayAiRecentComparison,
} from '../utils/dashboardTodayAi';
import type {
  TodayAiComparisonRow,
  TodayAiCostReturnMetrics,
  TodayAiEfficiencyComparisonRow,
  TodayAiEfficiencyMetrics,
} from '../utils/dashboardTodayAi';
import {
  buildTodayReverseIvrFunnel,
  buildTodayReverseIvrRecentComparison,
  getReverseIvrScopeDigitDistribution,
  getReverseIvrScopeHeatmap,
  getReverseIvrScopePerformance,
} from '../utils/dashboardTodayReverseIvr';
import type { TodayReverseIvrComparisonRow } from '../utils/dashboardTodayReverseIvr';

const HISTORY_DAYS = 30;
const DISPLAY_FONT = '"Barlow Condensed", "Oswald", sans-serif';
const AI_CONSUMPTION_SERIES_KEY = 'aiConsumedMinutes';
const TELEPHONY_CONSUMPTION_SERIES_KEY = 'telephonyConsumedMinutes';
const AI_CONSUMPTION_TREND_SERIES_KEY = 'aiConsumedMinutesTrend';
const TELEPHONY_CONSUMPTION_TREND_SERIES_KEY = 'telephonyConsumedMinutesTrend';
const ANSWERED_CALLS_SERIES_KEY = 'answeredCalls';
const CONVERTED_CALLS_SERIES_KEY = 'convertedCalls';
const ANSWERED_CALLS_TREND_SERIES_KEY = 'answeredCallsTrend';
const CONVERTED_CALLS_TREND_SERIES_KEY = 'convertedCallsTrend';
const AI_AGENT_CONVERTED_SERIES_KEY_PREFIX = 'aiAgentConvertedCalls';
const AI_AGENT_CONVERTED_TREND_SERIES_KEY_PREFIX = 'aiAgentConvertedCallsTrend';
const AI_AGENT_PERFORMANCE_SERIES_COLORS = ['#7C3AED', '#F59E0B', '#0EA5E9', '#EC4899', '#14B8A6'];
const AI_AGENT_CONSUMPTION_SERIES_KEY_PREFIX = 'aiAgentConsumedMinutes';
const AI_AGENT_CONSUMPTION_TREND_SERIES_KEY_PREFIX = 'aiAgentConsumedMinutesTrend';
const AI_AGENT_CONSUMPTION_SERIES_COLORS = ['#10B981', '#84CC16', '#F59E0B', '#7C3AED', '#EC4899', '#0EA5E9', '#F97316', '#22C55E'];
const EFFICIENCY_AGGREGATE_SERIES_KEY = 'efficiencyValue';
const EFFICIENCY_AGGREGATE_TREND_SERIES_KEY = 'efficiencyValueTrend';
const AI_AGENT_EFFICIENCY_SERIES_KEY_PREFIX = 'aiAgentEfficiencyValue';
const AI_AGENT_EFFICIENCY_TREND_SERIES_KEY_PREFIX = 'aiAgentEfficiencyValueTrend';
const AI_AGENT_EFFICIENCY_SERIES_COLORS = AI_AGENT_PERFORMANCE_SERIES_COLORS;
const CONVERSATION_QUALITY_AGGREGATE_SERIES_KEY = 'conversationQualityValue';
const CONVERSATION_QUALITY_AGGREGATE_TREND_SERIES_KEY = 'conversationQualityValueTrend';
const AI_AGENT_CONVERSATION_QUALITY_SERIES_KEY_PREFIX = 'aiAgentConversationQualityValue';
const AI_AGENT_CONVERSATION_QUALITY_TREND_SERIES_KEY_PREFIX = 'aiAgentConversationQualityValueTrend';
const AI_AGENT_CONVERSATION_QUALITY_SERIES_COLORS = AI_AGENT_PERFORMANCE_SERIES_COLORS;
const CAMPAIGN_BASE_HEALTH_SERIES_KEY_PREFIX = 'campaignBaseHealthValue';
const CAMPAIGN_BASE_HEALTH_COLORS = ['#F43F5E', '#F59E0B', '#0EA5E9', '#7C3AED', '#14B8A6', '#EC4899'];
const CAMPAIGN_BASE_HEALTH_MAX_CHART_CAMPAIGNS = 5;
const HUMAN_ATTENDANCE_OPERATOR_SERIES_KEY_PREFIX = 'humanAttendanceOperatorValue';
const HUMAN_ATTENDANCE_OPERATOR_COLORS = ['#22C55E', '#0EA5E9', '#F59E0B', '#EC4899', '#7C3AED', '#14B8A6', '#F97316', '#84CC16'];
const DENSE_CHART_TOOLTIP_SCROLL_CLASS = 'dashboard-dense-chart-tooltip-scroll';
const DENSE_CHART_TOOLTIP_WRAPPER_STYLE: CSSProperties = {
  zIndex: 30,
  pointerEvents: 'auto',
};
const DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN = 8;
const DENSE_CHART_SCROLL_AREA_SX = {
  width: '100%',
  overflowX: { xs: 'auto', md: 'visible' },
  overflowY: 'visible',
  pb: 0.5,
};
const HISTORY_LINE_CHART_MARGIN = { top: 8, right: 32, bottom: 4, left: 8 };
const HISTORY_LINE_CHART_X_AXIS_PADDING = { left: 24, right: 24 };
const TIME_HEATMAP_BUCKET_SIZE_HOURS = 3;
const TIME_HEATMAP_LOW_SAMPLE_ATTEMPTS = 5;
const TERRITORY_LOW_SAMPLE_ATTEMPTS = 5;
const DUQUE_DASHBOARD_ADVANCED_AI_PANELS_BLOCKED_TENANT_ID = '0698f6d9-b5e1-76e4-8000-c603c0d29581';
const DASHBOARD_PRODUCT_VIEW_PARAM = 'view';
const DASHBOARD_PRODUCT_VIEW_AI = 'ai';
const DASHBOARD_PRODUCT_VIEW_REVERSE_IVR = 'reverse_ivr';
const DASHBOARD_TODAY_AI_AGENT_ALL = 'all';

type DenseChartTooltipViewportOffset = {
  x: number;
  y: number;
};

type PanelTone = 'rose' | 'teal' | 'amber' | 'cobalt' | 'neutral';
type TerritoryMetricKey = 'answerRate' | 'conversionRate' | 'heardToEndRate' | 'dtmfRate';
type TerritoryProduct = 'ai' | 'reverseIvr';
type DashboardProductViewMode =
  | typeof DASHBOARD_PRODUCT_VIEW_AI
  | typeof DASHBOARD_PRODUCT_VIEW_REVERSE_IVR;
type AiConsumptionEfficiencyMetricKey =
  | 'aiMinutesPerAnswer'
  | 'aiMinutesPerConversion'
  | 'answersPer100AiMinutes'
  | 'conversionsPer100AiMinutes'
  | 'telephonyMinutesPerConversion';
type ConversationQualityMetricKey =
  | 'averageConvertedSeconds'
  | 'shortInterruptionRate'
  | 'longUnconvertedRate';
type CampaignBaseHealthMetricKey =
  | 'answerRate'
  | 'invalidRate'
  | 'unreachableRate'
  | 'noAttendanceRate'
  | 'voicemailRate'
  | 'mismatchRate'
  | 'optoutRate';
type HumanAttendancePerformanceMetricKey =
  | 'humanAnsweredCalls'
  | 'humanAnswerRate'
  | 'averageWaitSeconds'
  | 'averageHandleSeconds';
type HumanAttendanceOperatorMetricKey =
  | 'handledCalls'
  | 'humanAnswerRate'
  | 'averageHandleSeconds'
  | 'averageWaitSeconds'
  | 'unavailableSeconds';
type ReverseIvrPerformanceMetricKey =
  | 'answerRate'
  | 'heardToEndRate'
  | 'dtmfRate'
  | 'mappedRate'
  | 'timeoutRate';
type DashboardCampaignMetricColumn = {
  key: string;
  label: string;
  value: string;
};
type DashboardAnalyticsSliceStateMap = Record<DashboardCockpitAnalyticsSliceName, { status: string }>;

const TELEPHONY_BLOCK_REASON_LABEL_KEYS = {
  telephony_balance_unavailable: 'pages:dashboard.budget.telephonyBlocked.balanceUnavailable',
  telephony_overage_pending: 'pages:dashboard.budget.telephonyBlocked.overagePending',
} as const;

function getTonePalette(theme: Theme, tone: PanelTone) {
  const paletteMap = {
    rose: theme.palette.primary.main,
    teal: theme.palette.success.main,
    amber: theme.palette.warning.main,
    cobalt: theme.palette.info.main,
    neutral: theme.palette.text.secondary,
  } as const;
  const accent = paletteMap[tone];
  return {
    accent,
    border: alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.11),
    soft: alpha(accent, theme.palette.mode === 'dark' ? 0.1 : 0.07),
    text: accent,
  };
}

function getBusinessOutcomeAccent(theme: Theme, key: string) {
  if (key === 'converted') {
    return theme.palette.success.main;
  }
  if (key === 'mismatch') {
    return theme.palette.error.main;
  }
  return theme.palette.warning.main;
}

function buildPanelSx(theme: Theme, tone: PanelTone, minHeight?: number): SystemStyleObject<Theme> {
  const palette = getTonePalette(theme, tone);
  return {
    minHeight,
    borderRadius: '18px',
    border: `1px solid ${palette.border}`,
    backgroundColor: alpha(theme.palette.background.paper, 0.96),
    boxShadow: `0 10px 26px ${alpha(theme.palette.common.black, 0.1)}`,
    overflow: 'hidden',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '32%',
      height: '1px',
      backgroundColor: alpha(palette.accent, 0.34),
      pointerEvents: 'none',
    },
  };
}

function isDashboardAnalyticsSliceLoading(
  analyticsSlices: DashboardAnalyticsSliceStateMap,
  sliceName: DashboardCockpitAnalyticsSliceName,
) {
  return analyticsSlices[sliceName]?.status === 'loading';
}

function isDashboardAnalyticsSliceError(
  analyticsSlices: DashboardAnalyticsSliceStateMap,
  sliceName: DashboardCockpitAnalyticsSliceName,
) {
  return analyticsSlices[sliceName]?.status === 'error';
}

function buildMetricChipSx(accent: string, isActive: boolean): SystemStyleObject<Theme> {
  return {
    height: 26,
    fontWeight: 800,
    bgcolor: alpha(accent, isActive ? 0.16 : 0.04),
    border: `1px solid ${alpha(accent, isActive ? 0.22 : 0.12)}`,
    color: isActive ? accent : 'text.secondary',
    opacity: isActive ? 1 : 0.72,
    transition: 'background-color 160ms ease, opacity 160ms ease, color 160ms ease',
    '& .MuiChip-icon': {
      color: 'inherit',
      fontSize: 16,
    },
    '&:hover': {
      bgcolor: alpha(accent, isActive ? 0.2 : 0.08),
      opacity: 1,
    },
  };
}

function buildDropdownMetricLabel(label: string) {
  return (
    <Stack component="span" direction="row" alignItems="center" gap={0.35}>
      <Box component="span">{label}</Box>
      <KeyboardArrowDown sx={{ fontSize: 16, color: 'inherit', ml: -0.1 }} />
    </Stack>
  );
}

function buildMetricHelpLabel(label: string) {
  return (
    <Stack component="span" direction="row" alignItems="center" gap={0.35}>
      <Box component="span">{label}</Box>
      <HelpOutline
        sx={{
          fontSize: 13,
          color: 'inherit',
          opacity: 0.78,
          cursor: 'help',
        }}
      />
    </Stack>
  );
}

function formatNullableDuration(
  value: number | null | undefined,
  formatter: (seconds: number | null | undefined, isAverage?: boolean) => string,
  fallback: string,
) {
  if (value == null) {
    return fallback;
  }
  return formatter(value, true);
}

function formatNullablePercent(
  value: number | null | undefined,
  formatter: (value: number | null | undefined, isAverage?: boolean) => string,
  fallback: string,
) {
  if (value == null) {
    return fallback;
  }
  return formatter(value);
}

/**
 * Redirects wheel gestures from dense chart areas into the open tooltip list.
 *
 * Recharts tooltips are positioned over the chart, while the user's pointer can
 * remain on the plot line. This handler keeps page scroll stable and lets
 * operators inspect long tooltip lists without moving the cursor away from the
 * chart. When the tooltip is already at its top/bottom edge, the page regains
 * the wheel event naturally.
 */
function handleDenseChartTooltipWheel(event: ReactWheelEvent<HTMLElement>) {
  if (!event.deltaY) {
    return;
  }
  const scrollTargets = Array.from(
    document.querySelectorAll<HTMLElement>(`.${DENSE_CHART_TOOLTIP_SCROLL_CLASS}`),
  );
  const scrollTarget = scrollTargets.find((target) => {
    const rect = target.getBoundingClientRect();
    return (
      rect.width > 0
      && rect.height > 0
      && target.scrollHeight > target.clientHeight
      && getComputedStyle(target).visibility !== 'hidden'
    );
  });

  if (!scrollTarget) {
    return;
  }

  const maxScrollTop = scrollTarget.scrollHeight - scrollTarget.clientHeight;
  const nextScrollTop = Math.max(0, Math.min(maxScrollTop, scrollTarget.scrollTop + event.deltaY));
  if (Math.abs(nextScrollTop - scrollTarget.scrollTop) < 0.5) {
    return;
  }

  scrollTarget.scrollTop = nextScrollTop;
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Compact shell for chart tooltips that may contain many series rows.
 *
 * The shell clamps itself to the browser viewport after Recharts chooses the
 * initial chart-relative position. This keeps dense hover boxes readable near
 * every edge without turning the chart card itself into a scroll container.
 */
function DenseChartTooltipShell({
  accent,
  title,
  children,
}: {
  accent: string;
  title: string;
  children: ReactNode;
}) {
  const theme = useTheme();
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const viewportOffsetRef = useRef<DenseChartTooltipViewportOffset>({ x: 0, y: 0 });
  const [viewportOffset, setViewportOffset] = useState<DenseChartTooltipViewportOffset>({ x: 0, y: 0 });

  useLayoutEffect(() => {
    const updateViewportOffset = () => {
      const tooltip = tooltipRef.current;
      if (!tooltip) {
        return;
      }
      const currentOffset = viewportOffsetRef.current;
      const rect = tooltip.getBoundingClientRect();
      const baseLeft = rect.left - currentOffset.x;
      const baseRight = rect.right - currentOffset.x;
      const baseTop = rect.top - currentOffset.y;
      const baseBottom = rect.bottom - currentOffset.y;
      let nextX = 0;
      let nextY = 0;
      const maxRight = window.innerWidth - DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN;
      const maxBottom = window.innerHeight - DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN;

      if (baseRight + nextX > maxRight) {
        nextX += maxRight - (baseRight + nextX);
      }
      if (baseLeft + nextX < DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN) {
        nextX += DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN - (baseLeft + nextX);
      }
      if (baseBottom + nextY > maxBottom) {
        nextY += maxBottom - (baseBottom + nextY);
      }
      if (baseTop + nextY < DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN) {
        nextY += DENSE_CHART_TOOLTIP_VIEWPORT_MARGIN - (baseTop + nextY);
      }

      if (
        Math.abs(nextX - currentOffset.x) < 0.5
        && Math.abs(nextY - currentOffset.y) < 0.5
      ) {
        return;
      }
      const nextOffset = { x: nextX, y: nextY };
      viewportOffsetRef.current = nextOffset;
      setViewportOffset(nextOffset);
    };

    const frame = window.requestAnimationFrame(updateViewportOffset);
    window.addEventListener('resize', updateViewportOffset);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateViewportOffset);
    };
  }, [children, title]);

  return (
    <Paper
      ref={tooltipRef}
      elevation={0}
      sx={{
        p: 1.35,
        borderRadius: 2,
        border: `1px solid ${alpha(accent, 0.24)}`,
        backgroundColor: alpha(theme.palette.background.default, 0.96),
        minWidth: 320,
        maxWidth: 680,
        maxHeight: { xs: 240, md: 270 },
        overflow: 'hidden',
        transform: viewportOffset.x || viewportOffset.y
          ? `translate3d(${viewportOffset.x}px, ${viewportOffset.y}px, 0)`
          : 'none',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, mb: 0.25 }}>
        {title}
      </Typography>
      <Stack
        className={DENSE_CHART_TOOLTIP_SCROLL_CLASS}
        spacing={0.9}
        sx={{ maxHeight: { xs: 190, md: 210 }, overflowY: 'auto', pr: 0.4 }}
      >
        {children}
      </Stack>
    </Paper>
  );
}

function DashboardSectionHeader({
  eyebrow,
  title,
  subtitle,
  action,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      gap={1.5}
      sx={{ position: 'relative', zIndex: 1, mb: 2.5 }}
    >
      <Box>
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            letterSpacing: '0.18em',
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontFamily: DISPLAY_FONT,
            lineHeight: 0.95,
            letterSpacing: '-0.04em',
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography color="text.secondary" sx={{ mt: 0.8, maxWidth: 720 }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
  );
}

function EmptyPanelState({ label }: { label: string }) {
  return (
    <Box
      sx={{
        minHeight: 140,
        px: 2.4,
        py: 2.2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '18px',
        border: (theme) => `1px dashed ${alpha(theme.palette.text.secondary, 0.28)}`,
        color: 'text.secondary',
      }}
    >
      <Typography variant="body2" sx={{ textAlign: 'center', maxWidth: 320 }}>
        {label}
      </Typography>
    </Box>
  );
}

function DashboardAnalyticsSliceSkeleton({ height = 320 }: { height?: number }) {
  return <Skeleton variant="rounded" height={height} />;
}

function DashboardAnalyticsSliceErrorState({
  label,
  retryLabel,
  onRetry,
}: {
  label: string;
  retryLabel: string;
  onRetry: () => void;
}) {
  return (
    <Alert
      severity="warning"
      action={(
        <Button color="inherit" size="small" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    >
      {label}
    </Alert>
  );
}

/** Return the localized visible label for one carrier row. */
function resolveCarrierDashboardLabel(row: DashboardCockpitCarrierRow, t: TFunction) {
  return row.key === 'unknown' || row.label === 'unknown'
    ? t('pages:dashboard.patterns.carrier.unknown')
    : row.label;
}

/** Return the localized label for the dominant carrier terminal signal. */
function resolveCarrierProblemLabel(problemKey: string, t: TFunction) {
  if (!problemKey) {
    return t('pages:dashboard.patterns.carrier.noDominantProblem');
  }
  return t(`pages:dashboard.patterns.carrier.problemLabels.${problemKey}`, problemKey);
}

function CarrierAnalyticsRows({
  rows,
  emptyLabel,
  t,
  formatNumber,
  formatPercent,
}: {
  rows: DashboardCockpitCarrierRow[];
  emptyLabel: string;
  t: TFunction;
  formatNumber: (value: number) => string;
  formatPercent: (value: number, isAverage?: boolean) => string;
}) {
  if (rows.length === 0) {
    return <EmptyPanelState label={emptyLabel} />;
  }

  return (
    <Stack gap={1}>
      {rows.slice(0, 6).map((row) => (
        <Box
          key={row.key}
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(160px, 1.2fr) repeat(4, minmax(84px, 0.8fr))' },
            gap: { xs: 0.8, md: 1.2 },
            alignItems: 'center',
            px: 1.4,
            py: 1.1,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            backgroundColor: alpha(theme.palette.background.default, 0.32),
          })}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800 }} noWrap>
              {resolveCarrierDashboardLabel(row, t)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {t('pages:dashboard.patterns.carrier.mainProblem', {
                problem: resolveCarrierProblemLabel(row.mainProblem, t),
              })}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('pages:dashboard.patterns.carrier.columns.calls')}
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {formatNumber(row.attemptedCalls)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('pages:dashboard.patterns.carrier.columns.answerRate')}
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {formatPercent(row.answerRate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('pages:dashboard.patterns.carrier.columns.conversionRate')}
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {formatPercent(row.conversionRate)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('pages:dashboard.patterns.carrier.columns.problemRate')}
            </Typography>
            <Typography sx={{ fontWeight: 800 }}>
              {formatPercent(row.problemRate)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function CarrierAnalyticsPanel({
  data,
  t,
  formatNumber,
  formatPercent,
}: {
  data: DashboardCockpitCarrierSlice;
  t: TFunction;
  formatNumber: (value: number) => string;
  formatPercent: (value: number, isAverage?: boolean) => string;
}) {
  const theme = useTheme();

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.carrier.title')}
      subtitle={t('pages:dashboard.patterns.carrier.subtitle')}
      titleIcon={<ContactPhone />}
      accent={theme.palette.info.main}
    >
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1.2}>
          <Chip
            size="small"
            label={t('pages:dashboard.patterns.carrier.todayTotal', {
              total: formatNumber(data.today.totalAttemptedCalls),
            })}
          />
          <Chip
            size="small"
            label={t('pages:dashboard.patterns.carrier.historyTotal', {
              total: formatNumber(data.history.totalAttemptedCalls),
              days: formatNumber(data.windowDays),
            })}
          />
        </Stack>
        <Box>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>
            {t('pages:dashboard.patterns.carrier.todayTitle')}
          </Typography>
          <CarrierAnalyticsRows
            rows={data.today.rows}
            emptyLabel={t('pages:dashboard.patterns.carrier.emptyToday')}
            t={t}
            formatNumber={formatNumber}
            formatPercent={formatPercent}
          />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 900, mb: 1 }}>
            {t('pages:dashboard.patterns.carrier.historyTitle', {
              days: formatNumber(data.windowDays),
            })}
          </Typography>
          <CarrierAnalyticsRows
            rows={data.history.rows}
            emptyLabel={t('pages:dashboard.patterns.carrier.emptyHistory')}
            t={t}
            formatNumber={formatNumber}
            formatPercent={formatPercent}
          />
        </Box>
      </Stack>
    </InsightPanel>
  );
}

function buildCampaignMetricColumns(
  campaign: DashboardCockpitTopCampaign,
  t: ReturnType<typeof useTranslation>['t'],
  formatNumber: (value: number) => string,
  formatPercent: (value: number, isAverage?: boolean) => string,
  formatCurrency: (value: number, currency?: string) => string,
): DashboardCampaignMetricColumn[] {
  const columns: DashboardCampaignMetricColumn[] = [
    {
      key: 'connectedCalls',
      label: t('pages:dashboard.campaigns.connectedCalls'),
      value: formatNumber(campaign.connectedCalls),
    },
    {
      key: 'answerRate',
      label: t('pages:dashboard.campaigns.answerRate'),
      value: formatPercent(campaign.answerRate),
    },
    {
      key: 'conversionRate',
      label: t('pages:dashboard.campaigns.conversionRate'),
      value: formatPercent(campaign.conversionRate),
    },
  ];

  if (campaign.avgEstimatedCostUsd != null) {
    columns.push({
      key: 'avgEstimatedCost',
      label: t('pages:dashboard.campaigns.avgEstimatedCost'),
      value: formatCurrency(campaign.avgEstimatedCostUsd, 'USD'),
    });
  }

  return columns;
}

function resolveOperationalBlockLabel(
  reason: string | null | undefined,
  fallbackKey: string,
  t: ReturnType<typeof useTranslation>['t'],
  lookup: Record<string, string>,
) {
  if (!reason) {
    return t(fallbackKey);
  }
  const translationKey = lookup[reason];
  return translationKey ? t(translationKey) : reason;
}

type TimeHeatmapMetricKey = 'answerRate' | 'conversionRate' | 'heardToEndRate';

type TimeHeatmapMetricOption = {
  key: TimeHeatmapMetricKey;
  label: string;
  accent: string;
};

type TimeHeatmapBucket = {
  weekday: string;
  isoWeekday: number;
  startHour: number;
  endHour: number;
  attempts: number;
  answered: number;
  conversionEligible: number;
  converted: number;
  heardToEnd: number;
  dtmf: number;
  answerRate: number;
  conversionRate: number;
  heardToEndRate: number;
};

type TimeHeatmapMetricDomain = {
  minPositiveValue: number;
  maxValue: number;
  hasPositiveSpread: boolean;
};

function calculateRate(numerator: number, denominator: number) {
  if (!denominator) {
    return 0;
  }
  return Number(((numerator / denominator) * 100).toFixed(2));
}

function aggregateAiAgentHeatmapSeries(
  seriesList: DashboardCockpitAiAgentPatternSeries[],
): DashboardCockpitTimeHeatmapPoint[] {
  const pointsByKey = new Map<string, DashboardCockpitTimeHeatmapPoint>();

  seriesList.forEach((series) => {
    (series.heatmap ?? []).forEach((point) => {
      const key = `${point.weekday}:${point.isoWeekday}:${point.hour}`;
      const current = pointsByKey.get(key) ?? {
        weekday: point.weekday,
        isoWeekday: point.isoWeekday,
        hour: point.hour,
        attempts: 0,
        answered: 0,
        conversionEligible: 0,
        converted: 0,
        heardToEnd: 0,
        dtmf: 0,
        answerRate: 0,
        conversionRate: 0,
        heardToEndRate: 0,
        dtmfRate: 0,
      };
      current.attempts += point.attempts ?? 0;
      current.answered += point.answered ?? 0;
      current.conversionEligible += point.conversionEligible ?? 0;
      current.converted += point.converted ?? 0;
      current.heardToEnd += point.heardToEnd ?? 0;
      current.dtmf += point.dtmf ?? 0;
      pointsByKey.set(key, current);
    });
  });

  return Array.from(pointsByKey.values())
    .map((point) => ({
      ...point,
      answerRate: calculateRate(point.answered, point.attempts),
      conversionRate: calculateRate(point.converted, point.conversionEligible),
      heardToEndRate: calculateRate(point.heardToEnd, point.attempts),
      dtmfRate: calculateRate(point.dtmf, point.attempts),
    }))
    .sort((left, right) => (
      left.isoWeekday - right.isoWeekday
      || left.hour - right.hour
    ));
}

function aggregateTerritoryPatternRows(
  rows: DashboardCockpitTerritoryPattern[],
): DashboardCockpitTerritoryPattern[] {
  const rowsByKey = new Map<string, DashboardCockpitTerritoryPattern>();

  rows.forEach((row) => {
    const key = `${row.level}:${row.code}:${row.parentCode ?? ''}`;
    const current = rowsByKey.get(key) ?? {
      ...row,
      attempts: 0,
      answered: 0,
      conversionEligible: 0,
      converted: 0,
      heardToEnd: 0,
      dtmf: 0,
      optout: 0,
      mismatch: 0,
      answerRate: 0,
      conversionRate: 0,
      heardToEndRate: 0,
      dtmfRate: 0,
      sampleEligible: false,
    };
    current.attempts += row.attempts ?? 0;
    current.answered += row.answered ?? 0;
    current.conversionEligible += row.conversionEligible ?? 0;
    current.converted += row.converted ?? 0;
    current.heardToEnd += row.heardToEnd ?? 0;
    current.dtmf += row.dtmf ?? 0;
    current.optout += row.optout ?? 0;
    current.mismatch += row.mismatch ?? 0;
    rowsByKey.set(key, current);
  });

  return Array.from(rowsByKey.values()).map((row) => ({
    ...row,
    answerRate: calculateRate(row.answered, row.attempts),
    conversionRate: calculateRate(row.converted, row.conversionEligible),
    heardToEndRate: calculateRate(row.heardToEnd, row.attempts),
    dtmfRate: calculateRate(row.dtmf, row.attempts),
    sampleEligible: row.attempts >= TERRITORY_LOW_SAMPLE_ATTEMPTS,
  }));
}

function aggregateAiAgentTerritorySeries(
  seriesList: DashboardCockpitAiAgentPatternSeries[],
): DashboardCockpitTerritoryPatterns {
  return {
    regions: aggregateTerritoryPatternRows(seriesList.flatMap((series) => series.territory?.regions ?? [])),
    states: aggregateTerritoryPatternRows(seriesList.flatMap((series) => series.territory?.states ?? [])),
    ddds: aggregateTerritoryPatternRows(seriesList.flatMap((series) => series.territory?.ddds ?? [])),
  };
}

function buildEmptyHeatmapBucket(weekday: string, isoWeekday: number, startHour: number): TimeHeatmapBucket {
  return {
    weekday,
    isoWeekday,
    startHour,
    endHour: startHour + TIME_HEATMAP_BUCKET_SIZE_HOURS - 1,
    attempts: 0,
    answered: 0,
    conversionEligible: 0,
    converted: 0,
    heardToEnd: 0,
    dtmf: 0,
    answerRate: 0,
    conversionRate: 0,
    heardToEndRate: 0,
  };
}

/**
 * Aggregates the API's stable weekday x hour matrix into fewer columns.
 *
 * Three-hour buckets keep the operational pattern readable on the cockpit
 * while preserving exact rates by summing the original denominators first.
 */
function buildTimeHeatmapBuckets(
  data: DashboardCockpitTimeHeatmapPoint[],
  weekdayKeys: string[],
) {
  const bucketsByKey = new Map<string, TimeHeatmapBucket>();
  const bucketStarts = Array.from(
    { length: 24 / TIME_HEATMAP_BUCKET_SIZE_HOURS },
    (_, index) => index * TIME_HEATMAP_BUCKET_SIZE_HOURS,
  );

  weekdayKeys.forEach((weekday, index) => {
    bucketStarts.forEach((startHour) => {
      bucketsByKey.set(
        `${weekday}:${startHour}`,
        buildEmptyHeatmapBucket(weekday, index + 1, startHour),
      );
    });
  });

  data.forEach((point) => {
    const startHour = Math.floor(point.hour / TIME_HEATMAP_BUCKET_SIZE_HOURS) * TIME_HEATMAP_BUCKET_SIZE_HOURS;
    const bucket = bucketsByKey.get(`${point.weekday}:${startHour}`);
    if (!bucket) {
      return;
    }
    bucket.attempts += point.attempts ?? 0;
    bucket.answered += point.answered ?? 0;
    bucket.conversionEligible += point.conversionEligible ?? 0;
    bucket.converted += point.converted ?? 0;
    bucket.heardToEnd += point.heardToEnd ?? 0;
    bucket.dtmf += point.dtmf ?? 0;
  });

  const buckets = Array.from(bucketsByKey.values()).map((bucket) => ({
    ...bucket,
    answerRate: calculateRate(bucket.answered, bucket.attempts),
    conversionRate: calculateRate(bucket.converted, bucket.conversionEligible),
    heardToEndRate: calculateRate(bucket.heardToEnd, bucket.attempts),
  }));

  return {
    bucketStarts,
    bucketsByWeekday: weekdayKeys.map((weekday) => ({
      weekday,
      buckets: buckets.filter((bucket) => bucket.weekday === weekday),
    })),
    buckets,
  };
}

function buildHeatmapMetricDomain(
  buckets: TimeHeatmapBucket[],
  selectedMetric: TimeHeatmapMetricKey,
): TimeHeatmapMetricDomain {
  const positiveValues = buckets
    .filter((bucket) => bucket.attempts > 0)
    .map((bucket) => Number(bucket[selectedMetric] ?? 0))
    .filter((value) => value > 0);
  if (!positiveValues.length) {
    return { minPositiveValue: 0, maxValue: 0, hasPositiveSpread: false };
  }
  const minPositiveValue = Math.min(...positiveValues);
  const maxValue = Math.max(...positiveValues);
  return {
    minPositiveValue,
    maxValue,
    hasPositiveSpread: maxValue > minPositiveValue,
  };
}

function getHeatmapCellIntensity(
  value: number,
  metricDomain: TimeHeatmapMetricDomain,
  attempts: number,
) {
  if (attempts <= 0 || value <= 0 || metricDomain.maxValue <= 0) {
    return 0;
  }
  if (!metricDomain.hasPositiveSpread) {
    return attempts < TIME_HEATMAP_LOW_SAMPLE_ATTEMPTS ? 0.2 : 0.44;
  }
  const normalizedValue = Math.min(
    Math.max((value - metricDomain.minPositiveValue) / (metricDomain.maxValue - metricDomain.minPositiveValue), 0),
    1,
  );
  const sampleMultiplier = attempts < TIME_HEATMAP_LOW_SAMPLE_ATTEMPTS ? 0.45 : 1;
  return (0.12 + Math.pow(normalizedValue, 0.85) * 0.58) * sampleMultiplier;
}

function getHeatmapCellBackground(
  theme: Theme,
  accent: string,
  value: number,
  metricDomain: TimeHeatmapMetricDomain,
  attempts: number,
) {
  if (attempts <= 0) {
    return alpha(theme.palette.text.secondary, 0.055);
  }
  const intensity = getHeatmapCellIntensity(value, metricDomain, attempts);
  if (intensity <= 0) {
    return alpha(theme.palette.text.secondary, 0.07);
  }
  return alpha(accent, intensity);
}

function getHeatmapCellBorder(
  theme: Theme,
  accent: string,
  value: number,
  metricDomain: TimeHeatmapMetricDomain,
  attempts: number,
) {
  if (attempts <= 0) {
    return alpha(theme.palette.text.secondary, 0.08);
  }
  const intensity = getHeatmapCellIntensity(value, metricDomain, attempts);
  if (intensity <= 0) {
    return alpha(theme.palette.text.secondary, 0.14);
  }
  return alpha(accent, Math.min(0.16 + intensity * 0.62, 0.58));
}

function formatHourRange(startHour: number, endHour: number) {
  return `${String(startHour).padStart(2, '0')}h-${String(endHour + 1).padStart(2, '0')}h`;
}

/**
 * Renders historical time patterns as a weekday x time-window heatmap.
 *
 * This replaces separate flattened charts because the useful operational
 * question is the intersection of day and hour, not either dimension alone.
 */
function TimePatternHeatmap({
  data,
  aiAgentSeries = [],
  title,
  subtitle,
  titleIcon,
  metricOptions,
  weekdayLabelMap,
  emptyLabel,
  lowSampleLabel,
  attemptsLabel,
  answeredLabel,
  convertedLabel,
  heardToEndLabel,
  topSlotsLabel,
  formatNumber,
  formatPercent,
}: {
  data: DashboardCockpitTimeHeatmapPoint[];
  aiAgentSeries?: DashboardCockpitAiAgentPatternSeries[];
  title: string;
  subtitle: string;
  titleIcon?: ReactNode;
  metricOptions: TimeHeatmapMetricOption[];
  weekdayLabelMap: Record<string, string>;
  emptyLabel: string;
  lowSampleLabel: string;
  attemptsLabel: string;
  answeredLabel: string;
  convertedLabel: string;
  heardToEndLabel: string;
  topSlotsLabel: string;
  formatNumber: (value: number) => string;
  formatPercent: (value: number, isAverage?: boolean) => string;
}) {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<TimeHeatmapMetricKey>(
    metricOptions[0]?.key ?? 'answerRate',
  );
  const [hiddenConversionAgentIds, setHiddenConversionAgentIds] = useState<Set<string>>(() => new Set());
  const [conversionMenuAnchorEl, setConversionMenuAnchorEl] = useState<HTMLElement | null>(null);
  const hasConversionAgentMenu = aiAgentSeries.length > 1 && metricOptions.some((option) => option.key === 'conversionRate');
  const visibleConversionAgentSeries = useMemo(
    () => (
      hasConversionAgentMenu
        ? aiAgentSeries.filter((series) => !hiddenConversionAgentIds.has(series.id))
        : []
    ),
    [aiAgentSeries, hasConversionAgentMenu, hiddenConversionAgentIds],
  );
  const isConversionMenuOpen = Boolean(conversionMenuAnchorEl);
  const activeHeatmapData = useMemo(
    () => (
      hasConversionAgentMenu && selectedMetric === 'conversionRate'
        ? aggregateAiAgentHeatmapSeries(visibleConversionAgentSeries)
        : data
    ),
    [data, hasConversionAgentMenu, selectedMetric, visibleConversionAgentSeries],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];
  const weekdayKeys = useMemo(() => Object.keys(weekdayLabelMap), [weekdayLabelMap]);
  const heatmap = useMemo(
    () => buildTimeHeatmapBuckets(activeHeatmapData, weekdayKeys),
    [activeHeatmapData, weekdayKeys],
  );
  const hasData = heatmap.buckets.some((bucket) => bucket.attempts > 0);
  const metricDomain = useMemo(
    () => buildHeatmapMetricDomain(heatmap.buckets, selectedMetric),
    [heatmap.buckets, selectedMetric],
  );
  const topBuckets = useMemo(() => {
    const ranked = heatmap.buckets
      .filter((bucket) => bucket.attempts >= TIME_HEATMAP_LOW_SAMPLE_ATTEMPTS)
      .sort((left, right) => (
        Number(right[selectedMetric] ?? 0) - Number(left[selectedMetric] ?? 0)
        || right.attempts - left.attempts
      ));
    const fallback = heatmap.buckets
      .filter((bucket) => bucket.attempts > 0)
      .sort((left, right) => (
        Number(right[selectedMetric] ?? 0) - Number(left[selectedMetric] ?? 0)
        || right.attempts - left.attempts
      ));
    return (ranked.length ? ranked : fallback).slice(0, 3);
  }, [heatmap.buckets, selectedMetric]);

  useEffect(() => {
    if (!metricOptions.some((option) => option.key === selectedMetric)) {
      setSelectedMetric(metricOptions[0]?.key ?? 'answerRate');
    }
  }, [metricOptions, selectedMetric]);
  useEffect(() => {
    setHiddenConversionAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);

  const handleMetricClick = (option: TimeHeatmapMetricOption, event: MouseEvent<HTMLElement>) => {
    if (option.key === 'conversionRate' && hasConversionAgentMenu) {
      setSelectedMetric(option.key);
      setConversionMenuAnchorEl(event.currentTarget);
      return;
    }
    setSelectedMetric(option.key);
  };
  const toggleConversionAgentSeries = (agentId: string) => {
    setHiddenConversionAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      return nextVisibleAgentCount > 0 ? nextHidden : current;
    });
  };

  if (!activeMetric) {
    return (
      <InsightPanel
        title={title}
        subtitle={subtitle}
        titleIcon={titleIcon}
        accent={theme.palette.info.main}
      >
        <EmptyPanelState label={emptyLabel} />
      </InsightPanel>
    );
  }

  const metricActions = (
    <>
      {metricOptions.map((option) => {
        const isSelected = option.key === selectedMetric;
        const opensAgentMenu = option.key === 'conversionRate' && hasConversionAgentMenu;
        return (
          <Chip
            key={option.key}
            label={opensAgentMenu ? buildDropdownMetricLabel(option.label) : option.label}
            icon={
              option.key === 'conversionRate'
                ? <CheckCircle />
                : option.key === 'heardToEndRate'
                  ? <GraphicEq />
                  : <PhoneInTalk />
            }
            clickable
            aria-haspopup={opensAgentMenu ? 'menu' : undefined}
            aria-expanded={opensAgentMenu ? isConversionMenuOpen : undefined}
            onClick={(event) => handleMetricClick(option, event)}
            sx={buildMetricChipSx(option.accent, isSelected)}
          />
        );
      })}
      {hasConversionAgentMenu ? (
        <Menu
          anchorEl={conversionMenuAnchorEl}
          open={isConversionMenuOpen}
          onClose={() => setConversionMenuAnchorEl(null)}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                minWidth: 220,
                maxWidth: 340,
              },
            },
          }}
        >
          {aiAgentSeries.map((series, index) => {
            const isVisible = !hiddenConversionAgentIds.has(series.id);
            const color = AI_AGENT_PERFORMANCE_SERIES_COLORS[
              index % AI_AGENT_PERFORMANCE_SERIES_COLORS.length
            ];
            return (
              <MenuItem
                key={series.id}
                dense
                onClick={() => toggleConversionAgentSeries(series.id)}
              >
                <Checkbox
                  size="small"
                  checked={isVisible}
                  sx={{
                    color,
                    '&.Mui-checked': {
                      color,
                    },
                  }}
                />
                <ListItemText
                  primary={series.label}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </>
  );

  return (
    <InsightPanel
      title={title}
      subtitle={subtitle}
      titleIcon={titleIcon}
      accent={activeMetric.accent}
      actions={metricActions}
    >
      {!hasData ? (
        <EmptyPanelState label={emptyLabel} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 260px' },
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ overflowX: 'auto', pb: 0.4 }}>
            <Box
              sx={{
                minWidth: 720,
                display: 'grid',
                gridTemplateColumns: `84px repeat(${heatmap.bucketStarts.length}, minmax(62px, 1fr))`,
                gap: 0.75,
              }}
            >
              <Box />
              {heatmap.bucketStarts.map((startHour) => (
                <Typography
                  key={startHour}
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatHourRange(startHour, startHour + TIME_HEATMAP_BUCKET_SIZE_HOURS - 1)}
                </Typography>
              ))}

              {heatmap.bucketsByWeekday.map((row) => (
                <Fragment key={row.weekday}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 800,
                      alignSelf: 'center',
                    }}
                  >
                    {weekdayLabelMap[row.weekday] || row.weekday}
                  </Typography>
                  {row.buckets.map((bucket) => {
                    const value = Number(bucket[selectedMetric] ?? 0);
                    const hasAttempts = bucket.attempts > 0;
                    const isLowSample = bucket.attempts > 0 && bucket.attempts < TIME_HEATMAP_LOW_SAMPLE_ATTEMPTS;
                    return (
                      <Tooltip
                        key={`${bucket.weekday}:${bucket.startHour}`}
                        arrow
                        title={(
                          <Box sx={{ py: 0.4 }}>
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800 }}>
                              {weekdayLabelMap[bucket.weekday] || bucket.weekday} · {formatHourRange(bucket.startHour, bucket.endHour)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {activeMetric.label}: {formatPercent(value)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {attemptsLabel}: {formatNumber(bucket.attempts)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {answeredLabel}: {formatNumber(bucket.answered)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {convertedLabel}: {formatNumber(bucket.converted)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block' }}>
                              {heardToEndLabel}: {formatNumber(bucket.heardToEnd)}
                            </Typography>
                            {isLowSample ? (
                              <Typography variant="caption" sx={{ display: 'block', color: 'warning.light', mt: 0.4 }}>
                                {lowSampleLabel}
                              </Typography>
                            ) : null}
                          </Box>
                        )}
                      >
                        <Box
                          tabIndex={0}
                          sx={{
                            minHeight: 42,
                            px: 0.8,
                            py: 0.7,
                            borderRadius: '10px',
                            border: `1px solid ${getHeatmapCellBorder(theme, activeMetric.accent, value, metricDomain, bucket.attempts)}`,
                            backgroundColor: getHeatmapCellBackground(
                              theme,
                              activeMetric.accent,
                              value,
                              metricDomain,
                              bucket.attempts,
                            ),
                            opacity: hasAttempts ? 1 : 0.46,
                            outline: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            transition: 'transform 120ms ease, border-color 120ms ease, background-color 120ms ease',
                            '&:hover, &:focus-visible': {
                              transform: 'translateY(-1px)',
                              borderColor: alpha(activeMetric.accent, 0.72),
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.primary',
                              fontWeight: 900,
                              fontVariantNumeric: 'tabular-nums',
                              lineHeight: 1.05,
                            }}
                          >
                            {hasAttempts ? formatPercent(value) : '-'}
                          </Typography>
                          {hasAttempts ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                fontSize: '0.68rem',
                                fontVariantNumeric: 'tabular-nums',
                                lineHeight: 1.1,
                              }}
                            >
                              {formatNumber(bucket.attempts)}
                            </Typography>
                          ) : null}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Fragment>
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: '14px',
              border: `1px solid ${alpha(activeMetric.accent, 0.18)}`,
              backgroundColor: alpha(activeMetric.accent, 0.055),
              p: 1.4,
              minHeight: 0,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
              {topSlotsLabel}
            </Typography>
            <Stack spacing={0.9} sx={{ mt: 1.1 }}>
              {topBuckets.map((bucket, index) => {
                const value = Number(bucket[selectedMetric] ?? 0);
                return (
                  <Box
                    key={`${bucket.weekday}:${bucket.startHour}`}
                    sx={{
                      p: 1,
                      borderRadius: '10px',
                      border: `1px solid ${alpha(activeMetric.accent, 0.16)}`,
                      backgroundColor: alpha(theme.palette.background.paper, 0.54),
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                      {index + 1}. {weekdayLabelMap[bucket.weekday] || bucket.weekday}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.2 }}>
                      {formatHourRange(bucket.startHour, bucket.endHour)}
                    </Typography>
                    <Stack direction="row" spacing={1.2} alignItems="baseline" sx={{ mt: 0.6 }}>
                      <Typography
                        sx={{
                          color: activeMetric.accent,
                          fontWeight: 900,
                          fontFamily: DISPLAY_FONT,
                          fontSize: '1.35rem',
                          lineHeight: 1,
                        }}
                      >
                        {formatPercent(value)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formatNumber(bucket.attempts)} {attemptsLabel.toLowerCase()}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </InsightPanel>
  );
}

type ConsumptionEvolutionChartPoint = {
  date: string;
  dateLabel: string;
  aiConsumedMinutes: number | null;
  telephonyConsumedMinutes: number | null;
  aiConsumedMinutesTrend: number | null;
  telephonyConsumedMinutesTrend: number | null;
  [seriesKey: string]: string | number | null;
};

function secondsToChartMinutes(seconds: number | null | undefined): number | null {
  if (seconds === null || seconds === undefined) {
    return null;
  }
  return Number((Math.max(seconds, 0) / 60).toFixed(2));
}

function buildLinearTrend(values: Array<number | null | undefined>): Array<number | null> {
  const points = values
    .map((value, index) => ({ index, value }))
    .filter((point): point is { index: number; value: number } => (
      point.value !== null
      && point.value !== undefined
      && Number.isFinite(point.value)
    ));
  if (points.length < 2) {
    return values.map(() => null);
  }

  const meanX = points.reduce((sum, point) => sum + point.index, 0) / points.length;
  const meanY = points.reduce((sum, point) => sum + point.value, 0) / points.length;
  const denominator = points.reduce((sum, point) => sum + ((point.index - meanX) ** 2), 0);
  if (denominator === 0) {
    return values.map(() => null);
  }

  const slope = points.reduce(
    (sum, point) => sum + ((point.index - meanX) * (point.value - meanY)),
    0,
  ) / denominator;
  const intercept = meanY - (slope * meanX);
  const observedIndexes = new Set(points.map((point) => point.index));
  return values.map((_value, index) => (
    observedIndexes.has(index)
      ? Number(Math.max(0, intercept + (slope * index)).toFixed(2))
      : null
  ));
}

/**
 * Formats local-day history labels with weekday context for chart axes/tooltips.
 *
 * Dashboard history points are date-only values already resolved in the
 * tenant-local day window. Parsing them at noon prevents browser timezone
 * offsets from rolling the label into the previous day around midnight.
 */
function formatHistoryChartDateLabel(dateIso: string, locale: string): string {
  const dateValue = new Date(`${dateIso}T12:00:00`);
  if (Number.isNaN(dateValue.getTime())) {
    return dateIso;
  }
  const dayMonth = dateValue.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
  });
  const weekday = dateValue.toLocaleDateString(locale, { weekday: 'short' });
  const formattedWeekday = weekday
    ? `${weekday.charAt(0).toLocaleUpperCase(locale)}${weekday.slice(1)}`
    : '';
  return formattedWeekday ? `${dayMonth} - ${formattedWeekday}` : dayMonth;
}

function getAiAgentConvertedSeriesKey(agentId: string): string {
  return `${AI_AGENT_CONVERTED_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentConvertedTrendSeriesKey(agentId: string): string {
  return `${AI_AGENT_CONVERTED_TREND_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentConsumedSeriesKey(agentId: string): string {
  return `${AI_AGENT_CONSUMPTION_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentConsumedTrendSeriesKey(agentId: string): string {
  return `${AI_AGENT_CONSUMPTION_TREND_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentEfficiencySeriesKey(agentId: string): string {
  return `${AI_AGENT_EFFICIENCY_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentEfficiencyTrendSeriesKey(agentId: string): string {
  return `${AI_AGENT_EFFICIENCY_TREND_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentConversationQualitySeriesKey(agentId: string): string {
  return `${AI_AGENT_CONVERSATION_QUALITY_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getAiAgentConversationQualityTrendSeriesKey(agentId: string): string {
  return `${AI_AGENT_CONVERSATION_QUALITY_TREND_SERIES_KEY_PREFIX}_${agentId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getCampaignBaseHealthSeriesKey(campaignId: string): string {
  return `${CAMPAIGN_BASE_HEALTH_SERIES_KEY_PREFIX}_${campaignId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function getHumanAttendanceOperatorSeriesKey(operatorId: string): string {
  return `${HUMAN_ATTENDANCE_OPERATOR_SERIES_KEY_PREFIX}_${operatorId.replace(/[^a-zA-Z0-9_]/g, '_')}`;
}

function ConsumptionEvolutionChart({
  title,
  subtitle,
  titleIcon,
  accent,
  data,
  aiLabel,
  telephonyLabel,
  agentLabel,
  minutesUnitLabel,
  emptyLabel,
  locale,
}: {
  title: string;
  subtitle: string;
  titleIcon?: ReactNode;
  accent: string;
  data: DashboardCockpitConsumptionEvolutionSlice;
  aiLabel: string;
  telephonyLabel: string;
  agentLabel: (agentName: string) => string;
  minutesUnitLabel: string;
  emptyLabel: string;
  locale: string;
}) {
  const theme = useTheme();
  const [visibleSeries, setVisibleSeries] = useState({
    ai: true,
    telephony: true,
  });
  const [hiddenAiAgentIds, setHiddenAiAgentIds] = useState<Set<string>>(() => new Set());
  const [aiMenuAnchorEl, setAiMenuAnchorEl] = useState<HTMLElement | null>(null);
  const aiAgentSeries = useMemo(() => data.aiAgentSeries ?? [], [data.aiAgentSeries]);
  const hasMultipleAiAgents = aiAgentSeries.length > 1;
  const chartData = useMemo<ConsumptionEvolutionChartPoint[]>(
    () => {
      const agentPointMaps = aiAgentSeries.map((series) => ({
        key: getAiAgentConsumedSeriesKey(series.id),
        trendKey: getAiAgentConsumedTrendSeriesKey(series.id),
        pointsByDate: new Map(series.points.map((point) => [point.date, point])),
      }));
      const basePoints = data.points.map((point) => {
        return {
          date: point.date,
          dateLabel: formatHistoryChartDateLabel(point.date, locale),
          aiConsumedMinutes: secondsToChartMinutes(point.aiConsumedSeconds),
          telephonyConsumedMinutes: secondsToChartMinutes(point.telephonyConsumedSeconds),
        };
      });
      const aiTrend = buildLinearTrend(basePoints.map((point) => point.aiConsumedMinutes));
      const telephonyTrend = buildLinearTrend(basePoints.map((point) => point.telephonyConsumedMinutes));
      const agentValues = agentPointMaps.map(({ pointsByDate }) => (
        basePoints.map((point) => (
          secondsToChartMinutes(pointsByDate.get(point.date)?.aiConsumedSeconds ?? 0)
        ))
      ));
      const agentTrends = agentValues.map((values) => (
        buildLinearTrend(values)
      ));
      return basePoints.map((point, index) => ({
        ...point,
        aiConsumedMinutesTrend: aiTrend[index],
        telephonyConsumedMinutesTrend: telephonyTrend[index],
        ...Object.fromEntries(
          agentPointMaps.flatMap(({ key, trendKey }, agentIndex) => [
            [key, agentValues[agentIndex][index]],
            [trendKey, agentTrends[agentIndex][index]],
          ]),
        ),
      }));
    },
    [aiAgentSeries, data.points, locale],
  );
  useEffect(() => {
    setHiddenAiAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);
  const aiAgentLabelByKey = useMemo(
    () => new Map(
      aiAgentSeries.map((series) => [
        getAiAgentConsumedSeriesKey(series.id),
        agentLabel(series.label),
      ]),
    ),
    [agentLabel, aiAgentSeries],
  );
  const hasAggregateAi = chartData.some((point) => point.aiConsumedMinutes !== null);
  const hasAi = hasMultipleAiAgents || hasAggregateAi;
  const hasTelephony = chartData.some((point) => point.telephonyConsumedMinutes !== null);
  const visibleAiAgentSeries = hasMultipleAiAgents
    ? aiAgentSeries.filter((series) => !hiddenAiAgentIds.has(series.id))
    : [];
  const showAggregateAi = !hasMultipleAiAgents && hasAggregateAi && visibleSeries.ai;
  const showAi = hasMultipleAiAgents ? visibleAiAgentSeries.length > 0 : showAggregateAi;
  const showTelephony = hasTelephony && visibleSeries.telephony;
  const isAiMenuOpen = Boolean(aiMenuAnchorEl);
  const minChartWidth = Math.max(chartData.length * 34, 760);
  const formatMinutes = (value: number) => `${value.toLocaleString(locale, {
    maximumFractionDigits: 2,
  })} ${minutesUnitLabel}`;
  const toggleConsumptionSeries = (series: 'ai' | 'telephony') => {
    if (series === 'ai' && hasMultipleAiAgents) {
      return;
    }
    setVisibleSeries((current) => {
      const nextValue = !current[series];
      const nextState = {
        ...current,
        [series]: nextValue,
      };
      const nextHasVisibleAi = hasMultipleAiAgents
        ? visibleAiAgentSeries.length > 0
        : (hasAggregateAi && nextState.ai);
      const nextHasVisibleSeries = nextHasVisibleAi || (hasTelephony && nextState.telephony);
      return nextHasVisibleSeries ? nextState : current;
    });
  };
  const handleAiChipClick = (event: MouseEvent<HTMLElement>) => {
    if (hasMultipleAiAgents) {
      setAiMenuAnchorEl(event.currentTarget);
      return;
    }
    toggleConsumptionSeries('ai');
  };
  const toggleAiAgentSeries = (agentId: string) => {
    setHiddenAiAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      const nextHasVisibleSeries = nextVisibleAgentCount > 0 || showTelephony;
      return nextHasVisibleSeries ? nextHidden : current;
    });
  };
  const resolveSeriesLabel = (dataKey: unknown) => {
    if (dataKey === AI_CONSUMPTION_SERIES_KEY) {
      return aiLabel;
    }
    if (dataKey === TELEPHONY_CONSUMPTION_SERIES_KEY) {
      return telephonyLabel;
    }
    if (typeof dataKey === 'string' && aiAgentLabelByKey.has(dataKey)) {
      return aiAgentLabelByKey.get(dataKey) ?? minutesUnitLabel;
    }
    return minutesUnitLabel;
  };

  if (!hasAi && !hasTelephony) {
    return (
      <InsightPanel title={title} subtitle={subtitle} titleIcon={titleIcon} accent={accent}>
        <EmptyPanelState label={emptyLabel} />
      </InsightPanel>
    );
  }

  const chartActions = (
    <>
      {hasTelephony ? (
        <Chip
          size="small"
          component="button"
          clickable
          label={telephonyLabel}
          icon={<PhoneInTalk />}
          aria-pressed={showTelephony}
          onClick={() => toggleConsumptionSeries('telephony')}
          sx={buildMetricChipSx(theme.palette.info.main, showTelephony)}
        />
      ) : null}
      {hasAi ? (
        <>
          <Chip
            size="small"
            component="button"
            clickable
            label={hasMultipleAiAgents ? buildDropdownMetricLabel(aiLabel) : aiLabel}
            icon={<Psychology />}
            aria-pressed={showAi}
            aria-haspopup={hasMultipleAiAgents ? 'menu' : undefined}
            aria-expanded={hasMultipleAiAgents ? isAiMenuOpen : undefined}
            onClick={handleAiChipClick}
            sx={buildMetricChipSx(theme.palette.success.main, showAi)}
          />
          {hasMultipleAiAgents ? (
            <Menu
              anchorEl={aiMenuAnchorEl}
              open={isAiMenuOpen}
              onClose={() => setAiMenuAnchorEl(null)}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.75,
                    minWidth: 220,
                    maxWidth: 340,
                  },
                },
              }}
            >
              {aiAgentSeries.map((series, index) => {
                const isVisible = !hiddenAiAgentIds.has(series.id);
                const color = AI_AGENT_CONSUMPTION_SERIES_COLORS[
                  index % AI_AGENT_CONSUMPTION_SERIES_COLORS.length
                ];
                return (
                  <MenuItem
                    key={series.id}
                    dense
                    onClick={() => toggleAiAgentSeries(series.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={isVisible}
                      sx={{
                        color,
                        '&.Mui-checked': {
                          color,
                        },
                      }}
                    />
                    <ListItemText
                      primary={series.label}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </MenuItem>
                );
              })}
            </Menu>
          ) : null}
        </>
      ) : null}
    </>
  );

  return (
    <InsightPanel
      title={title}
      subtitle={subtitle}
      titleIcon={titleIcon}
      accent={accent}
      actions={chartActions}
    >
      <Box sx={{ width: '100%', minHeight: 320 }}>
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
          <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
              <XAxis
                dataKey="dateLabel"
                interval={0}
                padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                stroke={theme.palette.text.secondary}
                tick={{ fontSize: 10 }}
                tickMargin={8}
                angle={-34}
                textAnchor="end"
                height={52}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                stroke={theme.palette.text.secondary}
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => formatMinutes(Number(value))}
                tickLine={false}
                axisLine={false}
                width={68}
              />
              <RechartsTooltip
                contentStyle={{
                  borderRadius: 12,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                  backgroundColor: alpha(theme.palette.background.default, 0.96),
                }}
                formatter={(
                  value: number | string | undefined,
                  _name?: string,
                  item?,
                ) => [
                  formatMinutes(Number(value ?? 0)),
                  resolveSeriesLabel(item?.dataKey),
                ]}
                labelFormatter={(label) => label}
              />
              {showAggregateAi ? (
                <Line
                  type="monotone"
                  dataKey={AI_CONSUMPTION_SERIES_KEY}
                  name={aiLabel}
                  stroke={theme.palette.success.main}
                  strokeWidth={2.8}
                  dot={false}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ) : null}
              {showAggregateAi ? (
                <Line
                  type="linear"
                  dataKey={AI_CONSUMPTION_TREND_SERIES_KEY}
                  stroke={theme.palette.success.main}
                  strokeWidth={2}
                  strokeDasharray="7 5"
                  strokeOpacity={0.54}
                  dot={false}
                  activeDot={false}
                  tooltipType="none"
                  connectNulls={false}
                />
              ) : null}
              {hasMultipleAiAgents ? aiAgentSeries.map((series, index) => {
                if (hiddenAiAgentIds.has(series.id)) {
                  return null;
                }
                const color = AI_AGENT_CONSUMPTION_SERIES_COLORS[
                  index % AI_AGENT_CONSUMPTION_SERIES_COLORS.length
                ];
                const seriesKey = getAiAgentConsumedSeriesKey(series.id);
                const trendKey = getAiAgentConsumedTrendSeriesKey(series.id);
                const seriesLabel = agentLabel(series.label);
                return (
                  <Fragment key={series.id}>
                    <Line
                      type="monotone"
                      dataKey={seriesKey}
                      name={seriesLabel}
                      stroke={color}
                      strokeWidth={2.8}
                      dot={false}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    />
                    <Line
                      type="linear"
                      dataKey={trendKey}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="7 5"
                      strokeOpacity={0.54}
                      dot={false}
                      activeDot={false}
                      tooltipType="none"
                      connectNulls={false}
                    />
                  </Fragment>
                );
              }) : null}
              {showTelephony ? (
                <Line
                  type="monotone"
                  dataKey={TELEPHONY_CONSUMPTION_SERIES_KEY}
                  name={telephonyLabel}
                  stroke={theme.palette.info.main}
                  strokeWidth={2.8}
                  dot={false}
                  activeDot={{ r: 5 }}
                  connectNulls={false}
                />
              ) : null}
              {showTelephony ? (
                <Line
                  type="linear"
                  dataKey={TELEPHONY_CONSUMPTION_TREND_SERIES_KEY}
                  stroke={theme.palette.info.main}
                  strokeWidth={2}
                  strokeDasharray="7 5"
                  strokeOpacity={0.54}
                  dot={false}
                  activeDot={false}
                  tooltipType="none"
                  connectNulls={false}
                />
              ) : null}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      </Box>
    </InsightPanel>
  );
}

type PerformanceEvolutionChartPoint = {
  date: string;
  dateLabel: string;
  answeredCalls: number;
  convertedCalls: number;
  answeredCallsTrend: number | null;
  convertedCallsTrend: number | null;
  [seriesKey: string]: string | number | null;
};

function PerformanceEvolutionChart({
  title,
  subtitle,
  titleIcon,
  accent,
  data,
  answeredLabel,
  convertedLabel,
  agentConvertedLabel,
  callsUnitLabel,
  emptyLabel,
  locale,
}: {
  title: string;
  subtitle: string;
  titleIcon?: ReactNode;
  accent: string;
  data: DashboardCockpitPerformanceEvolutionSlice;
  answeredLabel: string;
  convertedLabel: string;
  agentConvertedLabel: (agentName: string) => string;
  callsUnitLabel: string;
  emptyLabel: string;
  locale: string;
}) {
  const theme = useTheme();
  const [visibleSeries, setVisibleSeries] = useState({
    answered: true,
    converted: true,
  });
  const [hiddenConvertedAgentIds, setHiddenConvertedAgentIds] = useState<Set<string>>(() => new Set());
  const [convertedMenuAnchorEl, setConvertedMenuAnchorEl] = useState<HTMLElement | null>(null);
  const aiAgentSeries = useMemo(() => data.aiAgentSeries ?? [], [data.aiAgentSeries]);
  const hasMultipleAiAgents = aiAgentSeries.length > 1;
  const chartData = useMemo<PerformanceEvolutionChartPoint[]>(
    () => {
      const agentPointMaps = aiAgentSeries.map((series) => ({
        key: getAiAgentConvertedSeriesKey(series.id),
        trendKey: getAiAgentConvertedTrendSeriesKey(series.id),
        pointsByDate: new Map(series.points.map((point) => [point.date, point])),
      }));
      const basePoints = data.points.map((point) => {
        return {
          date: point.date,
          dateLabel: formatHistoryChartDateLabel(point.date, locale),
          answeredCalls: point.answeredCalls,
          convertedCalls: point.convertedCalls,
        };
      });
      const answeredTrend = buildLinearTrend(basePoints.map((point) => point.answeredCalls));
      const convertedTrend = buildLinearTrend(basePoints.map((point) => point.convertedCalls));
      const agentValues = agentPointMaps.map(({ pointsByDate }) => (
        basePoints.map((point) => pointsByDate.get(point.date)?.convertedCalls ?? 0)
      ));
      const agentTrends = agentValues.map((values) => buildLinearTrend(values));
      return basePoints.map((point, index) => ({
        ...point,
        answeredCallsTrend: answeredTrend[index],
        convertedCallsTrend: convertedTrend[index],
        ...Object.fromEntries(
          agentPointMaps.flatMap(({ key, trendKey }, agentIndex) => [
            [key, agentValues[agentIndex][index]],
            [trendKey, agentTrends[agentIndex][index]],
          ]),
        ),
      }));
    },
    [aiAgentSeries, data.points, locale],
  );
  useEffect(() => {
    setHiddenConvertedAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);
  const aiAgentSeriesLabelByKey = useMemo(
    () => new Map(
      aiAgentSeries.map((series) => [
        getAiAgentConvertedSeriesKey(series.id),
        agentConvertedLabel(series.label),
      ]),
    ),
    [agentConvertedLabel, aiAgentSeries],
  );
  const hasAnswered = chartData.some((point) => point.answeredCalls > 0);
  const hasAggregateConverted = chartData.some((point) => point.convertedCalls > 0);
  const visibleConvertedAgentSeries = hasMultipleAiAgents
    ? aiAgentSeries.filter((series) => !hiddenConvertedAgentIds.has(series.id))
    : [];
  const showAnswered = hasAnswered && visibleSeries.answered;
  const showAggregateConverted = !hasMultipleAiAgents && hasAggregateConverted && visibleSeries.converted;
  const showConverted = hasMultipleAiAgents ? visibleConvertedAgentSeries.length > 0 : showAggregateConverted;
  const isConvertedMenuOpen = Boolean(convertedMenuAnchorEl);
  const hasData = hasAnswered || hasAggregateConverted;
  const minChartWidth = Math.max(chartData.length * 34, 760);
  const formatCalls = (value: number) => `${value.toLocaleString(locale, {
    maximumFractionDigits: 2,
  })} ${callsUnitLabel}`;
  const togglePerformanceSeries = (series: 'answered' | 'converted') => {
    if (series === 'converted' && hasMultipleAiAgents) {
      return;
    }
    setVisibleSeries((current) => {
      const nextValue = !current[series];
      const nextState = {
        ...current,
        [series]: nextValue,
      };
      const nextHasConverted = hasMultipleAiAgents
        ? visibleConvertedAgentSeries.length > 0
        : (hasAggregateConverted && nextState.converted);
      const nextHasVisibleSeries = (hasAnswered && nextState.answered) || nextHasConverted;
      return nextHasVisibleSeries ? nextState : current;
    });
  };
  const handleConvertedChipClick = (event: MouseEvent<HTMLElement>) => {
    if (hasMultipleAiAgents) {
      setConvertedMenuAnchorEl(event.currentTarget);
      return;
    }
    togglePerformanceSeries('converted');
  };
  const toggleConvertedAgentSeries = (agentId: string) => {
    setHiddenConvertedAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      const nextHasVisibleSeries = nextVisibleAgentCount > 0 || showAnswered;
      return nextHasVisibleSeries ? nextHidden : current;
    });
  };
  const resolveSeriesLabel = (dataKey: unknown) => {
    if (dataKey === ANSWERED_CALLS_SERIES_KEY) {
      return answeredLabel;
    }
    if (dataKey === CONVERTED_CALLS_SERIES_KEY) {
      return convertedLabel;
    }
    if (typeof dataKey === 'string' && aiAgentSeriesLabelByKey.has(dataKey)) {
      return aiAgentSeriesLabelByKey.get(dataKey) ?? callsUnitLabel;
    }
    return callsUnitLabel;
  };

  if (!hasData) {
    return (
      <InsightPanel title={title} subtitle={subtitle} titleIcon={titleIcon} accent={accent}>
        <EmptyPanelState label={emptyLabel} />
      </InsightPanel>
    );
  }

  const chartActions = (
    <>
      <Chip
        size="small"
        component="button"
        clickable
        label={answeredLabel}
        icon={<PhoneInTalk />}
        aria-pressed={showAnswered}
        onClick={() => togglePerformanceSeries('answered')}
        sx={buildMetricChipSx(theme.palette.info.main, showAnswered)}
      />
      <Chip
        size="small"
        component="button"
        clickable
        label={hasMultipleAiAgents ? buildDropdownMetricLabel(convertedLabel) : convertedLabel}
        icon={<CheckCircle />}
        aria-pressed={showConverted}
        aria-haspopup={hasMultipleAiAgents ? 'menu' : undefined}
        aria-expanded={hasMultipleAiAgents ? isConvertedMenuOpen : undefined}
        onClick={handleConvertedChipClick}
        sx={buildMetricChipSx(theme.palette.primary.main, showConverted)}
      />
      {hasMultipleAiAgents ? (
        <Menu
          anchorEl={convertedMenuAnchorEl}
          open={isConvertedMenuOpen}
          onClose={() => setConvertedMenuAnchorEl(null)}
          slotProps={{
            paper: {
              sx: {
                mt: 0.75,
                minWidth: 220,
                maxWidth: 340,
              },
            },
          }}
        >
          {aiAgentSeries.map((series, index) => {
            const isVisible = !hiddenConvertedAgentIds.has(series.id);
            const color = AI_AGENT_PERFORMANCE_SERIES_COLORS[
              index % AI_AGENT_PERFORMANCE_SERIES_COLORS.length
            ];
            return (
              <MenuItem
                key={series.id}
                dense
                onClick={() => toggleConvertedAgentSeries(series.id)}
              >
                <Checkbox
                  size="small"
                  checked={isVisible}
                  sx={{
                    color,
                    '&.Mui-checked': {
                      color,
                    },
                  }}
                />
                <ListItemText
                  primary={series.label}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      ) : null}
    </>
  );

  return (
    <InsightPanel
      title={title}
      subtitle={subtitle}
      titleIcon={titleIcon}
      accent={accent}
      actions={chartActions}
    >
      <Box sx={{ width: '100%', minHeight: 320 }}>
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
          <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
              <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
              <XAxis
                dataKey="dateLabel"
                interval={0}
                padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                stroke={theme.palette.text.secondary}
                tick={{ fontSize: 10 }}
                tickMargin={8}
                angle={-34}
                textAnchor="end"
                height={52}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                stroke={theme.palette.text.secondary}
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => Number(value).toLocaleString(locale)}
                tickLine={false}
                axisLine={false}
                width={54}
              />
              <RechartsTooltip
                contentStyle={{
                  borderRadius: 12,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
                  backgroundColor: alpha(theme.palette.background.default, 0.96),
                }}
                formatter={(
                  value: number | string | undefined,
                  _name?: string,
                  item?,
                ) => [
                  formatCalls(Number(value ?? 0)),
                  resolveSeriesLabel(item?.dataKey),
                ]}
                labelFormatter={(label) => label}
              />
              {showAnswered ? (
                <Line
                  type="monotone"
                  dataKey={ANSWERED_CALLS_SERIES_KEY}
                  name={answeredLabel}
                  stroke={theme.palette.info.main}
                  strokeWidth={2.8}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              ) : null}
              {showAnswered ? (
                <Line
                  type="linear"
                  dataKey={ANSWERED_CALLS_TREND_SERIES_KEY}
                  stroke={theme.palette.info.main}
                  strokeWidth={2}
                  strokeDasharray="7 5"
                  strokeOpacity={0.54}
                  dot={false}
                  activeDot={false}
                  tooltipType="none"
                />
              ) : null}
              {showAggregateConverted ? (
                <Line
                  type="monotone"
                  dataKey={CONVERTED_CALLS_SERIES_KEY}
                  name={convertedLabel}
                  stroke={theme.palette.primary.main}
                  strokeWidth={2.8}
                  dot={false}
                  activeDot={{ r: 5 }}
                />
              ) : null}
              {showAggregateConverted ? (
                <Line
                  type="linear"
                  dataKey={CONVERTED_CALLS_TREND_SERIES_KEY}
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  strokeDasharray="7 5"
                  strokeOpacity={0.54}
                  dot={false}
                  activeDot={false}
                  tooltipType="none"
                />
              ) : null}
              {hasMultipleAiAgents ? aiAgentSeries.map((series, index) => {
                if (hiddenConvertedAgentIds.has(series.id)) {
                  return null;
                }
                const color = AI_AGENT_PERFORMANCE_SERIES_COLORS[index % AI_AGENT_PERFORMANCE_SERIES_COLORS.length];
                const seriesLabel = agentConvertedLabel(series.label);
                const seriesKey = getAiAgentConvertedSeriesKey(series.id);
                const trendKey = getAiAgentConvertedTrendSeriesKey(series.id);
                return (
                  <Fragment key={series.id}>
                    <Line
                      type="monotone"
                      dataKey={seriesKey}
                      name={seriesLabel}
                      stroke={color}
                      strokeWidth={2.4}
                      strokeOpacity={0.9}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Line
                      type="linear"
                      dataKey={trendKey}
                      stroke={color}
                      strokeWidth={1.8}
                      strokeDasharray="7 5"
                      strokeOpacity={0.46}
                      dot={false}
                      activeDot={false}
                      tooltipType="none"
                    />
                  </Fragment>
                );
              }) : null}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>
      </Box>
    </InsightPanel>
  );
}

type HumanAttendancePerformanceChartPoint = {
  date: string;
  dateLabel: string;
  humanAttendanceValue: number | null;
  humanAttendanceValueTrend: number | null;
  context: DashboardCockpitHumanAttendancePerformancePoint;
};

type HumanAttendancePerformanceMetricOption = {
  key: HumanAttendancePerformanceMetricKey;
  label: string;
  help: string;
  accent: string;
  icon: ReactNode;
};

function getHumanAttendancePerformanceValue(
  point: DashboardCockpitHumanAttendancePerformancePoint,
  metricKey: HumanAttendancePerformanceMetricKey,
): number | null {
  if (metricKey === 'humanAnsweredCalls') {
    return point.humanAnsweredCalls;
  }
  if (metricKey === 'humanAnswerRate') {
    return point.humanAnswerRate;
  }
  if (metricKey === 'averageWaitSeconds') {
    return point.averageWaitSeconds;
  }
  return point.averageHandleSeconds;
}

function formatHumanAttendanceMetricValue(
  metricKey: HumanAttendancePerformanceMetricKey | HumanAttendanceOperatorMetricKey,
  value: number | null | undefined,
  {
    formatNumber,
    formatPercent,
    formatDuration,
    notAvailableLabel,
  }: {
    formatNumber: (value: number | null | undefined) => string;
    formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
    formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string;
    notAvailableLabel: string;
  },
) {
  if (value === null || value === undefined) {
    return notAvailableLabel;
  }
  if (metricKey === 'humanAnswerRate') {
    return formatPercent(value);
  }
  if (
    metricKey === 'averageWaitSeconds'
    || metricKey === 'averageHandleSeconds'
    || metricKey === 'unavailableSeconds'
  ) {
    return formatDuration(value, metricKey !== 'unavailableSeconds');
  }
  return formatNumber(value);
}

function HumanAttendancePerformancePanel({
  data,
}: {
  data: DashboardCockpitHumanAttendancePerformanceSlice;
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { locale, formatDuration, formatNumber, formatPercent } = useNumberFormat();
  const [selectedMetric, setSelectedMetric] = useState<HumanAttendancePerformanceMetricKey>('humanAnsweredCalls');
  const metricOptions = useMemo<HumanAttendancePerformanceMetricOption[]>(
    () => [
      {
        key: 'humanAnsweredCalls',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnsweredCalls'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnsweredCallsHelp'),
        accent: theme.palette.success.main,
        icon: <PhoneInTalk />,
      },
      {
        key: 'humanAnswerRate',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnswerRate'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnswerRateHelp'),
        accent: theme.palette.info.main,
        icon: <CheckCircle />,
      },
      {
        key: 'averageWaitSeconds',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.averageWaitSeconds'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.averageWaitSecondsHelp'),
        accent: theme.palette.warning.main,
        icon: <AccessTime />,
      },
      {
        key: 'averageHandleSeconds',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.averageHandleSeconds'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.averageHandleSecondsHelp'),
        accent: theme.palette.primary.main,
        icon: <Speed />,
      },
    ],
    [t, theme.palette.info.main, theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];
  const chartData = useMemo<HumanAttendancePerformanceChartPoint[]>(
    () => {
      const basePoints = data.points.map((point) => ({
        date: point.date,
        dateLabel: formatHistoryChartDateLabel(point.date, locale),
        humanAttendanceValue: getHumanAttendancePerformanceValue(point, selectedMetric),
        context: point,
      }));
      const trend = buildLinearTrend(basePoints.map((point) => point.humanAttendanceValue));
      return basePoints.map((point, index) => ({
        ...point,
        humanAttendanceValueTrend: trend[index],
      }));
    },
    [data.points, locale, selectedMetric],
  );
  const hasData = chartData.some((point) => point.humanAttendanceValue !== null && point.humanAttendanceValue !== undefined);
  const minChartWidth = Math.max(chartData.length * 34, 760);
  const notAvailableLabel = t('pages:dashboard.notAvailable');
  const formatValue = (value: number | null | undefined) => formatHumanAttendanceMetricValue(
    selectedMetric,
    value,
    { formatNumber, formatPercent, formatDuration, notAvailableLabel },
  );

  if (!activeMetric) {
    return null;
  }

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.humanAttendance.performanceTitle')}
      subtitle={t('pages:dashboard.patterns.humanAttendance.performanceSubtitle', {
        days: formatNumber(data.windowDays),
      })}
      titleIcon={<QueryStats />}
      accent={activeMetric.accent}
      actions={(
        <>
          {metricOptions.map((option) => {
            const isSelected = option.key === selectedMetric;
            return (
              <Tooltip key={option.key} title={option.help} arrow>
                <Chip
                  size="small"
                  component="button"
                  clickable
                  label={option.label}
                  icon={option.icon as ReactElement}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedMetric(option.key)}
                  sx={buildMetricChipSx(option.accent, isSelected)}
                />
              </Tooltip>
            );
          })}
        </>
      )}
    >
      {!hasData ? (
        <EmptyPanelState label={t('pages:dashboard.patterns.humanAttendance.emptyPerformance')} />
      ) : (
        <Box sx={{ width: '100%', minHeight: 320 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
            <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                  <XAxis
                    dataKey="dateLabel"
                    interval={0}
                    padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickMargin={8}
                    angle={-34}
                    textAnchor="end"
                    height={52}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => formatValue(Number(value))}
                    tickLine={false}
                    axisLine={false}
                    width={76}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      borderRadius: 12,
                      border: `1px solid ${alpha(activeMetric.accent, 0.24)}`,
                      backgroundColor: alpha(theme.palette.background.default, 0.96),
                    }}
                    formatter={(value: number | string | undefined) => [
                      formatValue(Number(value ?? 0)),
                      activeMetric.label,
                    ]}
                    labelFormatter={(label) => label}
                  />
                  <Line
                    type="monotone"
                    dataKey="humanAttendanceValue"
                    name={activeMetric.label}
                    stroke={activeMetric.accent}
                    strokeWidth={2.8}
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Line
                    type="linear"
                    dataKey="humanAttendanceValueTrend"
                    stroke={activeMetric.accent}
                    strokeWidth={2}
                    strokeDasharray="7 5"
                    strokeOpacity={0.54}
                    dot={false}
                    activeDot={false}
                    tooltipType="none"
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      )}
    </InsightPanel>
  );
}

type HumanAttendanceOperatorMetricOption = {
  key: HumanAttendanceOperatorMetricKey;
  label: string;
  help: string;
  accent: string;
  icon: ReactElement;
};

type HumanAttendanceOperatorChartContext = {
  operatorName: string;
  transfersStarted: number;
  transfersAnswered: number;
  handledCalls: number;
  unavailabilityEvents: number;
};

type HumanAttendanceOperatorChartPoint = {
  date: string;
  dateLabel: string;
  [seriesKey: string]: string | number | null | HumanAttendanceOperatorChartContext;
};

function getHumanAttendanceOperatorMetricValue(
  operator: DashboardCockpitHumanAttendanceOperator | DashboardCockpitHumanAttendanceOperatorPoint,
  metricKey: HumanAttendanceOperatorMetricKey,
): number | null {
  if (metricKey === 'handledCalls') {
    return operator.handledCalls;
  }
  if (metricKey === 'humanAnswerRate') {
    return operator.humanAnswerRate;
  }
  if (metricKey === 'averageHandleSeconds') {
    return operator.averageHandleSeconds;
  }
  if (metricKey === 'averageWaitSeconds') {
    return operator.averageWaitSeconds;
  }
  return operator.unavailableSeconds;
}

function isHumanAttendanceOperatorChartContext(
  value: unknown,
): value is HumanAttendanceOperatorChartContext {
  return (
    typeof value === 'object'
    && value !== null
    && 'operatorName' in value
    && 'handledCalls' in value
  );
}

function getHumanAttendanceAttentionAccent(
  theme: Theme,
  severity: DashboardCockpitHumanAttendanceAttentionPoint['severity'],
) {
  if (severity === 'error') {
    return theme.palette.error.main;
  }
  if (severity === 'warning') {
    return theme.palette.warning.main;
  }
  return theme.palette.info.main;
}

function HumanAttendanceAttentionPoints({
  points,
  operators,
}: {
  points: DashboardCockpitHumanAttendanceAttentionPoint[];
  operators: DashboardCockpitHumanAttendanceOperator[];
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { formatDuration, formatPercent } = useNumberFormat();
  const notAvailableLabel = t('pages:dashboard.notAvailable');
  const operatorNameById = useMemo(
    () => new Map(operators.map((operator) => [operator.id, operator.name])),
    [operators],
  );
  if (!points.length) {
    return null;
  }
  return (
    <Box sx={{ mt: 2.1 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          fontWeight: 900,
          letterSpacing: '0.04em',
          mb: 1,
          textTransform: 'uppercase',
        }}
      >
        {t('pages:dashboard.patterns.humanAttendance.attention.title')}
      </Typography>
      <Stack gap={0.9}>
        {points.map((point) => {
          const accent = getHumanAttendanceAttentionAccent(theme, point.severity);
          const names = point.operatorIds
            .map((operatorId) => operatorNameById.get(operatorId))
            .filter(Boolean)
            .join(', ');
          const value = (
            point.metricValue === null
              ? notAvailableLabel
              : point.key === 'low_answer_rate'
                ? formatPercent(point.metricValue)
                : formatDuration(point.metricValue)
          );
          return (
            <Box
              key={`${point.key}:${point.operatorIds.join('-')}`}
              sx={{
                p: 1.2,
                borderRadius: '14px',
                border: `1px solid ${alpha(accent, 0.18)}`,
                backgroundColor: alpha(accent, 0.065),
              }}
            >
              <Typography sx={{ color: accent, fontWeight: 900, lineHeight: 1.2 }}>
                {t(`pages:dashboard.patterns.humanAttendance.attention.${point.key}.title`)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                {t(`pages:dashboard.patterns.humanAttendance.attention.${point.key}.description`, {
                  operators: names || t('pages:dashboard.patterns.humanAttendance.attention.noOperators'),
                  value,
                })}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function HumanAttendanceOperatorHealthPanel({
  data,
}: {
  data: DashboardCockpitHumanAttendancePatternsSlice;
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const {
    locale,
    formatDuration,
    formatNumber,
    formatPercent,
  } = useNumberFormat();
  const [selectedMetric, setSelectedMetric] = useState<HumanAttendanceOperatorMetricKey>('handledCalls');
  const [hiddenOperatorIds, setHiddenOperatorIds] = useState<Set<string>>(() => new Set());
  const [operatorMenuAnchorEl, setOperatorMenuAnchorEl] = useState<HTMLElement | null>(null);
  const operatorHealth = data.operatorHealth;
  const metricOptions = useMemo<HumanAttendanceOperatorMetricOption[]>(
    () => [
      {
        key: 'handledCalls',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.handledCalls'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.handledCallsHelp'),
        accent: theme.palette.success.main,
        icon: <PhoneInTalk />,
      },
      {
        key: 'humanAnswerRate',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnswerRate'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.humanAnswerRateHelp'),
        accent: theme.palette.info.main,
        icon: <CheckCircle />,
      },
      {
        key: 'averageHandleSeconds',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.averageHandleSeconds'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.averageHandleSecondsHelp'),
        accent: theme.palette.primary.main,
        icon: <Speed />,
      },
      {
        key: 'averageWaitSeconds',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.averageWaitSeconds'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.averageWaitSecondsHelp'),
        accent: theme.palette.warning.main,
        icon: <AccessTime />,
      },
      {
        key: 'unavailableSeconds',
        label: t('pages:dashboard.patterns.humanAttendance.metrics.unavailableSeconds'),
        help: t('pages:dashboard.patterns.humanAttendance.metrics.unavailableSecondsHelp'),
        accent: theme.palette.error.main,
        icon: <PersonOff />,
      },
    ],
    [
      t,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];
  const operatorSeries = useMemo<DashboardCockpitHumanAttendanceOperatorSeries[]>(
    () => operatorHealth.series ?? [],
    [operatorHealth.series],
  );
  const isOperatorMenuOpen = Boolean(operatorMenuAnchorEl);
  const visibleOperatorSeries = useMemo(
    () => operatorSeries.filter((series) => !hiddenOperatorIds.has(series.id)),
    [hiddenOperatorIds, operatorSeries],
  );
  const days = useMemo(
    () => Array.from(new Set(operatorSeries.flatMap((series) => (
      series.points.map((point) => point.date)
    )))).sort(),
    [operatorSeries],
  );
  const pointsByOperatorAndDate = useMemo(
    () => new Map(
      operatorSeries.flatMap((series) => (
        series.points.map((point) => [`${series.id}:${point.date}`, point] as const)
      )),
    ),
    [operatorSeries],
  );
  const chartData = useMemo<HumanAttendanceOperatorChartPoint[]>(
    () => days.map((day) => ({
      date: day,
      dateLabel: formatHistoryChartDateLabel(day, locale),
      ...Object.fromEntries(
        visibleOperatorSeries.flatMap((series) => {
          const seriesKey = getHumanAttendanceOperatorSeriesKey(series.id);
          const point = pointsByOperatorAndDate.get(`${series.id}:${day}`) ?? null;
          const value = point ? getHumanAttendanceOperatorMetricValue(point, selectedMetric) : null;
          const context: HumanAttendanceOperatorChartContext = {
            operatorName: series.name,
            transfersStarted: point?.transfersStarted ?? 0,
            transfersAnswered: point?.transfersAnswered ?? 0,
            handledCalls: point?.handledCalls ?? 0,
            unavailabilityEvents: point?.unavailabilityEvents ?? 0,
          };
          return [
            [seriesKey, value],
            [`${seriesKey}_context`, context],
          ];
        }),
      ),
    })),
    [days, locale, pointsByOperatorAndDate, selectedMetric, visibleOperatorSeries],
  );
  useEffect(() => {
    setHiddenOperatorIds((current) => {
      const knownOperatorIds = new Set(operatorSeries.map((series) => series.id));
      const nextHidden = new Set(Array.from(current).filter((operatorId) => knownOperatorIds.has(operatorId)));
      if (nextHidden.size >= operatorSeries.length && operatorSeries.length > 0) {
        nextHidden.delete(operatorSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [operatorSeries]);
  const toggleOperator = (operatorId: string) => {
    setHiddenOperatorIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(operatorId)) {
        nextHidden.delete(operatorId);
      } else {
        nextHidden.add(operatorId);
      }
      const nextVisibleCount = operatorSeries.filter((series) => !nextHidden.has(series.id)).length;
      return nextVisibleCount > 0 ? nextHidden : current;
    });
  };
  const notAvailableLabel = t('pages:dashboard.notAvailable');
  const formatValue = (value: number | null | undefined) => formatHumanAttendanceMetricValue(
    selectedMetric,
    value,
    { formatNumber, formatPercent, formatDuration, notAvailableLabel },
  );
  const hasChartData = operatorSeries.some((series) => (
    series.points.some((point) => (
      point.handledCalls > 0
      || point.transfersStarted > 0
      || point.unavailableSeconds > 0
    ))
  ));
  const hasSelectedMetricData = chartData.some((point) => (
    visibleOperatorSeries.some((series) => {
      const seriesKey = getHumanAttendanceOperatorSeriesKey(series.id);
      const value = point[seriesKey];
      return value !== null && value !== undefined;
    })
  ));
  const minChartWidth = Math.max(days.length * 34, 760);
  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      dataKey?: unknown;
      value?: unknown;
      payload?: Record<string, unknown>;
      color?: string;
    }>;
    label?: unknown;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }
    const visiblePayload = payload
      .filter((item) => (
        typeof item.dataKey === 'string'
        && item.value !== null
        && item.value !== undefined
      ))
      .map((item) => ({
        item,
        numericValue: Number(item.value ?? 0),
      }))
      .sort((left, right) => right.numericValue - left.numericValue);
    if (!visiblePayload.length) {
      return null;
    }
    return (
      <DenseChartTooltipShell accent={activeMetric.accent} title={`${String(label ?? '')} · ${activeMetric.label}`}>
        {visiblePayload.map(({ item, numericValue }) => {
          const dataKey = String(item.dataKey);
          const context = item.payload?.[`${dataKey}_context`];
          const typedContext = isHumanAttendanceOperatorChartContext(context) ? context : null;
          const seriesColor = item.color || activeMetric.accent;
          return (
            <Box key={dataKey} sx={{ pl: 1, borderLeft: `3px solid ${seriesColor}` }}>
              <Stack direction="row" justifyContent="space-between" gap={2} alignItems="baseline">
                <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, minWidth: 0 }} noWrap>
                  {typedContext?.operatorName || activeMetric.label}
                </Typography>
                <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, whiteSpace: 'nowrap' }}>
                  {formatValue(numericValue)}
                </Typography>
              </Stack>
              {typedContext ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                  {t('pages:dashboard.patterns.humanAttendance.operatorTooltipBase', {
                    handled: formatNumber(typedContext.handledCalls),
                    transfers: formatNumber(typedContext.transfersStarted),
                    answered: formatNumber(typedContext.transfersAnswered),
                    pauses: formatNumber(typedContext.unavailabilityEvents),
                  })}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </DenseChartTooltipShell>
    );
  };

  if (!activeMetric) {
    return null;
  }

  const chartActions = (
    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0.75} sx={{ width: '100%' }}>
      {operatorSeries.length > 1 ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, width: '100%' }}>
            <Chip
              size="small"
              component="button"
              clickable
              label={buildDropdownMetricLabel(t('pages:dashboard.patterns.humanAttendance.operators'))}
              icon={<PersonOff />}
              aria-haspopup="menu"
              aria-expanded={isOperatorMenuOpen}
              onClick={(event) => setOperatorMenuAnchorEl(event.currentTarget)}
              sx={buildMetricChipSx(activeMetric.accent, true)}
            />
          </Box>
          <Menu
            anchorEl={operatorMenuAnchorEl}
            open={isOperatorMenuOpen}
            onClose={() => setOperatorMenuAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.75,
                  minWidth: 220,
                  maxWidth: 340,
                },
              },
            }}
          >
            {operatorSeries.map((series, index) => {
              const isVisible = !hiddenOperatorIds.has(series.id);
              const color = HUMAN_ATTENDANCE_OPERATOR_COLORS[index % HUMAN_ATTENDANCE_OPERATOR_COLORS.length];
              return (
                <MenuItem key={series.id} dense onClick={() => toggleOperator(series.id)}>
                  <Checkbox
                    size="small"
                    checked={isVisible}
                    sx={{
                      color,
                      '&.Mui-checked': {
                        color,
                      },
                    }}
                  />
                  <ListItemText
                    primary={series.name}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      ) : null}
      <Stack
        direction="row"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
        gap={0.75}
      >
        {metricOptions.map((option) => {
          const isSelected = option.key === selectedMetric;
          return (
            <Tooltip key={option.key} title={option.help} arrow>
              <Chip
                size="small"
                component="button"
                clickable
                label={buildMetricHelpLabel(option.label)}
                icon={option.icon}
                aria-pressed={isSelected}
                onClick={() => setSelectedMetric(option.key)}
                sx={buildMetricChipSx(option.accent, isSelected)}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.humanAttendance.operatorHealthTitle')}
      subtitle={t('pages:dashboard.patterns.humanAttendance.operatorHealthSubtitle', {
        days: formatNumber(data.windowDays),
      })}
      titleIcon={<PhoneInTalk />}
      accent={activeMetric.accent}
      actions={chartActions}
    >
      {!operatorHealth.canViewUsers ? (
        <EmptyPanelState label={t('pages:dashboard.restricted.users')} />
      ) : hasChartData ? (
        <Stack gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t('pages:dashboard.patterns.humanAttendance.operatorReadingHint')}
          </Typography>
          {hasSelectedMetricData ? (
            <Box sx={{ width: '100%', minHeight: 300, minWidth: 0 }}>
              <Box sx={DENSE_CHART_SCROLL_AREA_SX} onWheel={handleDenseChartTooltipWheel}>
                <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                      <XAxis
                        dataKey="dateLabel"
                        interval={0}
                        padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                        stroke={theme.palette.text.secondary}
                        tick={{ fontSize: 10 }}
                        tickMargin={8}
                        angle={-34}
                        textAnchor="end"
                        height={52}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        domain={selectedMetric === 'humanAnswerRate' ? [0, 100] : [0, (maxValue: number) => Math.max(maxValue, 1)]}
                        stroke={theme.palette.text.secondary}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => formatValue(Number(value))}
                        tickLine={false}
                        axisLine={false}
                        width={78}
                      />
                      <RechartsTooltip
                        content={renderTooltip}
                        allowEscapeViewBox={{ x: true, y: true }}
                        wrapperStyle={DENSE_CHART_TOOLTIP_WRAPPER_STYLE}
                      />
                      {visibleOperatorSeries.map((series) => {
                        const seriesKey = getHumanAttendanceOperatorSeriesKey(series.id);
                        const sourceIndex = Math.max(operatorSeries.findIndex((item) => item.id === series.id), 0);
                        const color = HUMAN_ATTENDANCE_OPERATOR_COLORS[sourceIndex % HUMAN_ATTENDANCE_OPERATOR_COLORS.length];
                        return (
                          <Line
                            key={series.id}
                            type="monotone"
                            dataKey={seriesKey}
                            name={series.name}
                            stroke={color}
                            strokeWidth={2.5}
                            dot={false}
                            activeDot={{ r: 5 }}
                            connectNulls={false}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </Box>
          ) : (
            <EmptyPanelState label={t('pages:dashboard.patterns.humanAttendance.emptySelectedMetric')} />
          )}
          {operatorHealth.totalOperators > operatorHealth.operators.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2 }}>
              {t('pages:dashboard.patterns.humanAttendance.operatorLimit', {
                shown: formatNumber(operatorHealth.operators.length),
                total: formatNumber(operatorHealth.totalOperators),
              })}
            </Typography>
          ) : null}
          <HumanAttendanceAttentionPoints
            points={operatorHealth.attentionPoints}
            operators={operatorHealth.operators}
          />
        </Stack>
      ) : (
        <EmptyPanelState label={t('pages:dashboard.patterns.humanAttendance.emptyOperators')} />
      )}
    </InsightPanel>
  );
}

type AiConsumptionEfficiencyMetricOption = {
  key: AiConsumptionEfficiencyMetricKey;
  label: string;
  help: string;
  accent: string;
  unit: 'minutes' | 'per100';
};

type AiConsumptionEfficiencyChartContext = {
  aiConsumedSeconds: number;
  telephonyConvertedConsumedSeconds: number | null;
  answeredCalls: number;
  convertedCalls: number;
};

type AiConsumptionEfficiencyChartPoint = {
  date: string;
  dateLabel: string;
  efficiencyValue: number | null;
  efficiencyValueTrend: number | null;
  aggregateContext: AiConsumptionEfficiencyChartContext;
  [seriesKey: string]: string | number | null | AiConsumptionEfficiencyChartContext;
};

function roundEfficiencyMetric(value: number): number {
  return Number(value.toFixed(2));
}

function getEfficiencyMetricValue(
  point: DashboardCockpitConsumptionEfficiencyPoint,
  metricKey: AiConsumptionEfficiencyMetricKey,
): number | null {
  const aiConsumedMinutes = Math.max(point.aiConsumedSeconds, 0) / 60;
  const telephonyConvertedMinutes = point.telephonyConvertedConsumedSeconds === null
    ? null
    : Math.max(point.telephonyConvertedConsumedSeconds, 0) / 60;
  if (metricKey === 'aiMinutesPerAnswer') {
    return point.answeredCalls > 0
      ? roundEfficiencyMetric(aiConsumedMinutes / point.answeredCalls)
      : null;
  }
  if (metricKey === 'aiMinutesPerConversion') {
    return point.convertedCalls > 0
      ? roundEfficiencyMetric(aiConsumedMinutes / point.convertedCalls)
      : null;
  }
  if (metricKey === 'answersPer100AiMinutes') {
    return aiConsumedMinutes > 0
      ? roundEfficiencyMetric((point.answeredCalls / aiConsumedMinutes) * 100)
      : null;
  }
  if (metricKey === 'conversionsPer100AiMinutes') {
    return aiConsumedMinutes > 0
      ? roundEfficiencyMetric((point.convertedCalls / aiConsumedMinutes) * 100)
      : null;
  }
  if (telephonyConvertedMinutes === null) {
    return null;
  }
  return point.convertedCalls > 0
    ? roundEfficiencyMetric(telephonyConvertedMinutes / point.convertedCalls)
    : null;
}

function buildEfficiencyContext(
  point: DashboardCockpitConsumptionEfficiencyPoint | undefined,
): AiConsumptionEfficiencyChartContext {
  return {
    aiConsumedSeconds: point?.aiConsumedSeconds ?? 0,
    telephonyConvertedConsumedSeconds: point?.telephonyConvertedConsumedSeconds ?? null,
    answeredCalls: point?.answeredCalls ?? 0,
    convertedCalls: point?.convertedCalls ?? 0,
  };
}

function isEfficiencyContext(value: unknown): value is AiConsumptionEfficiencyChartContext {
  return (
    typeof value === 'object'
    && value !== null
    && 'aiConsumedSeconds' in value
    && 'answeredCalls' in value
    && 'convertedCalls' in value
  );
}

function isEfficiencyTrendDataKey(dataKey: unknown): boolean {
  return (
    dataKey === EFFICIENCY_AGGREGATE_TREND_SERIES_KEY
    || (
      typeof dataKey === 'string'
      && dataKey.startsWith(`${AI_AGENT_EFFICIENCY_TREND_SERIES_KEY_PREFIX}_`)
    )
  );
}

function AiConsumptionEfficiencyChart({
  title,
  subtitle,
  titleIcon,
  data,
  metricLabels,
  metricHelps,
  agentLabel,
  agentSelectorLabel,
  emptyLabel,
  contextLabels,
  locale,
}: {
  title: string;
  subtitle: string;
  titleIcon?: ReactNode;
  data: DashboardCockpitConsumptionEfficiencySlice;
  metricLabels: Record<AiConsumptionEfficiencyMetricKey, string>;
  metricHelps: Record<AiConsumptionEfficiencyMetricKey, string>;
  agentLabel: (agentName: string) => string;
  agentSelectorLabel: string;
  emptyLabel: string;
  contextLabels: {
    aggregateSeries: string;
    baseOfDay: string;
    aiMinutes: string;
    telephonyConvertedMinutes: string;
    answered: string;
    converted: string;
    minutesUnit: string;
  };
  locale: string;
}) {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<AiConsumptionEfficiencyMetricKey>('conversionsPer100AiMinutes');
  const [hiddenAgentIds, setHiddenAgentIds] = useState<Set<string>>(() => new Set());
  const [agentMenuAnchorEl, setAgentMenuAnchorEl] = useState<HTMLElement | null>(null);
  const aiAgentSeries = useMemo(() => data.aiAgentSeries ?? [], [data.aiAgentSeries]);
  const hasMultipleAiAgents = aiAgentSeries.length > 1;
  const hasTelephonyEfficiency = (
    data.points.some((point) => point.telephonyConvertedConsumedSeconds !== null)
    || aiAgentSeries.some((series) => (
      series.points.some((point) => point.telephonyConvertedConsumedSeconds !== null)
    ))
  );
  const metricOptions = useMemo<AiConsumptionEfficiencyMetricOption[]>(
    () => [
      {
        key: 'aiMinutesPerAnswer',
        label: metricLabels.aiMinutesPerAnswer,
        help: metricHelps.aiMinutesPerAnswer,
        accent: theme.palette.info.main,
        unit: 'minutes',
      },
      {
        key: 'aiMinutesPerConversion',
        label: metricLabels.aiMinutesPerConversion,
        help: metricHelps.aiMinutesPerConversion,
        accent: theme.palette.primary.main,
        unit: 'minutes',
      },
      {
        key: 'answersPer100AiMinutes',
        label: metricLabels.answersPer100AiMinutes,
        help: metricHelps.answersPer100AiMinutes,
        accent: theme.palette.success.main,
        unit: 'per100',
      },
      {
        key: 'conversionsPer100AiMinutes',
        label: metricLabels.conversionsPer100AiMinutes,
        help: metricHelps.conversionsPer100AiMinutes,
        accent: theme.palette.warning.main,
        unit: 'per100',
      },
      ...(hasTelephonyEfficiency
        ? [{
            key: 'telephonyMinutesPerConversion' as const,
            label: metricLabels.telephonyMinutesPerConversion,
            help: metricHelps.telephonyMinutesPerConversion,
            accent: theme.palette.secondary.main,
            unit: 'minutes' as const,
          }]
        : []),
    ],
    [
      hasTelephonyEfficiency,
      metricHelps.aiMinutesPerAnswer,
      metricHelps.aiMinutesPerConversion,
      metricHelps.answersPer100AiMinutes,
      metricHelps.conversionsPer100AiMinutes,
      metricHelps.telephonyMinutesPerConversion,
      metricLabels.aiMinutesPerAnswer,
      metricLabels.aiMinutesPerConversion,
      metricLabels.answersPer100AiMinutes,
      metricLabels.conversionsPer100AiMinutes,
      metricLabels.telephonyMinutesPerConversion,
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];

  useEffect(() => {
    if (!metricOptions.some((option) => option.key === selectedMetric)) {
      setSelectedMetric(metricOptions[0]?.key ?? 'conversionsPer100AiMinutes');
    }
  }, [metricOptions, selectedMetric]);
  useEffect(() => {
    setHiddenAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);

  const chartData = useMemo<AiConsumptionEfficiencyChartPoint[]>(() => {
    const agentPointMaps = aiAgentSeries.map((series) => ({
      key: getAiAgentEfficiencySeriesKey(series.id),
      trendKey: getAiAgentEfficiencyTrendSeriesKey(series.id),
      contextKey: `${getAiAgentEfficiencySeriesKey(series.id)}_context`,
      pointsByDate: new Map(series.points.map((point) => [point.date, point])),
    }));
    const basePoints = data.points.map((point) => {
      return {
        date: point.date,
        dateLabel: formatHistoryChartDateLabel(point.date, locale),
        efficiencyValue: getEfficiencyMetricValue(point, selectedMetric),
        aggregateContext: buildEfficiencyContext(point),
      };
    });
    const aggregateTrend = buildLinearTrend(basePoints.map((point) => point.efficiencyValue));
    const agentValues = agentPointMaps.map(({ pointsByDate }) => (
      basePoints.map((point) => getEfficiencyMetricValue(
        pointsByDate.get(point.date) ?? {
          date: point.date,
          aiConsumedSeconds: 0,
          telephonyConvertedConsumedSeconds: null,
          answeredCalls: 0,
          convertedCalls: 0,
        },
        selectedMetric,
      ))
    ));
    const agentTrends = agentValues.map((values) => buildLinearTrend(values));
    return basePoints.map((point, index) => ({
      ...point,
      efficiencyValueTrend: aggregateTrend[index],
      ...Object.fromEntries(
        agentPointMaps.flatMap(({ key, trendKey, contextKey, pointsByDate }, agentIndex) => [
          [key, agentValues[agentIndex][index]],
          [trendKey, agentTrends[agentIndex][index]],
          [contextKey, buildEfficiencyContext(pointsByDate.get(point.date))],
        ]),
      ),
    }));
  }, [aiAgentSeries, data.points, locale, selectedMetric]);

  const visibleAgentSeries = hasMultipleAiAgents
    ? aiAgentSeries.filter((series) => !hiddenAgentIds.has(series.id))
    : [];
  const showAggregate = !hasMultipleAiAgents;
  const hasAggregateData = chartData.some((point) => point.efficiencyValue !== null);
  const hasAgentData = hasMultipleAiAgents && visibleAgentSeries.some((series) => {
    const seriesKey = getAiAgentEfficiencySeriesKey(series.id);
    return chartData.some((point) => point[seriesKey] !== null && point[seriesKey] !== undefined);
  });
  const hasData = hasMultipleAiAgents ? hasAgentData : hasAggregateData;
  const isAgentMenuOpen = Boolean(agentMenuAnchorEl);
  const minChartWidth = Math.max(chartData.length * 34, 760);
  const formatMetricValue = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') {
      return 'N/D';
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 'N/D';
    }
    return activeMetric.unit === 'minutes'
      ? `${numericValue.toLocaleString(locale, { maximumFractionDigits: 2 })} ${contextLabels.minutesUnit}`
      : numericValue.toLocaleString(locale, { maximumFractionDigits: 2 });
  };
  const formatContextMinutes = (seconds: number | null) => {
    if (seconds === null) {
      return 'N/D';
    }
    return `${(Math.max(seconds, 0) / 60).toLocaleString(locale, {
      maximumFractionDigits: 2,
    })} ${contextLabels.minutesUnit}`;
  };
  const toggleAgentSeries = (agentId: string) => {
    setHiddenAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      return nextVisibleAgentCount > 0 ? nextHidden : current;
    });
  };
  const resolveSeriesLabel = (dataKey: unknown) => {
    if (dataKey === EFFICIENCY_AGGREGATE_SERIES_KEY) {
      return contextLabels.aggregateSeries;
    }
    if (typeof dataKey === 'string') {
      const series = aiAgentSeries.find((agentSeries) => (
        getAiAgentEfficiencySeriesKey(agentSeries.id) === dataKey
      ));
      if (series) {
        return agentLabel(series.label);
      }
    }
    return activeMetric.label;
  };
  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      dataKey?: unknown;
      value?: unknown;
      payload?: Record<string, unknown>;
      color?: string;
    }>;
    label?: unknown;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }
    const visiblePayload = payload.filter((item) => (
      typeof item.dataKey === 'string'
      && !isEfficiencyTrendDataKey(item.dataKey)
      && item.value !== null
      && item.value !== undefined
    ));
    if (!visiblePayload.length) {
      return null;
    }
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.35,
          borderRadius: 2,
          border: `1px solid ${alpha(activeMetric.accent, 0.24)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.96),
          minWidth: 360,
          maxWidth: 680,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, mb: 0.25 }}>
          {String(label ?? '')} · {activeMetric.label}
        </Typography>
        <Stack spacing={1}>
          {visiblePayload.map((item) => {
            const dataKey = String(item.dataKey);
            const contextKey = dataKey === EFFICIENCY_AGGREGATE_SERIES_KEY
              ? 'aggregateContext'
              : `${dataKey}_context`;
            const context = item.payload?.[contextKey];
            const typedContext = isEfficiencyContext(context) ? context : null;
            const seriesColor = item.color || activeMetric.accent;
            return (
              <Box
                key={dataKey}
                sx={{
                  pl: 1,
                  borderLeft: `3px solid ${seriesColor}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: typedContext ? 0.75 : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: seriesColor,
                      fontWeight: 900,
                      minWidth: 0,
                    }}
                  >
                    {resolveSeriesLabel(item.dataKey)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: seriesColor,
                      fontWeight: 900,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatMetricValue(item.value as number)}
                  </Typography>
                </Box>
                {typedContext ? (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.35, fontWeight: 800 }}
                    >
                      {contextLabels.baseOfDay}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.65,
                      }}
                    >
                      {[
                        [contextLabels.aiMinutes, formatContextMinutes(typedContext.aiConsumedSeconds)],
                        [contextLabels.answered, typedContext.answeredCalls.toLocaleString(locale)],
                        [contextLabels.converted, typedContext.convertedCalls.toLocaleString(locale)],
                        ...(typedContext.telephonyConvertedConsumedSeconds !== null
                          ? [[
                              contextLabels.telephonyConvertedMinutes,
                              formatContextMinutes(typedContext.telephonyConvertedConsumedSeconds),
                            ]]
                          : []),
                      ].map(([statLabel, statValue]) => (
                        <Box
                          key={statLabel}
                          sx={{
                            minWidth: 0,
                            px: 0.75,
                            py: 0.55,
                            borderRadius: 1.2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.06),
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'inline', lineHeight: 1.2 }}
                          >
                            {statLabel}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: 'inline', fontWeight: 850, lineHeight: 1.2, ml: 0.45 }}
                            noWrap
                          >
                            {statValue}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  if (!activeMetric || !hasData) {
    return (
      <InsightPanel
        title={title}
        subtitle={subtitle}
        titleIcon={titleIcon}
        accent={theme.palette.success.main}
      >
        <EmptyPanelState label={emptyLabel} />
      </InsightPanel>
    );
  }

  const chartActions = (
    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0.75} sx={{ width: '100%' }}>
      {hasMultipleAiAgents ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, width: '100%' }}>
            <Chip
              size="small"
              component="button"
              clickable
              label={buildDropdownMetricLabel(agentSelectorLabel)}
              icon={<Psychology />}
              aria-haspopup="menu"
              aria-expanded={isAgentMenuOpen}
              onClick={(event) => setAgentMenuAnchorEl(event.currentTarget)}
              sx={buildMetricChipSx(activeMetric.accent, true)}
            />
          </Box>
          <Menu
            anchorEl={agentMenuAnchorEl}
            open={isAgentMenuOpen}
            onClose={() => setAgentMenuAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.75,
                  minWidth: 220,
                  maxWidth: 340,
                },
              },
            }}
          >
            {aiAgentSeries.map((series, index) => {
              const isVisible = !hiddenAgentIds.has(series.id);
              const color = AI_AGENT_EFFICIENCY_SERIES_COLORS[
                index % AI_AGENT_EFFICIENCY_SERIES_COLORS.length
              ];
              return (
                <MenuItem
                  key={series.id}
                  dense
                  onClick={() => toggleAgentSeries(series.id)}
                >
                  <Checkbox
                    size="small"
                    checked={isVisible}
                    sx={{
                      color,
                      '&.Mui-checked': {
                        color,
                      },
                    }}
                  />
                  <ListItemText
                    primary={series.label}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      ) : null}
      <Stack
        direction="row"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
        gap={0.75}
      >
        {metricOptions.map((option) => {
          const isSelected = option.key === selectedMetric;
          return (
            <Tooltip key={option.key} title={option.help} arrow>
              <Chip
                size="small"
                component="button"
                clickable
                label={buildMetricHelpLabel(option.label)}
                icon={option.unit === 'minutes' ? <DataUsage /> : <CheckCircle />}
                aria-pressed={isSelected}
                onClick={() => setSelectedMetric(option.key)}
                sx={buildMetricChipSx(option.accent, isSelected)}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <InsightPanel
      title={title}
      subtitle={subtitle}
      titleIcon={titleIcon}
      accent={activeMetric.accent}
      actions={chartActions}
    >
      <Box sx={{ width: '100%', minHeight: 320 }}>
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
          <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                <XAxis
                  dataKey="dateLabel"
                  interval={0}
                  padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 10 }}
                  tickMargin={8}
                  angle={-34}
                  textAnchor="end"
                  height={52}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                  stroke={theme.palette.text.secondary}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => formatMetricValue(Number(value))}
                  tickLine={false}
                  axisLine={false}
                  width={74}
                />
                <RechartsTooltip content={renderTooltip} />
                {showAggregate ? (
                  <Line
                    type="monotone"
                    dataKey={EFFICIENCY_AGGREGATE_SERIES_KEY}
                    name={activeMetric.label}
                    stroke={activeMetric.accent}
                    strokeWidth={2.8}
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                ) : null}
                {showAggregate ? (
                  <Line
                    type="linear"
                    dataKey={EFFICIENCY_AGGREGATE_TREND_SERIES_KEY}
                    stroke={activeMetric.accent}
                    strokeWidth={2}
                    strokeDasharray="7 5"
                    strokeOpacity={0.54}
                    dot={false}
                    activeDot={false}
                    tooltipType="none"
                    connectNulls={false}
                  />
                ) : null}
                {hasMultipleAiAgents ? aiAgentSeries.map((series, index) => {
                  if (hiddenAgentIds.has(series.id)) {
                    return null;
                  }
                  const color = AI_AGENT_EFFICIENCY_SERIES_COLORS[
                    index % AI_AGENT_EFFICIENCY_SERIES_COLORS.length
                  ];
                  const seriesKey = getAiAgentEfficiencySeriesKey(series.id);
                  const trendKey = getAiAgentEfficiencyTrendSeriesKey(series.id);
                  return (
                    <Fragment key={series.id}>
                      <Line
                        type="monotone"
                        dataKey={seriesKey}
                        name={agentLabel(series.label)}
                        stroke={color}
                        strokeWidth={2.6}
                        dot={false}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                      />
                      <Line
                        type="linear"
                        dataKey={trendKey}
                        stroke={color}
                        strokeWidth={1.8}
                        strokeDasharray="7 5"
                        strokeOpacity={0.46}
                        dot={false}
                        activeDot={false}
                        tooltipType="none"
                        connectNulls={false}
                      />
                    </Fragment>
                  );
                }) : null}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </InsightPanel>
  );
}

type ConversationQualityMetricOption = {
  key: ConversationQualityMetricKey;
  label: string;
  help: string;
  accent: string;
  unit: 'duration' | 'percent';
};

type ConversationQualityChartContext = {
  answeredCalls: number;
  reviewEligibleCalls: number;
  convertedCalls: number;
  convertedDurationSeconds: number;
  shortInterruptedCalls: number;
  longUnconvertedCalls: number;
};

type ConversationQualityChartPoint = {
  date: string;
  dateLabel: string;
  conversationQualityValue: number | null;
  conversationQualityValueTrend: number | null;
  aggregateContext: ConversationQualityChartContext;
  [seriesKey: string]: string | number | null | ConversationQualityChartContext;
};

function roundConversationQualityMetric(value: number): number {
  return Number(value.toFixed(2));
}

function getConversationQualityMetricValue(
  point: DashboardCockpitConversationQualityPoint,
  metricKey: ConversationQualityMetricKey,
): number | null {
  if (metricKey === 'averageConvertedSeconds') {
    return point.convertedCalls > 0
      ? roundConversationQualityMetric(point.convertedDurationSeconds / point.convertedCalls)
      : null;
  }
  if (metricKey === 'shortInterruptionRate') {
    return point.reviewEligibleCalls > 0
      ? roundConversationQualityMetric((point.shortInterruptedCalls / point.reviewEligibleCalls) * 100)
      : null;
  }
  return point.reviewEligibleCalls > 0
    ? roundConversationQualityMetric((point.longUnconvertedCalls / point.reviewEligibleCalls) * 100)
    : null;
}

function buildConversationQualityContext(
  point: DashboardCockpitConversationQualityPoint | undefined,
): ConversationQualityChartContext {
  return {
    answeredCalls: point?.answeredCalls ?? 0,
    reviewEligibleCalls: point?.reviewEligibleCalls ?? 0,
    convertedCalls: point?.convertedCalls ?? 0,
    convertedDurationSeconds: point?.convertedDurationSeconds ?? 0,
    shortInterruptedCalls: point?.shortInterruptedCalls ?? 0,
    longUnconvertedCalls: point?.longUnconvertedCalls ?? 0,
  };
}

function isConversationQualityContext(value: unknown): value is ConversationQualityChartContext {
  return (
    typeof value === 'object'
    && value !== null
    && 'reviewEligibleCalls' in value
    && 'convertedCalls' in value
    && 'shortInterruptedCalls' in value
    && 'longUnconvertedCalls' in value
  );
}

function isConversationQualityTrendDataKey(dataKey: unknown): boolean {
  return (
    dataKey === CONVERSATION_QUALITY_AGGREGATE_TREND_SERIES_KEY
    || (
      typeof dataKey === 'string'
      && dataKey.startsWith(`${AI_AGENT_CONVERSATION_QUALITY_TREND_SERIES_KEY_PREFIX}_`)
    )
  );
}

function aggregateConversationQualityPoints(
  points: DashboardCockpitConversationQualityPoint[],
): DashboardCockpitConversationQualityPoint[] {
  const pointsByDate = new Map<string, DashboardCockpitConversationQualityPoint>();
  points.forEach((point) => {
    const current = pointsByDate.get(point.date) ?? {
      date: point.date,
      answeredCalls: 0,
      reviewEligibleCalls: 0,
      convertedCalls: 0,
      convertedDurationSeconds: 0,
      shortInterruptedCalls: 0,
      longUnconvertedCalls: 0,
    };
    current.answeredCalls += point.answeredCalls ?? 0;
    current.reviewEligibleCalls += point.reviewEligibleCalls ?? 0;
    current.convertedCalls += point.convertedCalls ?? 0;
    current.convertedDurationSeconds += point.convertedDurationSeconds ?? 0;
    current.shortInterruptedCalls += point.shortInterruptedCalls ?? 0;
    current.longUnconvertedCalls += point.longUnconvertedCalls ?? 0;
    pointsByDate.set(point.date, current);
  });
  return Array.from(pointsByDate.values()).sort((left, right) => left.date.localeCompare(right.date));
}

function ConversationQualityChart({
  title,
  subtitle,
  titleIcon,
  data,
  metricLabels,
  metricHelps,
  agentLabel,
  agentSelectorLabel,
  emptyLabel,
  contextLabels,
  locale,
  formatNumber,
  formatPercent,
  formatDuration,
}: {
  title: string;
  subtitle: string;
  titleIcon?: ReactNode;
  data: DashboardCockpitConversationQualitySlice;
  metricLabels: Record<ConversationQualityMetricKey, string>;
  metricHelps: Record<ConversationQualityMetricKey, string>;
  agentLabel: (agentName: string) => string;
  agentSelectorLabel: string;
  emptyLabel: string;
  contextLabels: {
    operation: string;
    baseUsed: string;
    answered: string;
    eligible: string;
    converted: string;
    shortCalls: string;
    longCalls: string;
    convertedBase: string;
    shortBase: string;
    longBase: string;
    thresholdShort: string;
    thresholdLong: string;
  };
  locale: string;
  formatNumber: (value: number) => string;
  formatPercent: (value: number, isAverage?: boolean) => string;
  formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string;
}) {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<ConversationQualityMetricKey>('averageConvertedSeconds');
  const [hiddenAgentIds, setHiddenAgentIds] = useState<Set<string>>(() => new Set());
  const [agentMenuAnchorEl, setAgentMenuAnchorEl] = useState<HTMLElement | null>(null);
  const aiAgentSeries = useMemo(() => data.aiAgentSeries ?? [], [data.aiAgentSeries]);
  const hasMultipleAiAgents = aiAgentSeries.length > 1;
  const metricOptions = useMemo<ConversationQualityMetricOption[]>(
    () => [
      {
        key: 'averageConvertedSeconds',
        label: metricLabels.averageConvertedSeconds,
        help: metricHelps.averageConvertedSeconds,
        accent: theme.palette.success.main,
        unit: 'duration',
      },
      {
        key: 'shortInterruptionRate',
        label: metricLabels.shortInterruptionRate,
        help: metricHelps.shortInterruptionRate,
        accent: theme.palette.warning.main,
        unit: 'percent',
      },
      {
        key: 'longUnconvertedRate',
        label: metricLabels.longUnconvertedRate,
        help: metricHelps.longUnconvertedRate,
        accent: theme.palette.primary.main,
        unit: 'percent',
      },
    ],
    [
      metricHelps.averageConvertedSeconds,
      metricHelps.longUnconvertedRate,
      metricHelps.shortInterruptionRate,
      metricLabels.averageConvertedSeconds,
      metricLabels.longUnconvertedRate,
      metricLabels.shortInterruptionRate,
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
    ],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];

  useEffect(() => {
    setHiddenAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);

  const visibleAgentSeries = hasMultipleAiAgents
    ? aiAgentSeries.filter((series) => !hiddenAgentIds.has(series.id))
    : [];
  const selectedAggregatePoints = hasMultipleAiAgents
    ? aggregateConversationQualityPoints(visibleAgentSeries.flatMap((series) => series.points))
    : data.points;
  const summaryContext = selectedAggregatePoints.reduce<ConversationQualityChartContext>(
    (summary, point) => ({
      answeredCalls: summary.answeredCalls + point.answeredCalls,
      reviewEligibleCalls: summary.reviewEligibleCalls + point.reviewEligibleCalls,
      convertedCalls: summary.convertedCalls + point.convertedCalls,
      convertedDurationSeconds: summary.convertedDurationSeconds + point.convertedDurationSeconds,
      shortInterruptedCalls: summary.shortInterruptedCalls + point.shortInterruptedCalls,
      longUnconvertedCalls: summary.longUnconvertedCalls + point.longUnconvertedCalls,
    }),
    {
      answeredCalls: 0,
      reviewEligibleCalls: 0,
      convertedCalls: 0,
      convertedDurationSeconds: 0,
      shortInterruptedCalls: 0,
      longUnconvertedCalls: 0,
    },
  );
  const summaryAsPoint: DashboardCockpitConversationQualityPoint = {
    date: 'summary',
    ...summaryContext,
  };
  const chartData = useMemo<ConversationQualityChartPoint[]>(() => {
    const agentPointMaps = aiAgentSeries.map((series) => ({
      key: getAiAgentConversationQualitySeriesKey(series.id),
      trendKey: getAiAgentConversationQualityTrendSeriesKey(series.id),
      contextKey: `${getAiAgentConversationQualitySeriesKey(series.id)}_context`,
      pointsByDate: new Map(series.points.map((point) => [point.date, point])),
    }));
    const basePoints = data.points.map((point) => {
      return {
        date: point.date,
        dateLabel: formatHistoryChartDateLabel(point.date, locale),
        conversationQualityValue: getConversationQualityMetricValue(point, selectedMetric),
        aggregateContext: buildConversationQualityContext(point),
      };
    });
    const aggregateTrend = buildLinearTrend(basePoints.map((point) => point.conversationQualityValue));
    const agentValues = agentPointMaps.map(({ pointsByDate }) => (
      basePoints.map((point) => getConversationQualityMetricValue(
        pointsByDate.get(point.date) ?? {
          date: point.date,
          answeredCalls: 0,
          reviewEligibleCalls: 0,
          convertedCalls: 0,
          convertedDurationSeconds: 0,
          shortInterruptedCalls: 0,
          longUnconvertedCalls: 0,
        },
        selectedMetric,
      ))
    ));
    const agentTrends = agentValues.map((values) => buildLinearTrend(values));
    return basePoints.map((point, index) => ({
      ...point,
      conversationQualityValueTrend: aggregateTrend[index],
      ...Object.fromEntries(
        agentPointMaps.flatMap(({ key, trendKey, contextKey, pointsByDate }, agentIndex) => [
          [key, agentValues[agentIndex][index]],
          [trendKey, agentTrends[agentIndex][index]],
          [contextKey, buildConversationQualityContext(pointsByDate.get(point.date))],
        ]),
      ),
    }));
  }, [aiAgentSeries, data.points, locale, selectedMetric]);

  const hasSelectedData = selectedAggregatePoints.some((point) => (
    point.reviewEligibleCalls > 0 || point.convertedCalls > 0
  ));
  const isAgentMenuOpen = Boolean(agentMenuAnchorEl);
  const minChartWidth = Math.max(chartData.length * 34, 760);
  const showAggregate = !hasMultipleAiAgents;

  const formatMetricValue = (value: number | string | null | undefined, averageDuration = false) => {
    if (value === null || value === undefined || value === '') {
      return 'N/D';
    }
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 'N/D';
    }
    return activeMetric.unit === 'duration'
      ? formatDuration(numericValue, averageDuration)
      : formatPercent(numericValue);
  };
  const resolveSeriesLabel = (dataKey: unknown) => {
    if (dataKey === CONVERSATION_QUALITY_AGGREGATE_SERIES_KEY) {
      return contextLabels.operation;
    }
    if (typeof dataKey === 'string') {
      const series = aiAgentSeries.find((agentSeries) => (
        getAiAgentConversationQualitySeriesKey(agentSeries.id) === dataKey
      ));
      if (series) {
        return agentLabel(series.label);
      }
    }
    return activeMetric.label;
  };
  const toggleAgentSeries = (agentId: string) => {
    setHiddenAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      return nextVisibleAgentCount > 0 ? nextHidden : current;
    });
  };

  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      dataKey?: unknown;
      value?: unknown;
      payload?: Record<string, unknown>;
      color?: string;
    }>;
    label?: unknown;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }
    const visiblePayload = payload.filter((item) => (
      typeof item.dataKey === 'string'
      && !isConversationQualityTrendDataKey(item.dataKey)
      && item.value !== null
      && item.value !== undefined
    ));
    if (!visiblePayload.length) {
      return null;
    }
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.35,
          borderRadius: 2,
          border: `1px solid ${alpha(activeMetric.accent, 0.24)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.96),
          minWidth: 340,
          maxWidth: 680,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, mb: 0.25 }}>
          {String(label ?? '')} · {activeMetric.label}
        </Typography>
        <Stack spacing={1}>
          {visiblePayload.map((item) => {
            const dataKey = String(item.dataKey);
            const contextKey = dataKey === CONVERSATION_QUALITY_AGGREGATE_SERIES_KEY
              ? 'aggregateContext'
              : `${dataKey}_context`;
            const context = item.payload?.[contextKey];
            const typedContext = isConversationQualityContext(context) ? context : null;
            const seriesColor = item.color || activeMetric.accent;
            return (
              <Box key={dataKey} sx={{ pl: 1, borderLeft: `3px solid ${seriesColor}` }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: typedContext ? 0.75 : 0,
                  }}
                >
                  <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, minWidth: 0 }}>
                    {resolveSeriesLabel(item.dataKey)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, whiteSpace: 'nowrap' }}>
                    {formatMetricValue(item.value as number, true)}
                  </Typography>
                </Box>
                {typedContext ? (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.35, fontWeight: 800 }}>
                      {contextLabels.baseUsed}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.65 }}>
                      {[
                        [contextLabels.answered, formatNumber(typedContext.answeredCalls)],
                        [contextLabels.eligible, formatNumber(typedContext.reviewEligibleCalls)],
                        [contextLabels.converted, formatNumber(typedContext.convertedCalls)],
                        [contextLabels.shortCalls, formatNumber(typedContext.shortInterruptedCalls)],
                        [contextLabels.longCalls, formatNumber(typedContext.longUnconvertedCalls)],
                      ].map(([statLabel, statValue]) => (
                        <Box
                          key={statLabel}
                          sx={{
                            minWidth: 0,
                            px: 0.75,
                            py: 0.55,
                            borderRadius: 1.2,
                            backgroundColor: alpha(theme.palette.text.secondary, 0.06),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'inline', lineHeight: 1.2 }}>
                            {statLabel}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'inline', fontWeight: 850, lineHeight: 1.2, ml: 0.45 }} noWrap>
                            {statValue}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  if (!activeMetric || !hasSelectedData) {
    return (
      <InsightPanel
        title={title}
        subtitle={subtitle}
        titleIcon={titleIcon}
        accent={theme.palette.success.main}
      >
        <EmptyPanelState label={emptyLabel} />
      </InsightPanel>
    );
  }

  const summaryCards = [
    {
      key: 'averageConvertedSeconds',
      label: metricLabels.averageConvertedSeconds,
      value: formatDuration(
        getConversationQualityMetricValue(summaryAsPoint, 'averageConvertedSeconds'),
        true,
      ),
      context: contextLabels.convertedBase.replace('__value__', formatNumber(summaryContext.convertedCalls)),
      help: metricHelps.averageConvertedSeconds,
      accent: theme.palette.success.main,
      icon: <AccessTime />,
    },
    {
      key: 'shortInterruptionRate',
      label: metricLabels.shortInterruptionRate,
      value: formatPercent(getConversationQualityMetricValue(summaryAsPoint, 'shortInterruptionRate') ?? 0),
      context: contextLabels.shortBase
        .replace('__itemCount__', formatNumber(summaryContext.shortInterruptedCalls))
        .replace('__base__', formatNumber(summaryContext.reviewEligibleCalls)),
      help: `${metricHelps.shortInterruptionRate} ${contextLabels.thresholdShort.replace('__seconds__', formatNumber(data.shortCallThresholdSeconds))}`,
      accent: theme.palette.warning.main,
      icon: <PhoneInTalk />,
    },
    {
      key: 'longUnconvertedRate',
      label: metricLabels.longUnconvertedRate,
      value: formatPercent(getConversationQualityMetricValue(summaryAsPoint, 'longUnconvertedRate') ?? 0),
      context: contextLabels.longBase
        .replace('__itemCount__', formatNumber(summaryContext.longUnconvertedCalls))
        .replace('__base__', formatNumber(summaryContext.reviewEligibleCalls)),
      help: `${metricHelps.longUnconvertedRate} ${contextLabels.thresholdLong.replace('__seconds__', formatNumber(data.longUnconvertedThresholdSeconds))}`,
      accent: theme.palette.primary.main,
      icon: <QueryStats />,
    },
  ];
  const chartActions = (
    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0.75} sx={{ width: '100%' }}>
      {hasMultipleAiAgents ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, width: '100%' }}>
            <Chip
              size="small"
              component="button"
              clickable
              label={buildDropdownMetricLabel(agentSelectorLabel)}
              icon={<Psychology />}
              aria-haspopup="menu"
              aria-expanded={isAgentMenuOpen}
              onClick={(event) => setAgentMenuAnchorEl(event.currentTarget)}
              sx={buildMetricChipSx(activeMetric.accent, true)}
            />
          </Box>
          <Menu
            anchorEl={agentMenuAnchorEl}
            open={isAgentMenuOpen}
            onClose={() => setAgentMenuAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.75,
                  minWidth: 220,
                  maxWidth: 340,
                },
              },
            }}
          >
            {aiAgentSeries.map((series, index) => {
              const isVisible = !hiddenAgentIds.has(series.id);
              const color = AI_AGENT_CONVERSATION_QUALITY_SERIES_COLORS[
                index % AI_AGENT_CONVERSATION_QUALITY_SERIES_COLORS.length
              ];
              return (
                <MenuItem key={series.id} dense onClick={() => toggleAgentSeries(series.id)}>
                  <Checkbox
                    size="small"
                    checked={isVisible}
                    sx={{
                      color,
                      '&.Mui-checked': {
                        color,
                      },
                    }}
                  />
                  <ListItemText
                    primary={series.label}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      ) : null}
      <Stack
        direction="row"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
        gap={0.75}
      >
        {metricOptions.map((option) => {
          const isSelected = option.key === selectedMetric;
          return (
            <Tooltip key={option.key} title={option.help} arrow>
              <Chip
                size="small"
                component="button"
                clickable
                label={buildMetricHelpLabel(option.label)}
                icon={option.unit === 'duration' ? <AccessTime /> : <CheckCircle />}
                aria-pressed={isSelected}
                onClick={() => setSelectedMetric(option.key)}
                sx={buildMetricChipSx(option.accent, isSelected)}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );

  return (
    <InsightPanel
      title={title}
      subtitle={subtitle}
      titleIcon={titleIcon}
      accent={activeMetric.accent}
      actions={chartActions}
    >
      <Stack gap={2}>
        <Box sx={{ width: '100%', minHeight: 300, minWidth: 0 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
            <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                  <XAxis
                    dataKey="dateLabel"
                    interval={0}
                    padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickMargin={8}
                    angle={-34}
                    textAnchor="end"
                    height={52}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => formatMetricValue(Number(value))}
                    tickLine={false}
                    axisLine={false}
                    width={activeMetric.unit === 'duration' ? 70 : 62}
                  />
                  <RechartsTooltip content={renderTooltip} />
                  {showAggregate ? (
                    <Line
                      type="monotone"
                      dataKey={CONVERSATION_QUALITY_AGGREGATE_SERIES_KEY}
                      name={activeMetric.label}
                      stroke={activeMetric.accent}
                      strokeWidth={2.8}
                      dot={false}
                      activeDot={{ r: 5 }}
                      connectNulls={false}
                    />
                  ) : null}
                  {showAggregate ? (
                    <Line
                      type="linear"
                      dataKey={CONVERSATION_QUALITY_AGGREGATE_TREND_SERIES_KEY}
                      stroke={activeMetric.accent}
                      strokeWidth={2}
                      strokeDasharray="7 5"
                      strokeOpacity={0.54}
                      dot={false}
                      activeDot={false}
                      tooltipType="none"
                      connectNulls={false}
                    />
                  ) : null}
                  {hasMultipleAiAgents ? aiAgentSeries.map((series, index) => {
                    if (hiddenAgentIds.has(series.id)) {
                      return null;
                    }
                    const color = AI_AGENT_CONVERSATION_QUALITY_SERIES_COLORS[
                      index % AI_AGENT_CONVERSATION_QUALITY_SERIES_COLORS.length
                    ];
                    const seriesKey = getAiAgentConversationQualitySeriesKey(series.id);
                    const trendKey = getAiAgentConversationQualityTrendSeriesKey(series.id);
                    return (
                      <Fragment key={series.id}>
                        <Line
                          type="monotone"
                          dataKey={seriesKey}
                          name={agentLabel(series.label)}
                          stroke={color}
                          strokeWidth={2.6}
                          dot={false}
                          activeDot={{ r: 5 }}
                          connectNulls={false}
                        />
                        <Line
                          type="linear"
                          dataKey={trendKey}
                          stroke={color}
                          strokeWidth={1.8}
                          strokeDasharray="7 5"
                          strokeOpacity={0.46}
                          dot={false}
                          activeDot={false}
                          tooltipType="none"
                          connectNulls={false}
                        />
                      </Fragment>
                    );
                  }) : null}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.2,
          }}
        >
          {summaryCards.map((card) => (
            <Box
              key={card.key}
              sx={{
                p: 1.45,
                borderRadius: '18px',
                border: `1px solid ${alpha(card.accent, 0.2)}`,
                backgroundColor: alpha(card.accent, 0.065),
                minWidth: 0,
              }}
            >
              <Stack direction="row" gap={0.9} alignItems="flex-start">
                <Box sx={{ color: card.accent, display: 'flex', mt: 0.1, '& svg': { fontSize: 18 } }}>
                  {card.icon}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }} noWrap>
                      {card.label}
                    </Typography>
                    <Tooltip title={card.help} arrow>
                      <HelpOutline sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Stack>
                  <Typography
                    sx={{
                      mt: 0.3,
                      fontFamily: DISPLAY_FONT,
                      fontSize: '1.65rem',
                      lineHeight: 1,
                      fontWeight: 800,
                      color: card.accent,
                    }}
                  >
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    {card.context}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Box>
      </Stack>
    </InsightPanel>
  );
}

type CampaignBaseHealthMetricOption = {
  key: CampaignBaseHealthMetricKey;
  label: string;
  shortLabel: string;
  help: string;
  accent: string;
  icon: ReactElement;
};

type CampaignBaseHealthContext = {
  campaignName: string;
  aiAgentName: string | null;
  attemptedContacts: number;
  resultContacts: number;
};

type CampaignBaseHealthChartPoint = {
  date: string;
  dateLabel: string;
  [seriesKey: string]: string | number | null | CampaignBaseHealthContext;
};

function getCampaignBaseHealthMetricCount(
  row: DashboardCockpitCampaignBaseHealthCampaign | DashboardCockpitCampaignBaseHealthPoint,
  metricKey: CampaignBaseHealthMetricKey,
): number {
  if (metricKey === 'answerRate') {
    return row.answeredContacts;
  }
  if (metricKey === 'invalidRate') {
    return row.invalidContacts;
  }
  if (metricKey === 'unreachableRate') {
    return row.unreachableContacts;
  }
  if (metricKey === 'noAttendanceRate') {
    return row.noAttendanceContacts;
  }
  if (metricKey === 'voicemailRate') {
    return row.voicemailContacts;
  }
  if (metricKey === 'mismatchRate') {
    return row.mismatchContacts;
  }
  return row.optoutContacts;
}

function getCampaignBaseHealthMetricRate(
  row: DashboardCockpitCampaignBaseHealthCampaign | DashboardCockpitCampaignBaseHealthPoint,
  metricKey: CampaignBaseHealthMetricKey,
): number | null {
  if (!row.attemptedContacts) {
    return null;
  }
  return Number(((getCampaignBaseHealthMetricCount(row, metricKey) / row.attemptedContacts) * 100).toFixed(2));
}

function isCampaignBaseHealthContext(value: unknown): value is CampaignBaseHealthContext {
  return (
    typeof value === 'object'
    && value !== null
    && 'attemptedContacts' in value
    && 'resultContacts' in value
  );
}

function CampaignBaseHealthPanel({
  data,
}: {
  data: DashboardCockpitCampaignBaseHealthSlice;
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { locale, formatNumber, formatPercent } = useNumberFormat();
  const [selectedMetric, setSelectedMetric] = useState<CampaignBaseHealthMetricKey>('unreachableRate');
  const [hiddenAgentIds, setHiddenAgentIds] = useState<Set<string>>(() => new Set());
  const [agentMenuAnchorEl, setAgentMenuAnchorEl] = useState<HTMLElement | null>(null);
  const metricOptions = useMemo<CampaignBaseHealthMetricOption[]>(
    () => [
      {
        key: 'answerRate',
        label: t('pages:dashboard.patterns.baseHealth.answerRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.answerShort'),
        help: t('pages:dashboard.patterns.baseHealth.answerHelp'),
        accent: theme.palette.info.main,
        icon: <PhoneInTalk />,
      },
      {
        key: 'invalidRate',
        label: t('pages:dashboard.patterns.baseHealth.invalidRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.invalidShort'),
        help: t('pages:dashboard.patterns.baseHealth.invalidHelp'),
        accent: theme.palette.error.main,
        icon: <ContactPhone />,
      },
      {
        key: 'unreachableRate',
        label: t('pages:dashboard.patterns.baseHealth.unreachableRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.unreachableShort'),
        help: t('pages:dashboard.patterns.baseHealth.unreachableHelp'),
        accent: theme.palette.warning.main,
        icon: <PhoneInTalk />,
      },
      {
        key: 'noAttendanceRate',
        label: t('pages:dashboard.patterns.baseHealth.noAttendanceRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.noAttendanceShort'),
        help: t('pages:dashboard.patterns.baseHealth.noAttendanceHelp'),
        accent: theme.palette.warning.dark,
        icon: <PhoneInTalk />,
      },
      {
        key: 'voicemailRate',
        label: t('pages:dashboard.patterns.baseHealth.voicemailRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.voicemailShort'),
        help: t('pages:dashboard.patterns.baseHealth.voicemailHelp'),
        accent: theme.palette.secondary.main,
        icon: <GraphicEq />,
      },
      {
        key: 'mismatchRate',
        label: t('pages:dashboard.patterns.baseHealth.mismatchRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.mismatchShort'),
        help: t('pages:dashboard.patterns.baseHealth.mismatchHelp'),
        accent: theme.palette.primary.main,
        icon: <PersonOff />,
      },
      {
        key: 'optoutRate',
        label: t('pages:dashboard.patterns.baseHealth.optoutRate'),
        shortLabel: t('pages:dashboard.patterns.baseHealth.optoutShort'),
        help: t('pages:dashboard.patterns.baseHealth.optoutHelp'),
        accent: theme.palette.error.light,
        icon: <Block />,
      },
    ],
    [
      t,
      theme.palette.error.light,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.warning.dark,
      theme.palette.warning.main,
    ],
  );
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];
  const agentOptions = useMemo(
    () => Array.from(
      new Map(
        data.campaigns
          .filter((campaign) => campaign.aiAgentId)
          .map((campaign) => [
            campaign.aiAgentId as string,
            {
              id: campaign.aiAgentId as string,
              label: campaign.aiAgentName || t('pages:dashboard.patterns.baseHealth.unknownAgent'),
            },
          ]),
      ).values(),
    ),
    [data.campaigns, t],
  );
  const hasMultipleAgents = agentOptions.length > 1;
  const visibleCampaigns = useMemo(
    () => data.campaigns.filter((campaign) => (
      !campaign.aiAgentId || !hiddenAgentIds.has(campaign.aiAgentId)
    )),
    [data.campaigns, hiddenAgentIds],
  );
  const visibleCampaignIds = useMemo(
    () => new Set(visibleCampaigns.map((campaign) => campaign.id)),
    [visibleCampaigns],
  );
  const rankedCampaigns = useMemo(
    () => [...visibleCampaigns].sort((left, right) => {
      const leftRate = getCampaignBaseHealthMetricRate(left, selectedMetric) ?? -1;
      const rightRate = getCampaignBaseHealthMetricRate(right, selectedMetric) ?? -1;
      const leftCount = getCampaignBaseHealthMetricCount(left, selectedMetric);
      const rightCount = getCampaignBaseHealthMetricCount(right, selectedMetric);
      return (
        (rightRate - leftRate)
        || (rightCount - leftCount)
        || (right.attemptedContacts - left.attemptedContacts)
        || left.name.localeCompare(right.name)
      );
    }),
    [selectedMetric, visibleCampaigns],
  );
  const chartCampaigns = rankedCampaigns.slice(0, CAMPAIGN_BASE_HEALTH_MAX_CHART_CAMPAIGNS);
  const chartCampaignIds = useMemo(
    () => new Set(chartCampaigns.map((campaign) => campaign.id)),
    [chartCampaigns],
  );
  const pointsByCampaignAndDate = useMemo(
    () => new Map(
      data.points
        .filter((point) => chartCampaignIds.has(point.campaignId))
        .map((point) => [`${point.campaignId}:${point.date}`, point]),
    ),
    [chartCampaignIds, data.points],
  );
  const days = useMemo(
    () => Array.from(new Set(data.points.map((point) => point.date))).sort(),
    [data.points],
  );
  const chartData = useMemo<CampaignBaseHealthChartPoint[]>(
    () => days.map((day) => ({
      date: day,
      dateLabel: formatHistoryChartDateLabel(day, locale),
      ...Object.fromEntries(
        chartCampaigns.flatMap((campaign) => {
          const seriesKey = getCampaignBaseHealthSeriesKey(campaign.id);
          const point = pointsByCampaignAndDate.get(`${campaign.id}:${day}`);
          const rate = point ? getCampaignBaseHealthMetricRate(point, selectedMetric) : null;
          const resultContacts = point ? getCampaignBaseHealthMetricCount(point, selectedMetric) : 0;
          const context: CampaignBaseHealthContext = {
            campaignName: campaign.name,
            aiAgentName: campaign.aiAgentName,
            attemptedContacts: point?.attemptedContacts ?? 0,
            resultContacts,
          };
          return [
            [seriesKey, rate],
            [`${seriesKey}_context`, context],
          ];
        }),
      ),
    })),
    [chartCampaigns, days, locale, pointsByCampaignAndDate, selectedMetric],
  );

  useEffect(() => {
    setHiddenAgentIds((current) => {
      const knownAgentIds = new Set(agentOptions.map((agent) => agent.id));
      const nextHidden = new Set(Array.from(current).filter((agentId) => knownAgentIds.has(agentId)));
      if (nextHidden.size >= agentOptions.length && agentOptions.length > 0) {
        nextHidden.delete(agentOptions[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [agentOptions]);

  const toggleAgent = (agentId: string) => {
    setHiddenAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleCount = agentOptions.filter((agent) => !nextHidden.has(agent.id)).length;
      return nextVisibleCount > 0 ? nextHidden : current;
    });
  };
  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      dataKey?: unknown;
      value?: unknown;
      payload?: Record<string, unknown>;
      color?: string;
    }>;
    label?: unknown;
  }) => {
    if (!active || !payload?.length) {
      return null;
    }
    const visiblePayload = payload
      .filter((item) => (
        typeof item.dataKey === 'string'
        && item.value !== null
        && item.value !== undefined
      ))
      .map((item) => ({
        item,
        numericValue: Number(item.value ?? 0),
      }))
      .sort((left, right) => right.numericValue - left.numericValue);
    if (!visiblePayload.length) {
      return null;
    }
    return (
      <DenseChartTooltipShell accent={activeMetric.accent} title={`${String(label ?? '')} · ${activeMetric.label}`}>
        {visiblePayload.map(({ item, numericValue }) => {
          const dataKey = String(item.dataKey);
          const context = item.payload?.[`${dataKey}_context`];
          const typedContext = isCampaignBaseHealthContext(context) ? context : null;
          const seriesColor = item.color || activeMetric.accent;
          return (
            <Box key={dataKey} sx={{ pl: 1, borderLeft: `3px solid ${seriesColor}` }}>
              <Stack direction="row" justifyContent="space-between" gap={2} alignItems="baseline">
                <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, minWidth: 0 }} noWrap>
                  {typedContext?.campaignName || activeMetric.label}
                </Typography>
                <Typography variant="caption" sx={{ color: seriesColor, fontWeight: 900, whiteSpace: 'nowrap' }}>
                  {formatPercent(numericValue)}
                </Typography>
              </Stack>
              {typedContext ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                  {t('pages:dashboard.patterns.baseHealth.tooltipBase', {
                    result: formatNumber(typedContext.resultContacts),
                    attempted: formatNumber(typedContext.attemptedContacts),
                  })}
                  {typedContext.aiAgentName ? ` · ${typedContext.aiAgentName}` : ''}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </DenseChartTooltipShell>
    );
  };

  if (!data.campaigns.length || !visibleCampaigns.length) {
    return (
      <InsightPanel
        title={t('pages:dashboard.patterns.baseHealth.title')}
        subtitle={t('pages:dashboard.patterns.baseHealth.subtitle', {
          days: formatNumber(data.windowDays),
        })}
        titleIcon={<ContactPhone />}
        accent={theme.palette.warning.main}
      >
        <EmptyPanelState label={t('pages:dashboard.patterns.baseHealth.empty')} />
      </InsightPanel>
    );
  }

  const isAgentMenuOpen = Boolean(agentMenuAnchorEl);
  const minChartWidth = Math.max(days.length * 34, 760);
  const chartActions = (
    <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} gap={0.75} sx={{ width: '100%' }}>
      {hasMultipleAgents ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, width: '100%' }}>
            <Chip
              size="small"
              component="button"
              clickable
              label={buildDropdownMetricLabel(t('pages:dashboard.patterns.baseHealth.agents'))}
              icon={<Psychology />}
              aria-haspopup="menu"
              aria-expanded={isAgentMenuOpen}
              onClick={(event) => setAgentMenuAnchorEl(event.currentTarget)}
              sx={buildMetricChipSx(activeMetric.accent, true)}
            />
          </Box>
          <Menu
            anchorEl={agentMenuAnchorEl}
            open={isAgentMenuOpen}
            onClose={() => setAgentMenuAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.75,
                  minWidth: 220,
                  maxWidth: 340,
                },
              },
            }}
          >
            {agentOptions.map((agent, index) => {
              const isVisible = !hiddenAgentIds.has(agent.id);
              const color = CAMPAIGN_BASE_HEALTH_COLORS[index % CAMPAIGN_BASE_HEALTH_COLORS.length];
              return (
                <MenuItem key={agent.id} dense onClick={() => toggleAgent(agent.id)}>
                  <Checkbox
                    size="small"
                    checked={isVisible}
                    sx={{
                      color,
                      '&.Mui-checked': {
                        color,
                      },
                    }}
                  />
                  <ListItemText
                    primary={agent.label}
                    primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  />
                </MenuItem>
              );
            })}
          </Menu>
        </>
      ) : null}
      <Stack
        direction="row"
        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
        flexWrap="wrap"
        useFlexGap
        gap={0.75}
      >
        {metricOptions.map((option) => {
          const isSelected = option.key === selectedMetric;
          return (
            <Tooltip key={option.key} title={option.help} arrow>
              <Chip
                size="small"
                component="button"
                clickable
                label={buildMetricHelpLabel(option.label)}
                icon={option.icon}
                aria-pressed={isSelected}
                onClick={() => setSelectedMetric(option.key)}
                sx={buildMetricChipSx(option.accent, isSelected)}
              />
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
  const visiblePoints = data.points.filter((point) => visibleCampaignIds.has(point.campaignId));
  const visibleAttempted = visiblePoints.reduce((sum, point) => sum + point.attemptedContacts, 0);
  const cards = rankedCampaigns.slice(0, 6);

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.baseHealth.title')}
      subtitle={t('pages:dashboard.patterns.baseHealth.subtitle', {
        days: formatNumber(data.windowDays),
      })}
      titleIcon={<ContactPhone />}
      accent={activeMetric.accent}
      actions={chartActions}
    >
      <Stack gap={2}>
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.patterns.baseHealth.readingHint')}
        </Typography>
        <Box sx={{ width: '100%', minHeight: 300, minWidth: 0 }}>
          <Box sx={DENSE_CHART_SCROLL_AREA_SX} onWheel={handleDenseChartTooltipWheel}>
            <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                  <XAxis
                    dataKey="dateLabel"
                    interval={0}
                    padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickMargin={8}
                    angle={-34}
                    textAnchor="end"
                    height={52}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => formatPercent(Number(value))}
                    tickLine={false}
                    axisLine={false}
                    width={62}
                  />
                  <RechartsTooltip
                    content={renderTooltip}
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={DENSE_CHART_TOOLTIP_WRAPPER_STYLE}
                  />
                  {chartCampaigns.map((campaign, index) => {
                    const seriesKey = getCampaignBaseHealthSeriesKey(campaign.id);
                    const color = CAMPAIGN_BASE_HEALTH_COLORS[index % CAMPAIGN_BASE_HEALTH_COLORS.length];
                    return (
                      <Line
                        key={campaign.id}
                        type="monotone"
                        dataKey={seriesKey}
                        name={campaign.name}
                        stroke={color}
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5 }}
                        connectNulls={false}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.12em', fontWeight: 850 }}>
            {t('pages:dashboard.patterns.baseHealth.campaignsToReview')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('pages:dashboard.patterns.baseHealth.visibleBase', {
              value: formatNumber(visibleAttempted),
            })}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
            gap: 1.25,
          }}
        >
          {cards.map((campaign) => {
            const selectedRate = getCampaignBaseHealthMetricRate(campaign, selectedMetric) ?? 0;
            const selectedCount = getCampaignBaseHealthMetricCount(campaign, selectedMetric);
            const campaignPath = `/campaigns/${campaign.id}`;
            return (
              <Box
                key={campaign.id}
                sx={{
                  p: 1.45,
                  borderRadius: '18px',
                  border: `1px solid ${alpha(activeMetric.accent, 0.18)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.55),
                  minWidth: 0,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      component={RouterLink}
                      to={campaignPath}
                      sx={{
                        color: 'text.primary',
                        display: 'block',
                        fontWeight: 900,
                        textDecoration: 'none',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          color: activeMetric.accent,
                        },
                      }}
                    >
                      {campaign.name}
                    </Typography>
                    {campaign.aiAgentName ? (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {campaign.aiAgentName}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: DISPLAY_FONT,
                        fontSize: '1.45rem',
                        lineHeight: 1,
                        fontWeight: 850,
                        color: activeMetric.accent,
                      }}
                    >
                      {formatPercent(selectedRate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatNumber(selectedCount)}
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" justifyContent="space-between" gap={2} sx={{ mt: 0.8 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:dashboard.patterns.baseHealth.attemptedContacts', {
                      value: formatNumber(campaign.attemptedContacts),
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {activeMetric.label}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Stack>
    </InsightPanel>
  );
}

const TERRITORY_LEVEL_COLLECTIONS: Record<
  DashboardCockpitTerritoryLevel,
  keyof DashboardCockpitTerritoryPatterns
> = {
  region: 'regions',
  state: 'states',
  ddd: 'ddds',
};

function getTerritoryMetricValue(row: DashboardCockpitTerritoryPattern, metricKey: TerritoryMetricKey) {
  return row[metricKey] ?? 0;
}

function getTerritoryMetricContext(row: DashboardCockpitTerritoryPattern, metricKey: TerritoryMetricKey) {
  if (metricKey === 'conversionRate') {
    return {
      value: row.conversionEligible,
      translationKey: 'eligibleAnswersCount' as const,
    };
  }

  return {
    value: row.attempts,
    translationKey: 'attemptsCount' as const,
  };
}

function getTerritoryMetricResult(row: DashboardCockpitTerritoryPattern, metricKey: TerritoryMetricKey) {
  if (metricKey === 'conversionRate') {
    return {
      value: row.converted,
      translationKey: 'convertedResultCount' as const,
    };
  }
  if (metricKey === 'heardToEndRate') {
    return {
      value: row.heardToEnd,
      translationKey: 'heardToEndResultCount' as const,
    };
  }
  if (metricKey === 'dtmfRate') {
    return {
      value: row.dtmf,
      translationKey: 'dtmfResultCount' as const,
    };
  }
  return {
    value: row.answered,
    translationKey: 'answeredResultCount' as const,
  };
}

function stripStateCodeFromLabel(label: string, code: string) {
  const cleanLabel = label.trim();
  if (!code) {
    return cleanLabel;
  }
  return cleanLabel.replace(new RegExp(`\\s*\\(${code}\\)\\s*$`, 'i'), '').trim();
}

function formatTerritoryStateCodeLabel(codeValue: string, labelValue: string) {
  const code = codeValue.trim();
  const name = stripStateCodeFromLabel(labelValue, code);
  if (!code) {
    return name;
  }
  if (!name || name.toLowerCase() === code.toLowerCase()) {
    return code;
  }
  return `${code} - ${name}`;
}

function formatTerritoryStateLabel(row: DashboardCockpitTerritoryPattern) {
  return formatTerritoryStateCodeLabel(row.code, row.label);
}

function sortTerritoryRows(
  rows: DashboardCockpitTerritoryPattern[],
  metricKey: TerritoryMetricKey,
  direction: 'asc' | 'desc' = 'desc',
) {
  const multiplier = direction === 'asc' ? 1 : -1;
  return [...rows].sort((first, second) => {
    const sampleCompare = Number(!first.sampleEligible) - Number(!second.sampleEligible);
    if (sampleCompare !== 0) {
      return sampleCompare;
    }
    const metricCompare = getTerritoryMetricValue(first, metricKey) - getTerritoryMetricValue(second, metricKey);
    if (metricCompare !== 0) {
      return multiplier * metricCompare;
    }
    const attemptsCompare = first.attempts - second.attempts;
    if (attemptsCompare !== 0) {
      return -attemptsCompare;
    }
    return first.label.localeCompare(second.label);
  });
}

function TerritoryRankingRow({
  row,
  label,
  parentLabel,
  metricValue,
  metricKey,
  metricAccent,
  drilldownLabel,
  onDrillDown,
}: {
  row: DashboardCockpitTerritoryPattern;
  label: string;
  parentLabel?: string | null;
  metricValue: number;
  metricKey: TerritoryMetricKey;
  metricAccent: string;
  drilldownLabel: string;
  onDrillDown?: () => void;
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { formatNumber, formatPercent } = useNumberFormat();
  const canDrillDown = Boolean(onDrillDown);
  const metricContext = getTerritoryMetricContext(row, metricKey);
  const metricContextUnitLabel = metricContext.translationKey === 'eligibleAnswersCount'
    ? t('pages:dashboard.patterns.territory.eligibleAnswersVolumeLabel')
    : t('pages:dashboard.patterns.territory.attemptsVolumeLabel');
  const metricResult = getTerritoryMetricResult(row, metricKey);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!canDrillDown || !onDrillDown) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onDrillDown();
    }
  };

  return (
    <Box
      role={canDrillDown ? 'button' : undefined}
      tabIndex={canDrillDown ? 0 : undefined}
      onClick={onDrillDown}
      onKeyDown={handleKeyDown}
      sx={{
        p: 1.8,
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.text.secondary, 0.18)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.72),
        cursor: canDrillDown ? 'pointer' : 'default',
        transition: theme.transitions.create(['border-color', 'background-color', 'transform'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': canDrillDown
          ? {
              borderColor: alpha(metricAccent, 0.42),
              backgroundColor: alpha(metricAccent, 0.08),
              transform: 'translateY(-1px)',
            }
          : undefined,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Typography
            title={label}
            noWrap
            sx={{
              fontFamily: DISPLAY_FONT,
              fontSize: '1.5rem',
              lineHeight: 0.92,
              maxWidth: '100%',
            }}
          >
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mt: 0.55 }}>
            {formatNumber(metricContext.value)} {metricContextUnitLabel}
          </Typography>
        </Box>
        <Stack alignItems="flex-end" spacing={0.35} sx={{ minWidth: 78, flex: '0 0 auto', textAlign: 'right' }}>
          <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.65rem', lineHeight: 0.9, color: metricAccent }}>
            {formatPercent(metricValue)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {formatNumber(metricResult.value)}
          </Typography>
        </Stack>
      </Stack>

      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, metricValue))}
        sx={{
          mt: 1.4,
          height: 7,
          borderRadius: 999,
          bgcolor: alpha(metricAccent, 0.12),
          '& .MuiLinearProgress-bar': {
            borderRadius: 999,
            bgcolor: metricAccent,
          },
        }}
      />

      {parentLabel || !row.sampleEligible || canDrillDown ? (
        <Stack direction="row" gap={1} sx={{ mt: 1.2, flexWrap: 'wrap', alignItems: 'center' }}>
          {parentLabel ? (
            <Typography
              title={parentLabel}
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ minWidth: 0, maxWidth: canDrillDown ? '62%' : '100%' }}
            >
              {parentLabel}
            </Typography>
          ) : null}
          {!row.sampleEligible ? (
            <Chip
              size="small"
              label={t('pages:dashboard.patterns.lowSample')}
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.18),
                color: theme.palette.warning.main,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.28)}`,
              }}
            />
          ) : null}
          {canDrillDown ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {drilldownLabel}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
    </Box>
  );
}

function TerritoryPerformancePanel({
  territory,
  product,
  aiAgentSeries = [],
}: {
  territory: DashboardCockpitTerritoryPatterns;
  product: TerritoryProduct;
  aiAgentSeries?: DashboardCockpitAiAgentPatternSeries[];
}) {
  const theme = useTheme();
  const { t } = useTranslation(['pages']);
  const { formatNumber, formatPercent } = useNumberFormat();
  const [level, setLevel] = useState<DashboardCockpitTerritoryLevel>('region');
  const [metricKey, setMetricKey] = useState<TerritoryMetricKey>(
    product === 'ai' ? 'conversionRate' : 'heardToEndRate',
  );
  const [selectedRegionCode, setSelectedRegionCode] = useState<string | null>(null);
  const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);
  const [hiddenConversionAgentIds, setHiddenConversionAgentIds] = useState<Set<string>>(() => new Set());
  const [conversionMenuAnchorEl, setConversionMenuAnchorEl] = useState<HTMLElement | null>(null);

  const metricOptions = useMemo(() => {
    if (product === 'ai') {
      return [
        {
          key: 'answerRate' as const,
          label: t('pages:dashboard.patterns.answerRate'),
          accent: theme.palette.info.main,
          icon: <PhoneInTalk fontSize="small" />,
        },
        {
          key: 'conversionRate' as const,
          label: t('pages:dashboard.patterns.conversionRate'),
          accent: theme.palette.primary.main,
          icon: <CheckCircle fontSize="small" />,
        },
      ];
    }
    return [
      {
        key: 'answerRate' as const,
        label: t('pages:dashboard.patterns.answerRate'),
        accent: theme.palette.info.main,
        icon: <PhoneInTalk fontSize="small" />,
      },
      {
        key: 'heardToEndRate' as const,
        label: t('pages:dashboard.patterns.heardToEndRate'),
        accent: theme.palette.warning.main,
        icon: <GraphicEq fontSize="small" />,
      },
      {
        key: 'dtmfRate' as const,
        label: t('pages:dashboard.patterns.dtmfRate'),
        accent: theme.palette.primary.main,
        icon: <CheckCircle fontSize="small" />,
      },
    ];
  }, [product, t, theme.palette.info.main, theme.palette.primary.main, theme.palette.warning.main]);

  const hasConversionAgentMenu = product === 'ai' && aiAgentSeries.length > 1;
  const visibleConversionAgentSeries = useMemo(
    () => (
      hasConversionAgentMenu
        ? aiAgentSeries.filter((series) => !hiddenConversionAgentIds.has(series.id))
        : []
    ),
    [aiAgentSeries, hasConversionAgentMenu, hiddenConversionAgentIds],
  );
  const isConversionMenuOpen = Boolean(conversionMenuAnchorEl);
  const activeTerritory = useMemo(
    () => (
      hasConversionAgentMenu && metricKey === 'conversionRate'
        ? aggregateAiAgentTerritorySeries(visibleConversionAgentSeries)
        : territory
    ),
    [hasConversionAgentMenu, metricKey, territory, visibleConversionAgentSeries],
  );
  const activeMetric = metricOptions.find((option) => option.key === metricKey) ?? metricOptions[0];
  const activeMetricContextLabel = metricKey === 'conversionRate'
    ? t('pages:dashboard.patterns.territory.eligibleAnswersVolumeLabel')
    : t('pages:dashboard.patterns.territory.attemptsVolumeLabel');
  const stateByCode = useMemo(
    () => new Map((activeTerritory.states ?? []).map((row) => [row.code, row])),
    [activeTerritory.states],
  );
  const statesInSelectedRegion = useMemo(
    () => new Set((activeTerritory.states ?? [])
      .filter((row) => !selectedRegionCode || row.parentCode === selectedRegionCode)
      .map((row) => row.code)),
    [activeTerritory.states, selectedRegionCode],
  );
  const rawRows = useMemo(
    () => activeTerritory[TERRITORY_LEVEL_COLLECTIONS[level]] ?? [],
    [activeTerritory, level],
  );
  const visibleRows = useMemo(() => {
    if (level === 'state' && selectedRegionCode) {
      return rawRows.filter((row) => row.parentCode === selectedRegionCode);
    }
    if (level === 'ddd' && selectedStateCode) {
      return rawRows.filter((row) => row.parentCode === selectedStateCode);
    }
    if (level === 'ddd' && selectedRegionCode) {
      return rawRows.filter((row) => row.parentCode && statesInSelectedRegion.has(row.parentCode));
    }
    return rawRows;
  }, [level, rawRows, selectedRegionCode, selectedStateCode, statesInSelectedRegion]);
  const rankedRows = useMemo(() => sortTerritoryRows(visibleRows, metricKey), [metricKey, visibleRows]);
  const weakestDddRows = useMemo(
    () => (level === 'ddd' ? sortTerritoryRows(visibleRows.filter((row) => row.sampleEligible), metricKey, 'asc').slice(0, 6) : []),
    [level, metricKey, visibleRows],
  );
  const weakestDddContextTotal = useMemo(
    () => weakestDddRows.reduce(
      (total, row) => total + getTerritoryMetricContext(row, metricKey).value,
      0,
    ),
    [metricKey, weakestDddRows],
  );
  const dddRowsInCurrentScope = useMemo(() => {
    const rows = activeTerritory.ddds ?? [];
    if (selectedStateCode) {
      return rows.filter((row) => row.parentCode === selectedStateCode);
    }
    if (selectedRegionCode) {
      return rows.filter((row) => row.parentCode && statesInSelectedRegion.has(row.parentCode));
    }
    return rows;
  }, [activeTerritory.ddds, selectedRegionCode, selectedStateCode, statesInSelectedRegion]);
  const bestGeographyRows = useMemo(() => {
    const sampledRows = dddRowsInCurrentScope.filter((row) => row.sampleEligible);
    const rankedCandidates = sampledRows.length
      ? sampledRows
      : dddRowsInCurrentScope.filter((row) => getTerritoryMetricContext(row, metricKey).value > 0);
    return sortTerritoryRows(rankedCandidates, metricKey).slice(0, 3);
  }, [dddRowsInCurrentScope, metricKey]);

  const resolveRegionLabel = useCallback(
    (code: string | null | undefined, fallback?: string | null) => {
      if (!code) {
        return fallback ?? '';
      }
      return t(`pages:dashboard.patterns.territory.regions.${code}`, {
        defaultValue: fallback || code,
      });
    },
    [t],
  );
  const getRowLabel = useCallback(
    (row: DashboardCockpitTerritoryPattern) => {
      if (row.level === 'region') {
        return resolveRegionLabel(row.code, row.label);
      }
      if (row.level === 'state') {
        return formatTerritoryStateLabel(row);
      }
      return row.label;
    },
    [resolveRegionLabel],
  );
  const getParentLabel = useCallback(
    (row: DashboardCockpitTerritoryPattern) => {
      if (row.level === 'state') {
        return resolveRegionLabel(row.parentCode, row.parentLabel);
      }
      if (row.level === 'ddd') {
        const stateRow = row.parentCode ? stateByCode.get(row.parentCode) : undefined;
        const regionLabel = resolveRegionLabel(stateRow?.parentCode, stateRow?.parentLabel);
        const stateLabel = formatTerritoryStateCodeLabel(
          stateRow?.code || row.parentCode || '',
          stateRow?.label || row.parentLabel || '',
        );
        return [regionLabel, stateLabel].filter(Boolean).join(' -> ');
      }
      return row.parentLabel;
    },
    [resolveRegionLabel, stateByCode],
  );
  const getDddPathLabel = useCallback(
    (row: DashboardCockpitTerritoryPattern) => {
      const stateRow = row.parentCode ? stateByCode.get(row.parentCode) : undefined;
      const regionLabel = resolveRegionLabel(stateRow?.parentCode, stateRow?.parentLabel);
      const stateLabel = row.parentCode || stateRow?.code || row.parentLabel || '';
      return [regionLabel, stateLabel, getRowLabel(row)].filter(Boolean).join(' -> ');
    },
    [getRowLabel, resolveRegionLabel, stateByCode],
  );
  const selectedScopeLabel = useMemo(() => {
    if (selectedStateCode) {
      const selectedState = activeTerritory.states.find((row) => row.code === selectedStateCode);
      return selectedState ? selectedState.label : selectedStateCode;
    }
    if (selectedRegionCode) {
      const selectedRegion = activeTerritory.regions.find((row) => row.code === selectedRegionCode);
      return resolveRegionLabel(selectedRegionCode, selectedRegion?.label);
    }
    return null;
  }, [activeTerritory.regions, activeTerritory.states, resolveRegionLabel, selectedRegionCode, selectedStateCode]);

  useEffect(() => {
    setHiddenConversionAgentIds((current) => {
      const knownAgentIds = new Set(aiAgentSeries.map((series) => series.id));
      const nextHidden = new Set(
        Array.from(current).filter((agentId) => knownAgentIds.has(agentId)),
      );
      if (nextHidden.size >= aiAgentSeries.length && aiAgentSeries.length > 0) {
        nextHidden.delete(aiAgentSeries[0].id);
      }
      return nextHidden.size === current.size ? current : nextHidden;
    });
  }, [aiAgentSeries]);

  const handleMetricChange = (option: { key: TerritoryMetricKey }, event: MouseEvent<HTMLElement>) => {
    setMetricKey(option.key);
    if (option.key === 'conversionRate' && hasConversionAgentMenu) {
      setConversionMenuAnchorEl(event.currentTarget);
      return;
    }
    setConversionMenuAnchorEl(null);
  };
  const toggleConversionAgentSeries = (agentId: string) => {
    setHiddenConversionAgentIds((current) => {
      const nextHidden = new Set(current);
      if (nextHidden.has(agentId)) {
        nextHidden.delete(agentId);
      } else {
        nextHidden.add(agentId);
      }
      const nextVisibleAgentCount = aiAgentSeries.filter(
        (series) => !nextHidden.has(series.id),
      ).length;
      return nextVisibleAgentCount > 0 ? nextHidden : current;
    });
  };

  const handleLevelChange = (nextLevel: DashboardCockpitTerritoryLevel) => {
    setLevel(nextLevel);
    if (nextLevel === 'region') {
      setSelectedRegionCode(null);
      setSelectedStateCode(null);
    }
    if (nextLevel === 'state') {
      setSelectedStateCode(null);
    }
  };
  const clearScope = () => {
    setSelectedRegionCode(null);
    setSelectedStateCode(null);
    setLevel('region');
  };
  const handleRowDrillDown = (row: DashboardCockpitTerritoryPattern) => {
    if (row.level === 'region') {
      setSelectedRegionCode(row.code);
      setSelectedStateCode(null);
      setLevel('state');
      return;
    }
    if (row.level === 'state') {
      setSelectedStateCode(row.code);
      setLevel('ddd');
    }
  };
  const renderRows = (rows: DashboardCockpitTerritoryPattern[]) => (
    <Box
      sx={{
        display: 'grid',
        gap: 1.2,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' },
      }}
    >
      {rows.map((row) => (
        <TerritoryRankingRow
          key={`${row.level}-${row.code}-${row.parentCode ?? 'root'}`}
          row={row}
          label={getRowLabel(row)}
          parentLabel={getParentLabel(row)}
          metricValue={getTerritoryMetricValue(row, metricKey)}
          metricKey={metricKey}
          metricAccent={activeMetric.accent}
          drilldownLabel={t('pages:dashboard.patterns.territory.drilldownHint')}
          onDrillDown={row.level === 'ddd' ? undefined : () => handleRowDrillDown(row)}
        />
      ))}
    </Box>
  );

  return (
    <InsightPanel
      title={t(`pages:dashboard.patterns.territory.${product}.title`)}
      subtitle={t(`pages:dashboard.patterns.territory.${product}.subtitle`)}
      titleIcon={<Public />}
      accent={activeMetric.accent}
      actions={(
        <Stack gap={1.25} sx={{ alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {metricOptions.map((option) => {
              const opensAgentMenu = option.key === 'conversionRate' && hasConversionAgentMenu;
              return (
                <Chip
                  key={option.key}
                  size="small"
                  icon={option.icon}
                  label={opensAgentMenu ? buildDropdownMetricLabel(option.label) : option.label}
                  aria-haspopup={opensAgentMenu ? 'menu' : undefined}
                  aria-expanded={opensAgentMenu ? isConversionMenuOpen : undefined}
                  onClick={(event) => handleMetricChange(option, event)}
                  sx={buildMetricChipSx(option.accent, option.key === metricKey)}
                />
              );
            })}
          </Stack>
          {hasConversionAgentMenu ? (
            <Menu
              anchorEl={conversionMenuAnchorEl}
              open={isConversionMenuOpen}
              onClose={() => setConversionMenuAnchorEl(null)}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.75,
                    minWidth: 220,
                    maxWidth: 340,
                  },
                },
              }}
            >
              {aiAgentSeries.map((series, index) => {
                const isVisible = !hiddenConversionAgentIds.has(series.id);
                const color = AI_AGENT_PERFORMANCE_SERIES_COLORS[
                  index % AI_AGENT_PERFORMANCE_SERIES_COLORS.length
                ];
                return (
                  <MenuItem
                    key={series.id}
                    dense
                    onClick={() => toggleConversionAgentSeries(series.id)}
                  >
                    <Checkbox
                      size="small"
                      checked={isVisible}
                      sx={{
                        color,
                        '&.Mui-checked': {
                          color,
                        },
                      }}
                    />
                    <ListItemText
                      primary={series.label}
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                  </MenuItem>
                );
              })}
            </Menu>
          ) : null}
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            {(['region', 'state', 'ddd'] as DashboardCockpitTerritoryLevel[]).map((option) => (
              <Chip
                key={option}
                size="small"
                label={t(`pages:dashboard.patterns.territory.levels.${option}`)}
                onClick={() => handleLevelChange(option)}
                sx={{
                  bgcolor: option === level ? alpha(activeMetric.accent, 0.16) : alpha(theme.palette.text.secondary, 0.08),
                  color: option === level ? activeMetric.accent : 'text.secondary',
                  border: `1px solid ${option === level ? alpha(activeMetric.accent, 0.42) : alpha(theme.palette.text.secondary, 0.14)}`,
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
    >
      <Stack spacing={1.6}>
        <Typography variant="caption" color="text.secondary">
          {t('pages:dashboard.patterns.territory.barHint', {
            metric: activeMetric.label,
            volumeLabel: activeMetricContextLabel,
          })}
        </Typography>

        {selectedScopeLabel ? (
          <Chip
            size="small"
            label={`${t('pages:dashboard.patterns.territory.selectedScope')}: ${selectedScopeLabel}`}
            onDelete={clearScope}
            sx={{
              alignSelf: 'flex-start',
              bgcolor: alpha(activeMetric.accent, 0.12),
              color: activeMetric.accent,
              border: `1px solid ${alpha(activeMetric.accent, 0.26)}`,
            }}
          />
        ) : null}

        {rankedRows.length ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' },
              alignItems: 'start',
            }}
          >
            <Stack spacing={1.6} sx={{ minWidth: 0 }}>
              {renderRows(level === 'ddd' ? rankedRows.slice(0, 12) : rankedRows)}
              {level === 'ddd' && weakestDddRows.length ? (
                <Box
                  sx={{
                    mt: 0.5,
                    p: 1.6,
                    borderRadius: '18px',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
                    backgroundColor: alpha(theme.palette.warning.main, 0.06),
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {t('pages:dashboard.patterns.territory.weakDdds')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:dashboard.patterns.territory.weakDddsTotal', {
                        volumeLabel: activeMetricContextLabel,
                        value: formatNumber(weakestDddContextTotal),
                      })}
                    </Typography>
                  </Stack>
                  {renderRows(weakestDddRows)}
                </Box>
              ) : null}
            </Stack>

            {bestGeographyRows.length ? (
              <Box
                sx={{
                  p: 1.4,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(activeMetric.accent, 0.18)}`,
                  backgroundColor: alpha(activeMetric.accent, 0.055),
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('pages:dashboard.patterns.territory.bestRegions')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                  {t('pages:dashboard.patterns.territory.bestRegionsHint')}
                </Typography>
                <Stack spacing={0.9} sx={{ mt: 1.1 }}>
                  {bestGeographyRows.map((row, index) => {
                    const metricResult = getTerritoryMetricResult(row, metricKey);
                    const metricContext = getTerritoryMetricContext(row, metricKey);
                    const metricResultLabel = metricResult.translationKey === 'convertedResultCount'
                      ? t('pages:dashboard.patterns.territory.convertedResultCount', { value: formatNumber(metricResult.value) })
                      : metricResult.translationKey === 'heardToEndResultCount'
                        ? t('pages:dashboard.patterns.territory.heardToEndResultCount', { value: formatNumber(metricResult.value) })
                        : metricResult.translationKey === 'dtmfResultCount'
                          ? t('pages:dashboard.patterns.territory.dtmfResultCount', { value: formatNumber(metricResult.value) })
                          : t('pages:dashboard.patterns.territory.answeredResultCount', { value: formatNumber(metricResult.value) });
                    const metricContextLabel = metricContext.translationKey === 'eligibleAnswersCount'
                      ? t('pages:dashboard.patterns.territory.eligibleAnswersCount', { value: formatNumber(metricContext.value) })
                      : t('pages:dashboard.patterns.territory.attemptsCount', { value: formatNumber(metricContext.value) });

                    return (
                      <Box
                        key={`best-${row.code}-${row.parentCode ?? 'root'}`}
                        sx={{
                          p: 1,
                          borderRadius: '10px',
                          border: `1px solid ${alpha(activeMetric.accent, 0.16)}`,
                          backgroundColor: alpha(theme.palette.background.paper, 0.54),
                        }}
                      >
                        <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                          {index + 1}. {getDddPathLabel(row)}
                        </Typography>
                        <Stack direction="row" spacing={1.2} alignItems="baseline" sx={{ mt: 0.7 }}>
                          <Typography
                            sx={{
                              color: activeMetric.accent,
                              fontWeight: 900,
                              fontFamily: DISPLAY_FONT,
                              fontSize: '1.35rem',
                              lineHeight: 1,
                            }}
                          >
                            {formatPercent(getTerritoryMetricValue(row, metricKey))}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {metricResultLabel}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.35 }}>
                          {metricContextLabel}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ) : null}
          </Box>
        ) : (
          <EmptyPanelState label={t('pages:dashboard.patterns.territory.empty')} />
        )}
      </Stack>
    </InsightPanel>
  );
}

function ProductSubsectionTitle({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.2}>
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            letterSpacing: '0.12em',
            fontWeight: 700,
            minWidth: 0,
          }}
        >
          {title}
        </Typography>
        {action ? (
          <Box sx={{ flexShrink: 1, minWidth: 0, maxWidth: { xs: 180, sm: 220 } }}>
            {action}
          </Box>
        ) : null}
      </Stack>
      {subtitle ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.7 }}>
          {subtitle}
        </Typography>
      ) : null}
    </Box>
  );
}

function TodayAiAgentScopeSelector({
  agents,
  selectedAgentId,
  onSelectAgent,
  t,
  formatNumber,
}: {
  agents: DashboardCockpitTodayAiAgentSummary[];
  selectedAgentId: string;
  onSelectAgent: (agentId: string) => void;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);
  const selectedAgent = agents.find((agent) => agent.id === selectedAgentId) ?? null;
  const label = selectedAgent?.name ?? t('pages:dashboard.todayAi.agentScope.allAgents');

  return (
    <>
      <Chip
        size="small"
        component="button"
        clickable
        icon={<Psychology />}
        label={buildDropdownMetricLabel(label)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          ...buildMetricChipSx(theme.palette.success.main, true),
          maxWidth: '100%',
          '& .MuiChip-label': {
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              minWidth: 230,
              maxWidth: 340,
            },
          },
        }}
      >
        <MenuItem
          dense
          selected={selectedAgentId === DASHBOARD_TODAY_AI_AGENT_ALL}
          onClick={() => {
            onSelectAgent(DASHBOARD_TODAY_AI_AGENT_ALL);
            setAnchorEl(null);
          }}
        >
          <ListItemText
            primary={t('pages:dashboard.todayAi.agentScope.allAgents')}
            primaryTypographyProps={{ variant: 'body2', noWrap: true }}
          />
        </MenuItem>
        <Divider />
        {agents.map((agent) => (
          <MenuItem
            key={agent.id}
            dense
            selected={agent.id === selectedAgentId}
            onClick={() => {
              onSelectAgent(agent.id);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary={agent.name}
              secondary={agent.isHydrated ? t('pages:dashboard.todayAi.agentScope.agentStats', {
                calls: formatNumber(agent.attemptedCalls),
                conversions: formatNumber(agent.convertedCalls),
              }) : null}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function formatTodayAiComparisonValue(
  row: TodayAiComparisonRow,
  t: TFunction,
  formatNumber: (value: number | null | undefined) => string,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  if (row.isRate) {
    return formatPercent(row.currentValue);
  }
  if (row.isMinutes) {
    return t('pages:dashboard.todayAi.units.minutes', {
      value: formatDecimal(row.currentValue, 1),
    });
  }
  return formatNumber(row.currentValue);
}

function formatTodayAiAverageValue(
  row: TodayAiComparisonRow,
  t: TFunction,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  if (row.isRate) {
    return formatPercent(row.averageValue, true);
  }
  const averageLabel = `~${formatDecimal(row.averageValue, row.averageValue >= 10 ? 1 : 2)}`;
  if (row.isMinutes) {
    return t('pages:dashboard.todayAi.units.minutes', { value: averageLabel });
  }
  return averageLabel;
}

function getTodayAiTrendAccent(theme: Theme, row: TodayAiComparisonRow) {
  if (row.trend === 'steady') {
    return theme.palette.text.secondary;
  }
  if (row.key === 'aiConsumedMinutes') {
    return row.trend === 'above' ? theme.palette.warning.main : theme.palette.success.main;
  }
  return row.trend === 'above' ? theme.palette.success.main : theme.palette.error.main;
}

function getTodayAiTrendIcon(row: TodayAiComparisonRow) {
  if (row.trend === 'above') {
    return <TrendingUp sx={{ fontSize: 16 }} />;
  }
  if (row.trend === 'below') {
    return <TrendingDown sx={{ fontSize: 16 }} />;
  }
  return <TrendingFlat sx={{ fontSize: 16 }} />;
}

function formatTodayAiTrendLabel(
  row: TodayAiComparisonRow,
  t: TFunction,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  if (row.trend === 'steady') {
    return t('pages:dashboard.todayAi.comparison.steady');
  }
  if (row.isRate) {
    const key = row.trend === 'above'
      ? 'pages:dashboard.todayAi.comparison.pointsAbove'
      : 'pages:dashboard.todayAi.comparison.pointsBelow';
    return t(key, { value: formatDecimal(Math.abs(row.deltaValue), 2) });
  }
  const key = row.trend === 'above'
    ? 'pages:dashboard.todayAi.comparison.above'
    : 'pages:dashboard.todayAi.comparison.below';
  return t(key, { value: formatPercent(Math.abs(row.deltaPercent ?? row.deltaValue)) });
}

function TodayAiPanelHeader({
  title,
  helper,
}: {
  title: string;
  helper?: string;
}) {
  return (
    <Box sx={{ mb: 1.1 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          fontWeight: 800,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function TodayAiMetricCard({
  label,
  value,
  accent,
  helper,
  highlighted = false,
}: {
  label: string;
  value: string;
  accent: string;
  helper?: string;
  highlighted?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 1.55,
        minWidth: 0,
        minHeight: 92,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(accent, highlighted ? 0.32 : 0.18)}`,
        backgroundColor: alpha(accent, highlighted ? 0.12 : 0.07),
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography
        sx={{
          mt: 0.55,
          color: highlighted ? accent : 'text.primary',
          fontFamily: DISPLAY_FONT,
          fontSize: highlighted ? '1.95rem' : '1.72rem',
          lineHeight: 0.9,
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function TodayAiRecentComparisonPanel({
  rows,
  loading,
  error,
  windowDays,
  t,
  formatNumber,
  formatDecimal,
  formatPercent,
}: {
  rows: TodayAiComparisonRow[];
  loading: boolean;
  error: boolean;
  windowDays: number;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  formatDecimal: (value: number | null | undefined, decimals?: number) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.06),
      }}
    >
      <TodayAiPanelHeader
        title={t('pages:dashboard.todayAi.sections.recentComparison')}
        helper={t('pages:dashboard.todayAi.comparison.reference', {
          days: formatNumber(windowDays),
        })}
      />

      {loading && !rows.length ? (
        <Stack gap={0.8}>
          <Skeleton variant="rounded" height={42} />
          <Skeleton variant="rounded" height={42} />
        </Stack>
      ) : null}

      {error && !rows.length ? (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayAi.comparison.error')}
        </Typography>
      ) : null}

      {!loading && !error && !rows.length ? (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayAi.comparison.empty')}
        </Typography>
      ) : null}

      {rows.length ? (
        <Stack gap={0.85}>
          {rows.map((row) => {
            const accent = getTodayAiTrendAccent(theme, row);
            return (
              <Box
                key={row.key}
                sx={{
                  p: 1.05,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(accent, 0.16)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.58),
                }}
              >
                <Stack direction="row" justifyContent="space-between" gap={1.2} alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {row.key === 'aiConsumedMinutes'
                        ? t('pages:dashboard.todayMetrics.aiMinutesConsumed')
                        : t(`pages:dashboard.todayMetrics.${row.key}`)}
                    </Typography>
                    <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.48rem', lineHeight: 0.95 }}>
                      {formatTodayAiComparisonValue(row, t, formatNumber, formatDecimal, formatPercent)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 104, textAlign: 'right' }}>
                    <Stack direction="row" gap={0.4} alignItems="center" justifyContent="flex-end" sx={{ color: accent }}>
                      {getTodayAiTrendIcon(row)}
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>
                        {formatTodayAiTrendLabel(row, t, formatDecimal, formatPercent)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:dashboard.todayAi.comparison.averagePrefix')}{' '}
                      {formatTodayAiAverageValue(row, t, formatDecimal, formatPercent)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
}

type TodayHumanAttendanceComparisonRow = {
  key: string;
  label: string;
  currentValue: number | null;
  averageValue: number | null;
  positiveDirection: 'higher' | 'lower';
  valueType: 'number' | 'percent' | 'duration' | 'decimal';
};

function formatTodayHumanAttendanceComparisonValue(
  row: TodayHumanAttendanceComparisonRow,
  {
    formatNumber,
    formatDecimal,
    formatDuration,
    formatPercent,
    notAvailableLabel,
    isAverage = false,
  }: {
    formatNumber: (value: number | null | undefined) => string;
    formatDecimal: (value: number | null | undefined, decimals?: number) => string;
    formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string;
    formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
    notAvailableLabel: string;
    isAverage?: boolean;
  },
) {
  if (row.currentValue === null && !isAverage) {
    return notAvailableLabel;
  }
  if (row.averageValue === null && isAverage) {
    return notAvailableLabel;
  }
  const value = isAverage ? row.averageValue : row.currentValue;
  if (row.valueType === 'percent') {
    return formatPercent(value, isAverage);
  }
  if (row.valueType === 'duration') {
    return formatDuration(value, isAverage);
  }
  if (row.valueType === 'decimal') {
    return `${isAverage ? '~' : ''}${formatDecimal(value, 1)}`;
  }
  return isAverage ? `~${formatDecimal(value, 1)}` : formatNumber(value);
}

function getTodayHumanAttendanceComparisonState(row: TodayHumanAttendanceComparisonRow) {
  if (
    row.currentValue === null
    || row.averageValue === null
    || row.averageValue === 0
  ) {
    return 'steady';
  }
  const delta = row.currentValue - row.averageValue;
  const deltaPercent = (delta / Math.abs(row.averageValue)) * 100;
  if (Math.abs(deltaPercent) < 5) {
    return 'steady';
  }
  const isAbove = delta > 0;
  const improved = row.positiveDirection === 'higher' ? isAbove : !isAbove;
  return improved ? 'improved' : 'degraded';
}

function TodayHumanAttendanceComparisonPanel({
  humanAttendance,
  loading,
  error,
  windowDays,
  t,
  formatNumber,
  formatDecimal,
  formatDuration,
  formatPercent,
}: {
  humanAttendance: DashboardCockpitTodayHumanAttendance;
  loading: boolean;
  error: boolean;
  windowDays: number;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  formatDecimal: (value: number | null | undefined, decimals?: number) => string;
  formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
}) {
  const theme = useTheme();
  const comparison = humanAttendance.recentComparison;
  if (!comparison?.hasSufficientSample) {
    if (!loading && !error) {
      return null;
    }

    return (
      <Box
        sx={{
          p: 1.7,
          contain: 'layout paint',
          borderRadius: '18px',
          border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
          backgroundColor: alpha(theme.palette.info.main, 0.055),
        }}
      >
        <TodayAiPanelHeader
          title={t('pages:dashboard.todayHumanAttendance.comparison.title')}
          helper={t('pages:dashboard.todayHumanAttendance.comparison.reference', {
            days: formatNumber(windowDays),
          })}
        />
        {loading ? (
          <Stack gap={0.8}>
            <Skeleton variant="rounded" height={42} />
            <Skeleton variant="rounded" height={42} />
          </Stack>
        ) : null}
        {error ? (
          <Typography variant="body2" color="text.secondary">
            {t('pages:dashboard.todayHumanAttendance.comparison.error')}
          </Typography>
        ) : null}
      </Box>
    );
  }

  const notAvailableLabel = t('pages:dashboard.notAvailable');
  const rows: TodayHumanAttendanceComparisonRow[] = [
    {
      key: 'humanAnsweredCalls',
      label: t('pages:dashboard.todayMetrics.humanAnsweredCalls'),
      currentValue: humanAttendance.humanAnsweredCalls,
      averageValue: comparison.humanAnsweredCallsAverage,
      positiveDirection: 'higher',
      valueType: 'number',
    },
    {
      key: 'humanAnswerRate',
      label: t('pages:dashboard.todayMetrics.humanAnswerRate'),
      currentValue: humanAttendance.humanAnswerRate,
      averageValue: comparison.humanAnswerRate,
      positiveDirection: 'higher',
      valueType: 'percent',
    },
    {
      key: 'averageWaitSeconds',
      label: t('pages:dashboard.todayMetrics.averageWait'),
      currentValue: humanAttendance.averageWaitSeconds,
      averageValue: comparison.averageWaitSeconds,
      positiveDirection: 'lower',
      valueType: 'duration',
    },
    {
      key: 'averageHandleSeconds',
      label: t('pages:dashboard.todayMetrics.averageHandle'),
      currentValue: humanAttendance.averageHandleSeconds,
      averageValue: comparison.averageHandleSeconds,
      positiveDirection: 'lower',
      valueType: 'duration',
    },
  ];
  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.055),
      }}
    >
      <TodayAiPanelHeader
        title={t('pages:dashboard.todayHumanAttendance.comparison.title')}
        helper={t('pages:dashboard.todayHumanAttendance.comparison.reference', {
          days: formatNumber(comparison.windowDays),
        })}
      />
      <Stack gap={0.85}>
        {rows.map((row) => {
          const state = getTodayHumanAttendanceComparisonState(row);
          const accent = state === 'steady'
            ? theme.palette.text.secondary
            : state === 'improved'
              ? theme.palette.success.main
              : theme.palette.error.main;
          return (
            <Box
              key={row.key}
              sx={{
                p: 1.05,
                borderRadius: '14px',
                border: `1px solid ${alpha(accent, 0.16)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.58),
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.2}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {row.label}
                  </Typography>
                  <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.48rem', lineHeight: 0.95 }}>
                    {formatTodayHumanAttendanceComparisonValue(row, {
                      formatNumber,
                      formatDecimal,
                      formatDuration,
                      formatPercent,
                      notAvailableLabel,
                    })}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 106, textAlign: 'right' }}>
                  <Stack direction="row" gap={0.4} alignItems="center" justifyContent="flex-end" sx={{ color: accent }}>
                    {state === 'steady' ? <TrendingFlat sx={{ fontSize: 16 }} /> : null}
                    {state === 'improved' ? <TrendingUp sx={{ fontSize: 16 }} /> : null}
                    {state === 'degraded' ? <TrendingDown sx={{ fontSize: 16 }} /> : null}
                    <Typography variant="caption" sx={{ fontWeight: 900 }}>
                      {t(`pages:dashboard.todayHumanAttendance.comparison.${state}`)}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {t('pages:dashboard.todayAi.comparison.averagePrefix')}{' '}
                    {formatTodayHumanAttendanceComparisonValue(row, {
                      formatNumber,
                      formatDecimal,
                      formatDuration,
                      formatPercent,
                      notAvailableLabel,
                      isAverage: true,
                    })}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function getTodayAiEfficiencyTrendAccent(
  theme: Theme,
  comparison: TodayAiEfficiencyComparisonRow,
) {
  if (comparison.trend === 'steady') {
    return theme.palette.text.secondary;
  }
  if (comparison.trend === 'improved' || comparison.trend === 'below') {
    return theme.palette.success.main;
  }
  if (comparison.trend === 'degraded') {
    return theme.palette.error.main;
  }
  return theme.palette.warning.main;
}

function formatTodayAiEfficiencyTrendLabel(
  comparison: TodayAiEfficiencyComparisonRow,
  t: TFunction,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  if (comparison.trend === 'steady') {
    return t('pages:dashboard.todayAi.efficiencyComparison.steady');
  }
  const value = formatPercent(Math.abs(comparison.deltaPercent));
  if (comparison.trend === 'improved') {
    return t('pages:dashboard.todayAi.efficiencyComparison.improved', { value });
  }
  if (comparison.trend === 'degraded') {
    return t('pages:dashboard.todayAi.efficiencyComparison.degraded', { value });
  }
  if (comparison.trend === 'above') {
    return t('pages:dashboard.todayAi.efficiencyComparison.above', { value });
  }
  return t('pages:dashboard.todayAi.efficiencyComparison.below', { value });
}

function formatTodayAiEfficiencyAverageValue(
  comparison: TodayAiEfficiencyComparisonRow,
  formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
) {
  if (
    comparison.key === 'aiConsumedSeconds'
    || comparison.key === 'aiSecondsPerAnswer'
    || comparison.key === 'aiSecondsPerConversion'
  ) {
    return formatDuration(comparison.averageValue, true);
  }
  return `~${formatDecimal(comparison.averageValue, 1)}`;
}

function TodayAiEfficiencyPanel({
  metrics,
  t,
  formatDuration,
  formatDecimal,
  formatPercent,
  notAvailableLabel,
}: {
  metrics: TodayAiEfficiencyMetrics;
  t: TFunction;
  formatDuration: (seconds: number | null | undefined, isAverage?: boolean) => string;
  formatDecimal: (value: number | null | undefined, decimals?: number) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
  notAvailableLabel: string;
}) {
  const theme = useTheme();
  const comparisonByKey = new Map(metrics.comparisons.map((comparison) => [comparison.key, comparison]));
  const rows = [
    {
      key: 'aiConsumedSeconds',
      label: t('pages:dashboard.todayAi.metrics.aiConsumed'),
      help: t('pages:dashboard.todayAi.metrics.aiConsumedHelp'),
      value: formatDuration(metrics.aiConsumedSeconds),
      comparison: comparisonByKey.get('aiConsumedSeconds') ?? null,
    },
    {
      key: 'aiSecondsPerAnswer',
      label: t('pages:dashboard.todayAi.metrics.aiTimePerAnswer'),
      help: t('pages:dashboard.todayAi.metrics.aiTimePerAnswerHelp'),
      value: formatNullableDuration(metrics.aiSecondsPerAnswer, formatDuration, notAvailableLabel),
      comparison: comparisonByKey.get('aiSecondsPerAnswer') ?? null,
    },
    {
      key: 'aiSecondsPerConversion',
      label: t('pages:dashboard.todayAi.metrics.aiTimePerConversion'),
      help: t('pages:dashboard.todayAi.metrics.aiTimePerConversionHelp'),
      value: formatNullableDuration(metrics.aiSecondsPerConversion, formatDuration, notAvailableLabel),
      comparison: comparisonByKey.get('aiSecondsPerConversion') ?? null,
    },
    {
      key: 'answersPer100AiMinutes',
      label: t('pages:dashboard.todayAi.metrics.answersPer100AiMinutes'),
      help: t('pages:dashboard.todayAi.metrics.answersPer100AiMinutesHelp'),
      value: metrics.answersPer100AiMinutes == null
        ? notAvailableLabel
        : formatDecimal(metrics.answersPer100AiMinutes, 1),
      comparison: comparisonByKey.get('answersPer100AiMinutes') ?? null,
    },
    {
      key: 'conversionsPer100AiMinutes',
      label: t('pages:dashboard.todayAi.metrics.conversionsPer100AiMinutes'),
      help: t('pages:dashboard.todayAi.metrics.conversionsPer100AiMinutesHelp'),
      value: metrics.conversionsPer100AiMinutes == null
        ? notAvailableLabel
        : formatDecimal(metrics.conversionsPer100AiMinutes, 1),
      comparison: comparisonByKey.get('conversionsPer100AiMinutes') ?? null,
    },
  ];

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.success.main, 0.055),
      }}
    >
      <TodayAiPanelHeader title={t('pages:dashboard.todayAi.sections.efficiency')} />
      <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {rows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              p: 1.05,
              minHeight: index === 0 ? 74 : 68,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.success.main, 0.14)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.56),
              gridColumn: index === 0 ? '1 / -1' : undefined,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {row.label}
              </Typography>
              <Tooltip title={row.help} arrow>
                <HelpOutline
                  sx={{
                    color: 'text.secondary',
                    cursor: 'help',
                    flexShrink: 0,
                    fontSize: 14,
                  }}
                />
              </Tooltip>
            </Box>
            <Typography sx={{ mt: 0.4, fontFamily: DISPLAY_FONT, fontSize: '1.42rem', lineHeight: 0.95 }}>
              {row.value}
            </Typography>
            {row.comparison ? (
              <Stack direction="row" gap={0.7} alignItems="center" sx={{ mt: 0.8, flexWrap: 'wrap' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: getTodayAiEfficiencyTrendAccent(theme, row.comparison),
                    fontWeight: 900,
                  }}
                >
                  {formatTodayAiEfficiencyTrendLabel(row.comparison, t, formatPercent)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('pages:dashboard.todayAi.efficiencyComparison.averagePrefix')}{' '}
                  {formatTodayAiEfficiencyAverageValue(row.comparison, formatDuration, formatDecimal)}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TodayAiCostReturnPanel({
  metrics,
  t,
  formatCurrency,
  notAvailableLabel,
}: {
  metrics: TodayAiCostReturnMetrics;
  t: TFunction;
  formatCurrency: (value: number | null | undefined, currency?: string, decimals?: number) => string;
  notAvailableLabel: string;
}) {
  const theme = useTheme();
  const rows = [
    {
      key: 'totalEstimatedCostUsd',
      label: t('pages:dashboard.todayMetrics.estimatedCost'),
      value: formatCurrency(metrics.totalEstimatedCostUsd, 'USD'),
    },
    {
      key: 'estimatedCostPerAnswer',
      label: t('pages:dashboard.todayAi.metrics.estimatedCostPerAnswer'),
      value: metrics.estimatedCostPerAnswer == null
        ? notAvailableLabel
        : formatCurrency(metrics.estimatedCostPerAnswer, 'USD'),
    },
    {
      key: 'estimatedCostPerConversion',
      label: t('pages:dashboard.todayAi.metrics.estimatedCostPerConversion'),
      value: metrics.estimatedCostPerConversion == null
        ? notAvailableLabel
        : formatCurrency(metrics.estimatedCostPerConversion, 'USD'),
    },
  ];

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.065),
      }}
    >
      <TodayAiPanelHeader title={t('pages:dashboard.todayAi.sections.costReturn')} />
      <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        {rows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              p: 1.05,
              borderRadius: '14px',
              border: `1px solid ${alpha(theme.palette.warning.main, 0.16)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.56),
              gridColumn: index === 0 ? '1 / -1' : undefined,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.label}
            </Typography>
            <Typography sx={{ mt: 0.4, fontFamily: DISPLAY_FONT, fontSize: '1.42rem', lineHeight: 0.95 }}>
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function TodayAiEmptyStatePanel({
  dateLabel,
  activeCampaignsCount,
  activeCallsCount,
  lastActivityLabel,
  hasLastActiveDay,
  onOpenLastActiveDay,
  t,
  formatNumber,
}: {
  dateLabel: string;
  activeCampaignsCount: number;
  activeCallsCount: number;
  lastActivityLabel: string | null;
  hasLastActiveDay: boolean;
  onOpenLastActiveDay: () => void;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const diagnosisRows = [
    {
      key: 'activeCampaigns',
      label: t('pages:dashboard.todayAi.empty.activeCampaigns'),
      value: formatNumber(activeCampaignsCount),
      accent: theme.palette.info.main,
    },
    {
      key: 'activeCalls',
      label: t('pages:dashboard.todayAi.empty.activeCalls'),
      value: formatNumber(activeCallsCount),
      accent: theme.palette.success.main,
    },
    {
      key: 'lastActivity',
      label: t('pages:dashboard.todayAi.empty.lastActivity'),
      value: lastActivityLabel ?? t('pages:dashboard.todayAi.empty.noRecentActivity'),
      accent: theme.palette.warning.main,
    },
  ];

  return (
    <Box
      sx={{
        mt: 1.4,
        p: 1.7,
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.075),
      }}
    >
      <Typography sx={{ fontWeight: 900, color: theme.palette.warning.main }}>
        {t('pages:dashboard.todayAi.empty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
        {t('pages:dashboard.todayAi.empty.description', { date: dateLabel })}
      </Typography>

      <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mt: 1.4 }}>
        {diagnosisRows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              p: 1.05,
              borderRadius: '14px',
              border: `1px solid ${alpha(row.accent, 0.16)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.58),
              gridColumn: index === 2 ? '1 / -1' : undefined,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontFamily: DISPLAY_FONT,
                fontSize: index === 2 ? '1.28rem' : '1.5rem',
                lineHeight: 0.98,
                overflowWrap: 'anywhere',
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {hasLastActiveDay ? (
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<QueryStats />}
          onClick={onOpenLastActiveDay}
          sx={{ mt: 1.4 }}
        >
          {t('pages:dashboard.todayAi.empty.viewLastActiveDay')}
        </Button>
      ) : null}
    </Box>
  );
}

function TodayAiLastActiveDayBanner({
  dateLabel,
  onBackToToday,
  t,
}: {
  dateLabel: string;
  onBackToToday: () => void;
  t: TFunction;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.4,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.07),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
            {t('pages:dashboard.todayAi.empty.fallbackEyebrow')}
          </Typography>
          <Typography sx={{ fontWeight: 900 }}>
            {t('pages:dashboard.todayAi.empty.fallbackTitle', { date: dateLabel })}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onBackToToday} sx={{ flexShrink: 0 }}>
          {t('pages:dashboard.todayAi.empty.backToToday')}
        </Button>
      </Stack>
    </Box>
  );
}

function TodayAiSignalChips({
  rows,
  title,
  accentForRow,
  t,
  formatNumber,
  translationPrefix,
}: {
  rows: DashboardCockpitCountRow[];
  title: string;
  accentForRow: (row: DashboardCockpitCountRow) => string;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  translationPrefix: 'businessOutcomes' | 'technicalStatuses';
}) {
  if (!rows.length) {
    return null;
  }

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          fontWeight: 800,
          letterSpacing: '0.06em',
          mb: 0.7,
          textTransform: 'uppercase',
        }}
      >
        {title}
      </Typography>
      <Stack direction="row" gap={0.75} sx={{ flexWrap: 'wrap' }}>
        {rows.map((row) => {
          const accent = accentForRow(row);
          return (
            <Chip
              key={row.key}
              size="small"
              label={`${t(`pages:dashboard.${translationPrefix}.${row.key}`)}: ${formatNumber(row.count)}`}
              sx={{
                maxWidth: '100%',
                bgcolor: alpha(accent, 0.14),
                color: accent,
                fontWeight: 800,
              }}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

function TodayAiFunnelTooltip({
  active,
  payload,
  callsLabel,
  formatNumber,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{
    value?: unknown;
    payload?: {
      label?: unknown;
    };
  }>;
  callsLabel: string;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const firstPayload = payload?.[0];
  if (!active || !firstPayload) {
    return null;
  }
  const tooltipTitle = String(firstPayload.payload?.label ?? '');
  const tooltipValue = (
    typeof firstPayload.value === 'number' || typeof firstPayload.value === 'string'
      ? Number(firstPayload.value)
      : 0
  );

  return (
    <Box
      sx={{
        px: 1.4,
        py: 1,
        borderRadius: '12px',
        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.98),
        boxShadow: `0 12px 30px ${alpha(theme.palette.common.black, 0.24)}`,
      }}
    >
      <Typography sx={{ fontWeight: 900, lineHeight: 1.1 }}>
        {tooltipTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
        {formatNumber(tooltipValue)} {callsLabel}
      </Typography>
    </Box>
  );
}

function TodayAiResultFunnelPanel({
  todayAi,
  t,
  formatNumber,
}: {
  todayAi: DashboardCockpitTodayAiSummary;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const funnelRows = [
    {
      key: 'answeredCalls',
      label: t('pages:dashboard.todayMetrics.answeredCalls'),
      value: todayAi.answeredCalls,
      color: theme.palette.success.main,
    },
    {
      key: 'convertedCalls',
      label: t('pages:dashboard.todayMetrics.convertedCalls'),
      value: todayAi.convertedCalls,
      color: theme.palette.primary.main,
    },
  ];
  const hasFunnelData = funnelRows.some((row) => row.value > 0);

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.055),
      }}
    >
      <TodayAiPanelHeader title={t('pages:dashboard.todayAi.sections.resultFunnel')} />
      <Box
        sx={{
          p: 1.2,
          mb: 1.2,
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          backgroundColor: alpha(theme.palette.info.main, 0.075),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('pages:dashboard.todayMetrics.attemptedCalls')}
        </Typography>
        <Typography sx={{ mt: 0.35, fontFamily: DISPLAY_FONT, fontSize: '1.55rem', lineHeight: 0.95 }}>
          {formatNumber(todayAi.attemptedCalls)}
        </Typography>
      </Box>
      {hasFunnelData ? (
        <Box sx={{ width: '100%', height: 96 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={funnelRows}
              layout="vertical"
              margin={{ top: 2, right: 10, left: 4, bottom: 2 }}
              barCategoryGap={12}
            >
              <XAxis type="number" hide domain={[0, 'dataMax']} />
              <YAxis
                type="category"
                dataKey="label"
                width={92}
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 700 }}
              />
              <RechartsTooltip
                cursor={{ fill: alpha(theme.palette.common.white, 0.05) }}
                content={(props) => (
                  <TodayAiFunnelTooltip
                    active={props.active}
                    payload={props.payload}
                    callsLabel={t('pages:dashboard.todayAi.funnel.calls')}
                    formatNumber={formatNumber}
                  />
                )}
              />
              <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={14}>
                {funnelRows.map((row) => (
                  <Cell key={row.key} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayAi.funnel.empty')}
        </Typography>
      )}

      {todayAi.businessOutcomes.length || todayAi.technicalStatuses.length ? (
        <Stack gap={1.1} sx={{ mt: 1.2 }}>
          <TodayAiSignalChips
            rows={todayAi.businessOutcomes}
            title={t('pages:dashboard.todayAi.funnel.businessSignals')}
            accentForRow={(row) => getBusinessOutcomeAccent(theme, row.key)}
            t={t}
            formatNumber={formatNumber}
            translationPrefix="businessOutcomes"
          />
          <TodayAiSignalChips
            rows={todayAi.technicalStatuses}
            title={t('pages:dashboard.todayAi.funnel.technicalSignals')}
            accentForRow={() => theme.palette.warning.main}
            t={t}
            formatNumber={formatNumber}
            translationPrefix="technicalStatuses"
          />
        </Stack>
      ) : null}
    </Box>
  );
}

function getTodayReverseIvrTrendAccent(theme: Theme, row: TodayReverseIvrComparisonRow) {
  if (row.trend === 'steady') {
    return theme.palette.text.secondary;
  }
  const isPositive = row.lowerIsBetter
    ? row.trend === 'below'
    : row.trend === 'above';
  return isPositive ? theme.palette.success.main : theme.palette.error.main;
}

function getTodayReverseIvrTrendIcon(row: TodayReverseIvrComparisonRow) {
  if (row.trend === 'above') {
    return <TrendingUp sx={{ fontSize: 16 }} />;
  }
  if (row.trend === 'below') {
    return <TrendingDown sx={{ fontSize: 16 }} />;
  }
  return <TrendingFlat sx={{ fontSize: 16 }} />;
}

function formatTodayReverseIvrComparisonValue(
  row: TodayReverseIvrComparisonRow,
  formatNumber: (value: number | null | undefined) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  return row.isRate ? formatPercent(row.currentValue) : formatNumber(row.currentValue);
}

function formatTodayReverseIvrAverageValue(
  row: TodayReverseIvrComparisonRow,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  return row.isRate ? formatPercent(row.averageValue, true) : `~${formatDecimal(row.averageValue, 2)}`;
}

function formatTodayReverseIvrTrendLabel(
  row: TodayReverseIvrComparisonRow,
  t: TFunction,
  formatDecimal: (value: number | null | undefined, decimals?: number) => string,
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string,
) {
  if (row.trend === 'steady') {
    return t('pages:dashboard.todayReverseIvr.comparison.steady');
  }
  if (row.isRate) {
    const key = row.trend === 'above'
      ? 'pages:dashboard.todayReverseIvr.comparison.pointsAbove'
      : 'pages:dashboard.todayReverseIvr.comparison.pointsBelow';
    return t(key, { value: formatDecimal(Math.abs(row.deltaValue), 2) });
  }
  const key = row.trend === 'above'
    ? 'pages:dashboard.todayReverseIvr.comparison.above'
    : 'pages:dashboard.todayReverseIvr.comparison.below';
  return t(key, { value: formatPercent(Math.abs(row.deltaPercent ?? row.deltaValue)) });
}

function TodayReverseIvrRecentComparisonPanel({
  rows,
  loading,
  error,
  windowDays,
  t,
  formatNumber,
  formatDecimal,
  formatPercent,
}: {
  rows: TodayReverseIvrComparisonRow[];
  loading: boolean;
  error: boolean;
  windowDays: number;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  formatDecimal: (value: number | null | undefined, decimals?: number) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
}) {
  const theme = useTheme();
  const visibleRows = rows.filter((row) => (
    row.key === 'attemptedCalls'
    || row.key === 'answeredCalls'
    || row.key === 'heardToEndCalls'
    || row.key === 'dtmfCalls'
    || row.key === 'mappedCalls'
    || row.key === 'timeoutCalls'
  ));

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.055),
      }}
    >
      <TodayAiPanelHeader
        title={t('pages:dashboard.todayReverseIvr.sections.recentComparison')}
        helper={t('pages:dashboard.todayReverseIvr.comparison.reference', {
          days: formatNumber(windowDays),
        })}
      />

      {loading && !visibleRows.length ? (
        <Stack gap={0.8}>
          <Skeleton variant="rounded" height={42} />
          <Skeleton variant="rounded" height={42} />
        </Stack>
      ) : null}

      {error && !visibleRows.length ? (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayReverseIvr.comparison.error')}
        </Typography>
      ) : null}

      {!loading && !error && !visibleRows.length ? (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayReverseIvr.comparison.empty')}
        </Typography>
      ) : null}

      {visibleRows.length ? (
        <Stack gap={0.85}>
          {visibleRows.map((row) => {
            const accent = getTodayReverseIvrTrendAccent(theme, row);
            return (
              <Box
                key={row.key}
                sx={{
                  p: 1.05,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(accent, 0.16)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.58),
                }}
              >
                <Stack direction="row" justifyContent="space-between" gap={1.2} alignItems="flex-start">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t(`pages:dashboard.todayMetrics.${row.key}`)}
                    </Typography>
                    <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.48rem', lineHeight: 0.95 }}>
                      {formatTodayReverseIvrComparisonValue(row, formatNumber, formatPercent)}
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 104, textAlign: 'right' }}>
                    <Stack direction="row" gap={0.4} alignItems="center" justifyContent="flex-end" sx={{ color: accent }}>
                      {getTodayReverseIvrTrendIcon(row)}
                      <Typography variant="caption" sx={{ fontWeight: 800 }}>
                        {formatTodayReverseIvrTrendLabel(row, t, formatDecimal, formatPercent)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t('pages:dashboard.todayReverseIvr.comparison.averagePrefix')}{' '}
                      {formatTodayReverseIvrAverageValue(row, formatDecimal, formatPercent)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Box>
  );
}

function TodayReverseIvrEmptyStatePanel({
  dateLabel,
  activeCampaignsCount,
  activeCallsCount,
  lastActivityLabel,
  hasLastActiveDay,
  onOpenLastActiveDay,
  t,
  formatNumber,
}: {
  dateLabel: string;
  activeCampaignsCount: number;
  activeCallsCount: number;
  lastActivityLabel: string | null;
  hasLastActiveDay: boolean;
  onOpenLastActiveDay: () => void;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const diagnosisRows = [
    {
      key: 'activeCampaigns',
      label: t('pages:dashboard.todayReverseIvr.empty.activeCampaigns'),
      value: formatNumber(activeCampaignsCount),
      accent: theme.palette.info.main,
    },
    {
      key: 'activeCalls',
      label: t('pages:dashboard.todayReverseIvr.empty.activeCalls'),
      value: formatNumber(activeCallsCount),
      accent: theme.palette.success.main,
    },
    {
      key: 'lastActivity',
      label: t('pages:dashboard.todayReverseIvr.empty.lastActivity'),
      value: lastActivityLabel ?? t('pages:dashboard.todayReverseIvr.empty.noRecentActivity'),
      accent: theme.palette.warning.main,
    },
  ];

  return (
    <Box
      sx={{
        mt: 1.4,
        p: 1.7,
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
        backgroundColor: alpha(theme.palette.warning.main, 0.075),
      }}
    >
      <Typography sx={{ fontWeight: 900, color: theme.palette.warning.main }}>
        {t('pages:dashboard.todayReverseIvr.empty.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55 }}>
        {t('pages:dashboard.todayReverseIvr.empty.description', { date: dateLabel })}
      </Typography>

      <Box sx={{ display: 'grid', gap: 0.9, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', mt: 1.4 }}>
        {diagnosisRows.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              p: 1.05,
              borderRadius: '14px',
              border: `1px solid ${alpha(row.accent, 0.16)}`,
              backgroundColor: alpha(theme.palette.background.paper, 0.58),
              gridColumn: index === 2 ? '1 / -1' : undefined,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {row.label}
            </Typography>
            <Typography
              sx={{
                mt: 0.35,
                fontFamily: DISPLAY_FONT,
                fontSize: index === 2 ? '1.28rem' : '1.5rem',
                lineHeight: 0.98,
                overflowWrap: 'anywhere',
              }}
            >
              {row.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {hasLastActiveDay ? (
        <Button
          fullWidth
          size="small"
          variant="outlined"
          startIcon={<QueryStats />}
          onClick={onOpenLastActiveDay}
          sx={{ mt: 1.4 }}
        >
          {t('pages:dashboard.todayReverseIvr.empty.viewLastActiveDay')}
        </Button>
      ) : null}
    </Box>
  );
}

function TodayReverseIvrLastActiveDayBanner({
  dateLabel,
  onBackToToday,
  t,
}: {
  dateLabel: string;
  onBackToToday: () => void;
  t: TFunction;
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 1.4,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.07),
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.2}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }}>
            {t('pages:dashboard.todayReverseIvr.empty.fallbackEyebrow')}
          </Typography>
          <Typography sx={{ fontWeight: 900 }}>
            {t('pages:dashboard.todayReverseIvr.empty.fallbackTitle', { date: dateLabel })}
          </Typography>
        </Box>
        <Button size="small" variant="outlined" onClick={onBackToToday} sx={{ flexShrink: 0 }}>
          {t('pages:dashboard.todayReverseIvr.empty.backToToday')}
        </Button>
      </Stack>
    </Box>
  );
}

function TodayReverseIvrResultFunnelPanel({
  todayReverseIvr,
  t,
  formatNumber,
}: {
  todayReverseIvr: DashboardCockpitTodayReverseIvrSummary;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const rowColors = {
    answeredCalls: theme.palette.info.main,
    heardToEndCalls: theme.palette.warning.main,
    dtmfCalls: theme.palette.primary.main,
    mappedCalls: theme.palette.success.main,
  } as const;
  const funnelRows = buildTodayReverseIvrFunnel(todayReverseIvr).map((row) => ({
    ...row,
    label: t(row.labelKey),
    color: rowColors[row.key],
  }));
  const hasFunnelData = funnelRows.some((row) => row.value > 0);

  return (
    <Box
      sx={{
        p: 1.7,
        contain: 'layout paint',
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.055),
      }}
    >
      <TodayAiPanelHeader title={t('pages:dashboard.todayReverseIvr.sections.resultFunnel')} />
      <Box
        sx={{
          p: 1.2,
          mb: 1.2,
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          backgroundColor: alpha(theme.palette.info.main, 0.075),
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('pages:dashboard.todayMetrics.attemptedCalls')}
        </Typography>
        <Typography sx={{ mt: 0.35, fontFamily: DISPLAY_FONT, fontSize: '1.55rem', lineHeight: 0.95 }}>
          {formatNumber(todayReverseIvr.attemptedCalls)}
        </Typography>
      </Box>
      {hasFunnelData ? (
        <Box sx={{ width: '100%', height: 150 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={funnelRows}
              layout="vertical"
              margin={{ top: 2, right: 10, left: 4, bottom: 2 }}
              barCategoryGap={12}
            >
              <XAxis type="number" hide domain={[0, 'dataMax']} />
              <YAxis
                type="category"
                dataKey="label"
                width={102}
                axisLine={false}
                tickLine={false}
                tick={{ fill: theme.palette.text.secondary, fontSize: 11, fontWeight: 700 }}
              />
              <RechartsTooltip
                cursor={{ fill: alpha(theme.palette.common.white, 0.05) }}
                content={(props) => (
                  <TodayAiFunnelTooltip
                    active={props.active}
                    payload={props.payload}
                    callsLabel={t('pages:dashboard.todayReverseIvr.funnel.calls')}
                    formatNumber={formatNumber}
                  />
                )}
              />
              <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={14}>
                {funnelRows.map((row) => (
                  <Cell key={row.key} fill={row.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('pages:dashboard.todayReverseIvr.funnel.empty')}
        </Typography>
      )}
    </Box>
  );
}

function TodayReverseIvrDigitPanel({
  todayReverseIvr,
  t,
  formatNumber,
}: {
  todayReverseIvr: DashboardCockpitTodayReverseIvrSummary;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const rows = todayReverseIvr.digitDistribution.slice(0, 5);
  if (!rows.length) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.7,
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
        backgroundColor: alpha(theme.palette.success.main, 0.055),
      }}
    >
      <TodayAiPanelHeader
        title={t('pages:dashboard.todayReverseIvr.sections.digitDistribution')}
        helper={t('pages:dashboard.todayReverseIvr.digits.helper')}
      />
      <Stack direction="row" gap={0.75} sx={{ flexWrap: 'wrap' }}>
        {rows.map((row) => (
          <Chip
            key={`${row.digit}-${row.selectedStatus}`}
            size="small"
            label={t('pages:dashboard.todayReverseIvr.digits.chip', {
              digit: row.digit,
              value: formatNumber(row.calls),
            })}
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.14),
              color: theme.palette.success.main,
              fontWeight: 850,
              maxWidth: '100%',
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

function getReverseIvrPerformanceCount(row: DashboardCockpitReverseIvrPerformancePoint, metricKey: ReverseIvrPerformanceMetricKey) {
  if (metricKey === 'answerRate') {
    return row.answeredCalls;
  }
  if (metricKey === 'heardToEndRate') {
    return row.heardToEndCalls;
  }
  if (metricKey === 'dtmfRate') {
    return row.dtmfCalls;
  }
  if (metricKey === 'mappedRate') {
    return row.mappedCalls;
  }
  return row.timeoutCalls;
}

function ReverseIvrAudioScopeSelector({
  audioSeries,
  selectedAudioId,
  onSelect,
  t,
  formatNumber,
  formatPercent,
}: {
  audioSeries: DashboardCockpitReverseIvrAudioSeries[];
  selectedAudioId: string;
  onSelect: (audioId: string) => void;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
}) {
  const theme = useTheme();
  const selectedAudio = audioSeries.find((series) => series.id === selectedAudioId) ?? null;
  if (!audioSeries.length) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.6,
        borderRadius: '18px',
        border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
        backgroundColor: alpha(theme.palette.info.main, 0.055),
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1.2} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {t('pages:dashboard.patterns.reverseIvrAudio.scopeTitle')}
            </Typography>
            <Tooltip title={t('pages:dashboard.patterns.reverseIvrAudio.scopeHelp')} arrow>
              <HelpOutline sx={{ color: 'text.secondary', cursor: 'help', flexShrink: 0, fontSize: 14 }} />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {selectedAudio
              ? t('pages:dashboard.patterns.reverseIvrAudio.selectedHelper', { audio: selectedAudio.label })
              : t('pages:dashboard.patterns.reverseIvrAudio.allHelper')}
          </Typography>
        </Box>
        <Stack direction="row" gap={0.75} sx={{ flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
          <Chip
            size="small"
            clickable
            label={t('pages:dashboard.patterns.reverseIvrAudio.allAudios')}
            icon={<GraphicEq />}
            onClick={() => onSelect('all')}
            sx={buildMetricChipSx(theme.palette.info.main, selectedAudioId === 'all')}
          />
          {audioSeries.map((series) => (
            <Chip
              key={series.id}
              size="small"
              clickable
              title={series.label}
              label={series.label}
              icon={<GraphicEq />}
              onClick={() => onSelect(series.id)}
              sx={{
                ...buildMetricChipSx(theme.palette.warning.main, selectedAudioId === series.id),
                maxWidth: { xs: '100%', md: 240 },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </Stack>
      </Stack>

      {selectedAudio ? (
        <Box
          sx={{
            display: 'grid',
            gap: 0.85,
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            mt: 1.3,
          }}
        >
          {[
            {
              key: 'attempted',
              label: t('pages:dashboard.todayMetrics.attemptedCalls'),
              value: formatNumber(selectedAudio.attemptedCalls),
            },
            {
              key: 'heard',
              label: t('pages:dashboard.todayMetrics.heardToEndRate'),
              value: formatPercent(selectedAudio.heardToEndRate),
            },
            {
              key: 'dtmf',
              label: t('pages:dashboard.todayMetrics.dtmfRate'),
              value: formatPercent(selectedAudio.dtmfRate),
            },
            {
              key: 'mapped',
              label: t('pages:dashboard.todayMetrics.mappedRate'),
              value: formatPercent(selectedAudio.mappedRate),
            },
          ].map((row) => (
            <Box
              key={row.key}
              sx={{
                p: 1,
                borderRadius: '12px',
                border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.55),
              }}
            >
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {row.label}
              </Typography>
              <Typography sx={{ mt: 0.3, fontFamily: DISPLAY_FONT, fontSize: '1.35rem', lineHeight: 0.95 }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function ReverseIvrPerformanceEvolutionPanel({
  points,
  windowDays,
  scopeLabel,
  t,
  formatNumber,
  formatPercent,
  locale,
}: {
  points: DashboardCockpitReverseIvrPerformancePoint[];
  windowDays: number;
  scopeLabel: string;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
  formatPercent: (value: number | null | undefined, isAverage?: boolean) => string;
  locale: string;
}) {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<ReverseIvrPerformanceMetricKey>('heardToEndRate');
  const metricOptions = useMemo(() => [
    {
      key: 'answerRate' as const,
      label: t('pages:dashboard.patterns.answerRate'),
      help: t('pages:dashboard.patterns.reverseIvrPerformance.answerHelp'),
      accent: theme.palette.info.main,
      icon: <PhoneInTalk />,
    },
    {
      key: 'heardToEndRate' as const,
      label: t('pages:dashboard.patterns.heardToEndRate'),
      help: t('pages:dashboard.patterns.reverseIvrPerformance.heardToEndHelp'),
      accent: theme.palette.warning.main,
      icon: <GraphicEq />,
    },
    {
      key: 'dtmfRate' as const,
      label: t('pages:dashboard.patterns.dtmfRate'),
      help: t('pages:dashboard.patterns.reverseIvrPerformance.dtmfHelp'),
      accent: theme.palette.primary.main,
      icon: <CheckCircle />,
    },
    {
      key: 'mappedRate' as const,
      label: t('pages:dashboard.todayMetrics.mappedRate'),
      help: t('pages:dashboard.patterns.reverseIvrPerformance.mappedHelp'),
      accent: theme.palette.success.main,
      icon: <CheckCircle />,
    },
    {
      key: 'timeoutRate' as const,
      label: t('pages:dashboard.todayMetrics.timeoutRate'),
      help: t('pages:dashboard.patterns.reverseIvrPerformance.timeoutHelp'),
      accent: theme.palette.error.main,
      icon: <Block />,
    },
  ], [
    t,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]);
  const activeMetric = metricOptions.find((option) => option.key === selectedMetric) ?? metricOptions[0];
  const metricValues = points.map((point) => (point.attemptedCalls > 0 ? Number(point[selectedMetric] ?? 0) : null));
  const trendValues = useMemo(() => buildLinearTrend(metricValues), [metricValues]);
  const chartData = useMemo(
    () => points.map((point, index) => ({
      date: point.date,
      dateLabel: formatHistoryChartDateLabel(point.date, locale),
      metricValue: metricValues[index],
      trendValue: trendValues[index],
      context: point,
    })),
    [locale, metricValues, points, trendValues],
  );
  const hasData = points.some((point) => point.attemptedCalls > 0);
  const minChartWidth = Math.max(points.length * 34, 760);
  const renderTooltip = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: ReadonlyArray<{
      value?: unknown;
      payload?: {
        context?: DashboardCockpitReverseIvrPerformancePoint;
      };
    }>;
    label?: unknown;
  }) => {
    const context = payload?.[0]?.payload?.context;
    if (!active || !context) {
      return null;
    }
    const resultCount = getReverseIvrPerformanceCount(context, selectedMetric);
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.35,
          borderRadius: 2,
          border: `1px solid ${alpha(activeMetric.accent, 0.24)}`,
          backgroundColor: alpha(theme.palette.background.default, 0.96),
          minWidth: 240,
        }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900 }}>
          {String(label ?? '')} · {activeMetric.label}
        </Typography>
        <Typography sx={{ mt: 0.3, color: activeMetric.accent, fontFamily: DISPLAY_FONT, fontSize: '1.45rem', lineHeight: 0.95 }}>
          {formatPercent(Number(context[selectedMetric] ?? 0))}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
          {t('pages:dashboard.patterns.reverseIvrPerformance.tooltipBase', {
            result: formatNumber(resultCount),
            attempts: formatNumber(context.attemptedCalls),
          })}
        </Typography>
      </Paper>
    );
  };
  const metricActions = (
    <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} flexWrap="wrap" useFlexGap gap={0.75}>
      {metricOptions.map((option) => {
        const isSelected = option.key === selectedMetric;
        return (
          <Tooltip key={option.key} title={option.help} arrow>
            <Chip
              size="small"
              component="button"
              clickable
              label={buildMetricHelpLabel(option.label)}
              icon={option.icon}
              aria-pressed={isSelected}
              onClick={() => setSelectedMetric(option.key)}
              sx={buildMetricChipSx(option.accent, isSelected)}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.reverseIvrPerformance.title')}
      subtitle={t('pages:dashboard.patterns.reverseIvrPerformance.subtitle', {
        days: formatNumber(windowDays),
        scope: scopeLabel,
      })}
      titleIcon={<QueryStats />}
      accent={activeMetric.accent}
      actions={metricActions}
    >
      {!hasData ? (
        <EmptyPanelState label={t('pages:dashboard.patterns.reverseIvrPerformance.empty')} />
      ) : (
        <Box sx={{ width: '100%', minHeight: 300, minWidth: 0 }}>
          <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
            <Box sx={{ width: '100%', minWidth: minChartWidth, height: 290 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={HISTORY_LINE_CHART_MARGIN}>
                  <CartesianGrid strokeDasharray="4 4" stroke={alpha(theme.palette.text.secondary, 0.18)} />
                  <XAxis
                    dataKey="dateLabel"
                    interval={0}
                    padding={HISTORY_LINE_CHART_X_AXIS_PADDING}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickMargin={8}
                    angle={-34}
                    textAnchor="end"
                    height={52}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    domain={[0, (maxValue: number) => Math.max(maxValue, 1)]}
                    stroke={theme.palette.text.secondary}
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => formatPercent(Number(value))}
                    tickLine={false}
                    axisLine={false}
                    width={62}
                  />
                  <RechartsTooltip content={renderTooltip} />
                  <Line
                    type="monotone"
                    dataKey="metricValue"
                    name={activeMetric.label}
                    stroke={activeMetric.accent}
                    strokeWidth={2.8}
                    dot={false}
                    activeDot={{ r: 5 }}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="trendValue"
                    stroke={alpha(activeMetric.accent, 0.58)}
                    strokeWidth={1.8}
                    strokeDasharray="5 5"
                    dot={false}
                    activeDot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        </Box>
      )}
    </InsightPanel>
  );
}

function ReverseIvrDigitDistributionPanel({
  rows,
  scopeLabel,
  t,
  formatNumber,
}: {
  rows: DashboardCockpitTodayReverseIvrSummary['digitDistribution'];
  scopeLabel: string;
  t: TFunction;
  formatNumber: (value: number | null | undefined) => string;
}) {
  const theme = useTheme();
  const visibleRows = rows.slice(0, 8);
  const maxCalls = Math.max(...visibleRows.map((row) => row.calls), 1);

  return (
    <InsightPanel
      title={t('pages:dashboard.patterns.reverseIvrDigits.title')}
      subtitle={t('pages:dashboard.patterns.reverseIvrDigits.subtitle', { scope: scopeLabel })}
      titleIcon={<GraphicEq />}
      accent={theme.palette.success.main}
    >
      {!visibleRows.length ? (
        <EmptyPanelState label={t('pages:dashboard.patterns.reverseIvrDigits.empty')} />
      ) : (
        <Stack gap={1}>
          {visibleRows.map((row) => {
            const progress = (row.calls / maxCalls) * 100;
            return (
              <Box
                key={`${row.digit}-${row.selectedStatus}`}
                sx={{
                  p: 1.25,
                  borderRadius: '14px',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.16)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.58),
                }}
              >
                <Stack direction="row" justifyContent="space-between" gap={2} alignItems="baseline">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900 }} noWrap>
                      {t('pages:dashboard.patterns.digitLabel', { digit: row.digit })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {row.selectedStatus || t('pages:dashboard.patterns.noDigitMapping')}
                    </Typography>
                  </Box>
                  <Typography sx={{ color: theme.palette.success.main, fontFamily: DISPLAY_FONT, fontSize: '1.35rem', lineHeight: 0.95 }}>
                    {formatNumber(row.calls)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    mt: 1,
                    height: 7,
                    borderRadius: 999,
                    bgcolor: alpha(theme.palette.success.main, 0.12),
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 999,
                      bgcolor: theme.palette.success.main,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      )}
    </InsightPanel>
  );
}

function OperationalStatePanel({
  title,
  description,
  tone = 'amber',
}: {
  title: string;
  description: string;
  tone?: PanelTone;
}) {
  const theme = useTheme();
  const palette = getTonePalette(theme, tone);
  return (
    <Box
      sx={{
        p: 2.2,
        borderRadius: '18px',
        border: `1px solid ${palette.border}`,
        backgroundColor: alpha(palette.accent, 0.08),
      }}
    >
      <Typography sx={{ fontWeight: 700, color: palette.text }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.8 }}>
        {description}
      </Typography>
    </Box>
  );
}

function DashboardLoadingState() {
  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Skeleton variant="rounded" height={164} />
      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', xl: 'minmax(300px, 0.62fr) minmax(0, 1.9fr)' },
        }}
      >
        <Skeleton variant="rounded" height={620} />
        <Skeleton variant="rounded" height={620} />
      </Box>
    </Box>
  );
}

export default function DashboardPage() {
  const theme = useTheme();
  const { t } = useTranslation(['pages', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isTodayAiFallbackOpen, setIsTodayAiFallbackOpen] = useState(false);
  const [isTodayReverseIvrFallbackOpen, setIsTodayReverseIvrFallbackOpen] = useState(false);
  const [selectedTodayAiAgentId, setSelectedTodayAiAgentId] = useState(DASHBOARD_TODAY_AI_AGENT_ALL);
  const [todayAiAgentDetailsById, setTodayAiAgentDetailsById] = useState<
    Record<string, DashboardCockpitTodayAiAgentSummary>
  >({});
  const [loadingTodayAiAgentId, setLoadingTodayAiAgentId] = useState<string | null>(null);
  const [todayAiAgentErrorId, setTodayAiAgentErrorId] = useState<string | null>(null);
  const [selectedReverseIvrAudioId, setSelectedReverseIvrAudioId] = useState('all');
  const { currentTenant, isSuperuser } = useAuth();
  const { locale, formatCurrency, formatDecimal, formatDuration, formatNumber, formatPercent } = useNumberFormat();
  const {
    cockpit,
    patterns,
    analyticsSlices,
    todayComparisonStatus,
    isLoading,
    isRefreshing,
    error,
    refresh,
    retryAnalyticsSlice,
  } = useDashboardCockpit(HISTORY_DAYS);

  usePageTitle(t('pages:dashboard.title'));

  const dashboardTabs = useMemo(
    () => [
      {
        id: 'tenant',
        label: t('pages:dashboard.tabs.tenantCockpit'),
        icon: <Apartment fontSize="small" />,
      },
      {
        id: 'system',
        label: t('pages:dashboard.tabs.systemCockpit'),
        icon: <Public fontSize="small" />,
      },
    ],
    [t],
  );
  const productViewTabs = useMemo(
    () => [
      {
        id: DASHBOARD_PRODUCT_VIEW_AI,
        label: t('pages:dashboard.productViews.ai'),
        icon: <Psychology fontSize="small" />,
      },
      {
        id: DASHBOARD_PRODUCT_VIEW_REVERSE_IVR,
        label: t('pages:dashboard.productViews.reverseIvr'),
        icon: <ContactPhone fontSize="small" />,
      },
    ],
    [t],
  );
  const selectedDashboardTab = isSuperuser && searchParams.get('tab') === 'system' ? 'system' : 'tenant';

  const refreshDashboard = useCallback(async () => {
    await refresh();
  }, [refresh]);

  useEffect(() => {
    if (!isSuperuser) {
      return;
    }
    const tab = searchParams.get('tab');
    if (tab === 'tenant' || tab === 'system') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'tenant');
    setSearchParams(next, { replace: true });
  }, [isSuperuser, searchParams, setSearchParams]);

  const weekdayLabelMap = useMemo(
    () => ({
      mon: t('pages:dashboard.patterns.weekdays.mon'),
      tue: t('pages:dashboard.patterns.weekdays.tue'),
      wed: t('pages:dashboard.patterns.weekdays.wed'),
      thu: t('pages:dashboard.patterns.weekdays.thu'),
      fri: t('pages:dashboard.patterns.weekdays.fri'),
      sat: t('pages:dashboard.patterns.weekdays.sat'),
      sun: t('pages:dashboard.patterns.weekdays.sun'),
    }),
    [t],
  );

  const productMix = cockpit?.productMix ?? null;
  const hasAiProduct = Boolean(productMix?.hasAiProduct);
  const hasReverseIvrProduct = Boolean(productMix?.hasReverseIvrProduct);
  const hasMultipleProductViews = hasAiProduct && hasReverseIvrProduct;
  const productViewParam = searchParams.get(DASHBOARD_PRODUCT_VIEW_PARAM);
  const selectedProductViewMode: DashboardProductViewMode = hasMultipleProductViews
    ? (
        productViewParam === DASHBOARD_PRODUCT_VIEW_REVERSE_IVR
          ? DASHBOARD_PRODUCT_VIEW_REVERSE_IVR
          : DASHBOARD_PRODUCT_VIEW_AI
      )
    : (
        hasReverseIvrProduct && !hasAiProduct
          ? DASHBOARD_PRODUCT_VIEW_REVERSE_IVR
          : DASHBOARD_PRODUCT_VIEW_AI
      );
  const isAiProductView = selectedProductViewMode === DASHBOARD_PRODUCT_VIEW_AI;
  const isReverseIvrProductView = selectedProductViewMode === DASHBOARD_PRODUCT_VIEW_REVERSE_IVR;
  useEffect(() => {
    if (!cockpit || selectedDashboardTab !== 'tenant') {
      return;
    }

    const next = new URLSearchParams(searchParams);
    if (hasMultipleProductViews) {
      if (
        productViewParam === DASHBOARD_PRODUCT_VIEW_AI
        || productViewParam === DASHBOARD_PRODUCT_VIEW_REVERSE_IVR
      ) {
        return;
      }
      next.set(DASHBOARD_PRODUCT_VIEW_PARAM, selectedProductViewMode);
      setSearchParams(next, { replace: true });
      return;
    }

    if (productViewParam !== null) {
      next.delete(DASHBOARD_PRODUCT_VIEW_PARAM);
      setSearchParams(next, { replace: true });
    }
  }, [
    cockpit,
    hasMultipleProductViews,
    productViewParam,
    searchParams,
    selectedProductViewMode,
    selectedDashboardTab,
    setSearchParams,
  ]);
  const hasTelephonyPack = Boolean(productMix?.hasTelephonyPack);
  const canViewAttendanceMetrics = Boolean(cockpit?.actions.canViewAttendanceMetrics);
  const aiToday = cockpit?.today.ai ?? null;
  const reverseIvrToday = cockpit?.today.reverseIvr ?? null;
  const todayAiAgentSummaries = useMemo(
    () => aiToday?.agentSummaries ?? [],
    [aiToday?.agentSummaries],
  );
  const hasTodayAiAgentSelector = todayAiAgentSummaries.length > 1;
  const selectedTodayAiAgentOption = (
    hasTodayAiAgentSelector && selectedTodayAiAgentId !== DASHBOARD_TODAY_AI_AGENT_ALL
      ? todayAiAgentSummaries.find((agent) => agent.id === selectedTodayAiAgentId) ?? null
      : null
  );
  const selectedTodayAiAgentDetail = (
    selectedTodayAiAgentId !== DASHBOARD_TODAY_AI_AGENT_ALL
      ? todayAiAgentDetailsById[selectedTodayAiAgentId] ?? null
      : null
  );
  const selectedTodayAiAgent = selectedTodayAiAgentDetail ?? selectedTodayAiAgentOption;
  const isTodayAiAgentDetailLoading = Boolean(
    selectedTodayAiAgentOption
    && !selectedTodayAiAgentDetail
    && loadingTodayAiAgentId === selectedTodayAiAgentId,
  );
  const isTodayAiAgentDetailError = Boolean(
    selectedTodayAiAgentOption
    && !selectedTodayAiAgentDetail
    && todayAiAgentErrorId === selectedTodayAiAgentId,
  );
  const aiTodayScope = selectedTodayAiAgentDetail ?? (selectedTodayAiAgentOption ? null : aiToday);
  const todayConsumption = selectedTodayAiAgentOption
    ? selectedTodayAiAgentDetail?.consumption ?? null
    : cockpit?.today.consumption ?? null;
  const lastActiveAiDay = aiTodayScope?.lastActiveDay ?? null;
  const hasLastActiveAiDay = Boolean(lastActiveAiDay);
  const lastActiveReverseIvrDay = reverseIvrToday?.lastActiveDay ?? null;
  const hasLastActiveReverseIvrDay = Boolean(lastActiveReverseIvrDay);
  const isTodayAiEmpty = Boolean(aiTodayScope && aiTodayScope.attemptedCalls === 0);
  const isTodayReverseIvrEmpty = Boolean(reverseIvrToday && reverseIvrToday.attemptedCalls === 0);
  const isShowingLastActiveAiDay = Boolean(isTodayAiEmpty && isTodayAiFallbackOpen && lastActiveAiDay);
  const isShowingLastActiveReverseIvrDay = Boolean(
    isTodayReverseIvrEmpty
    && isTodayReverseIvrFallbackOpen
    && lastActiveReverseIvrDay,
  );
  const displayedAiSummary = isShowingLastActiveAiDay ? lastActiveAiDay?.ai ?? null : aiTodayScope;
  const displayedReverseIvrSummary = isShowingLastActiveReverseIvrDay
    ? lastActiveReverseIvrDay?.reverseIvr ?? null
    : reverseIvrToday;
  const displayedAiConsumption = isShowingLastActiveAiDay ? lastActiveAiDay?.consumption ?? null : todayConsumption;
  const humanAttendance = selectedTodayAiAgentOption
    ? selectedTodayAiAgentDetail?.humanAttendance ?? null
    : cockpit?.today.humanAttendance ?? null;
  const displayedHumanAttendance = isShowingLastActiveAiDay
    ? lastActiveAiDay?.humanAttendance ?? null
    : humanAttendance;
  const isShowingHistoricalHumanAttendance = Boolean(
    (isShowingLastActiveAiDay && lastActiveAiDay?.humanAttendance)
  );
  const runtimeAiCalls = cockpit?.runtime.ai?.calls ?? null;
  const runtimeReverseIvrCalls = cockpit?.runtime.reverseIvr?.calls ?? null;
  const activeAiCallsCount = (
    Number(runtimeAiCalls?.queued || 0)
    + Number(runtimeAiCalls?.dialing || 0)
    + Number(runtimeAiCalls?.ringing || 0)
    + Number(runtimeAiCalls?.inProgress || 0)
  );
  const activeReverseIvrCallsCount = (
    Number(runtimeReverseIvrCalls?.queued || 0)
    + Number(runtimeReverseIvrCalls?.dialing || 0)
    + Number(runtimeReverseIvrCalls?.ringing || 0)
    + Number(runtimeReverseIvrCalls?.inProgress || 0)
  );
  const activeAiCampaignsCount = Number(cockpit?.runtime.ai?.campaignsRunningCount || 0);
  const activeReverseIvrCampaignsCount = Number(cockpit?.runtime.reverseIvr?.campaignsRunningCount || 0);
  const todayConsumptionRows = useMemo(() => {
    const rows: Array<{
      key: 'ai' | 'telephony';
      label: string;
      value: string;
      accent: string;
    }> = [];
    if (!isAiProductView) {
      return rows;
    }
    if (
      displayedAiConsumption?.aiConsumedSeconds !== null
      && displayedAiConsumption?.aiConsumedSeconds !== undefined
    ) {
      rows.push({
        key: 'ai',
        label: t('pages:dashboard.todayMetrics.aiMinutesConsumed'),
        value: formatDuration(displayedAiConsumption.aiConsumedSeconds),
        accent: theme.palette.success.main,
      });
    }
    if (
      displayedAiConsumption?.telephonyConsumedSeconds !== null
      && displayedAiConsumption?.telephonyConsumedSeconds !== undefined
    ) {
      rows.push({
        key: 'telephony',
        label: t('pages:dashboard.todayMetrics.telephonyMinutesConsumed'),
        value: formatDuration(displayedAiConsumption.telephonyConsumedSeconds),
        accent: theme.palette.info.main,
      });
    }
    return rows;
  }, [
    displayedAiConsumption?.aiConsumedSeconds,
    displayedAiConsumption?.telephonyConsumedSeconds,
    formatDuration,
    isAiProductView,
    t,
    theme.palette.info.main,
    theme.palette.success.main,
  ]);
  const cockpitPatterns = patterns ?? cockpit?.patterns ?? null;
  const carrierAnalytics = DASHBOARD_CARRIER_ANALYTICS_ENABLED
    ? cockpitPatterns?.carrier ?? null
    : null;
  const hasCarrierAnalytics = Boolean(
    carrierAnalytics?.today.rows.length || carrierAnalytics?.history.rows.length,
  );
  const consumptionEvolution = cockpitPatterns?.consumptionEvolution ?? null;
  const hasConsumptionEvolution = Boolean(
    consumptionEvolution?.points.some(
      (point) => (
        point.aiConsumedSeconds !== null
        || point.telephonyConsumedSeconds !== null
      ),
    )
    || consumptionEvolution?.aiAgentSeries?.some(
      (series) => series.points.some((point) => point.aiConsumedSeconds > 0),
    ),
  );
  const performanceEvolution = cockpitPatterns?.performanceEvolution ?? null;
  const hasPerformanceEvolution = Boolean(
    performanceEvolution?.points.some(
      (point) => point.answeredCalls > 0 || point.convertedCalls > 0,
    ),
  );
  const campaignBaseHealth = cockpitPatterns?.campaignBaseHealth ?? null;
  const hasCampaignBaseHealth = Boolean(
    campaignBaseHealth?.campaigns.some((campaign) => campaign.attemptedContacts > 0),
  );
  const shouldHideAdvancedAiPanelsForTenant = (
    !isSuperuser
    &&
    currentTenant?.id === DUQUE_DASHBOARD_ADVANCED_AI_PANELS_BLOCKED_TENANT_ID
  );
  const efficiencyEvolution = cockpitPatterns?.efficiencyEvolution ?? null;
  const hasConsumptionEfficiency = (
    !shouldHideAdvancedAiPanelsForTenant
    && Boolean(
      efficiencyEvolution?.points.some((point) => point.aiConsumedSeconds > 0)
      || efficiencyEvolution?.aiAgentSeries?.some(
        (series) => series.points.some((point) => point.aiConsumedSeconds > 0),
      ),
    )
  );
  const conversationQualityEvolution = cockpitPatterns?.conversationQualityEvolution ?? null;
  const hasConversationQuality = (
    !shouldHideAdvancedAiPanelsForTenant
    && Boolean(
      conversationQualityEvolution?.points.some(
        (point) => point.reviewEligibleCalls > 0 || point.convertedCalls > 0,
      )
      || conversationQualityEvolution?.aiAgentSeries?.some(
        (series) => series.points.some(
          (point) => point.reviewEligibleCalls > 0 || point.convertedCalls > 0,
        ),
      ),
    )
  );
  const aiPatterns = cockpitPatterns?.ai ?? null;
  const reverseIvrPatterns = cockpitPatterns?.reverseIvr ?? null;
  const humanAttendancePatterns = cockpitPatterns?.humanAttendance ?? null;
  const hasHumanAttendancePatterns = Boolean(
    humanAttendancePatterns?.performanceEvolution.points.some(
      (point) => point.transfersStarted > 0 || point.humanAnsweredCalls > 0,
    )
    || humanAttendancePatterns?.operatorHealth.series.length
    || humanAttendancePatterns?.operatorHealth.operators.length,
  );
  const reverseIvrAudioSeries = useMemo(
    () => reverseIvrPatterns?.audioSeries ?? [],
    [reverseIvrPatterns?.audioSeries],
  );
  const selectedReverseIvrAudio = reverseIvrAudioSeries.find(
    (series) => series.id === selectedReverseIvrAudioId,
  ) ?? null;
  const reverseIvrScopeLabel = selectedReverseIvrAudio?.label
    ?? t('pages:dashboard.patterns.reverseIvrAudio.allAudios');
  const reverseIvrPerformancePoints = useMemo(
    () => getReverseIvrScopePerformance(reverseIvrPatterns?.performanceEvolution, selectedReverseIvrAudio),
    [reverseIvrPatterns?.performanceEvolution, selectedReverseIvrAudio],
  );
  const reverseIvrHeatmap = useMemo(
    () => getReverseIvrScopeHeatmap(reverseIvrPatterns?.heatmap, selectedReverseIvrAudio),
    [reverseIvrPatterns?.heatmap, selectedReverseIvrAudio],
  );
  const reverseIvrDigitDistribution = useMemo(
    () => getReverseIvrScopeDigitDistribution(reverseIvrPatterns?.digitDistribution, selectedReverseIvrAudio),
    [reverseIvrPatterns?.digitDistribution, selectedReverseIvrAudio],
  );
  const reverseIvrCampaignBaseHealth = reverseIvrPatterns?.campaignBaseHealth ?? null;
  const hasReverseIvrCampaignBaseHealth = Boolean(
    reverseIvrCampaignBaseHealth?.campaigns.some((campaign) => campaign.attemptedContacts > 0),
  );
  const isHistoryAnalyticsLoading = isDashboardAnalyticsSliceLoading(analyticsSlices, 'history');
  const isCarrierAnalyticsLoading = (
    DASHBOARD_CARRIER_ANALYTICS_ENABLED
    && isDashboardAnalyticsSliceLoading(analyticsSlices, 'carrier')
  );
  const isCampaignBaseHealthLoading = isDashboardAnalyticsSliceLoading(analyticsSlices, 'campaign-base-health');
  const isConversationQualityLoading = isDashboardAnalyticsSliceLoading(analyticsSlices, 'conversation-quality');
  const isAiPatternsLoading = isDashboardAnalyticsSliceLoading(analyticsSlices, 'patterns-ai');
  const isReverseIvrPatternsLoading = isDashboardAnalyticsSliceLoading(analyticsSlices, 'patterns-reverse-ivr');
  const isHumanAttendancePatternsLoading = isDashboardAnalyticsSliceLoading(
    analyticsSlices,
    'patterns-human-attendance',
  );
  const isHistoryAnalyticsError = isDashboardAnalyticsSliceError(analyticsSlices, 'history');
  const isCarrierAnalyticsError = (
    DASHBOARD_CARRIER_ANALYTICS_ENABLED
    && isDashboardAnalyticsSliceError(analyticsSlices, 'carrier')
  );
  const isCampaignBaseHealthError = isDashboardAnalyticsSliceError(analyticsSlices, 'campaign-base-health');
  const isConversationQualityError = isDashboardAnalyticsSliceError(analyticsSlices, 'conversation-quality');
  const isAiPatternsError = isDashboardAnalyticsSliceError(analyticsSlices, 'patterns-ai');
  const isReverseIvrPatternsError = isDashboardAnalyticsSliceError(analyticsSlices, 'patterns-reverse-ivr');
  const isHumanAttendancePatternsError = isDashboardAnalyticsSliceError(
    analyticsSlices,
    'patterns-human-attendance',
  );
  const todayAiComparisonRows = useMemo(
    () => (
      isTodayAiEmpty || isShowingLastActiveAiDay
        ? []
        : buildTodayAiRecentComparison(
          aiTodayScope,
          todayConsumption?.aiConsumedSeconds,
        )
    ),
    [
      aiTodayScope,
      isShowingLastActiveAiDay,
      isTodayAiEmpty,
      todayConsumption?.aiConsumedSeconds,
    ],
  );
  const todayReverseIvrComparisonRows = useMemo(
    () => (
      isTodayReverseIvrEmpty || isShowingLastActiveReverseIvrDay
        ? []
        : buildTodayReverseIvrRecentComparison(reverseIvrToday)
    ),
    [
      isShowingLastActiveReverseIvrDay,
      isTodayReverseIvrEmpty,
      reverseIvrToday,
    ],
  );
  const todayAiEfficiency = useMemo(
    () => (displayedAiSummary
      ? buildTodayAiEfficiency(
        displayedAiConsumption?.aiConsumedSeconds,
        displayedAiSummary.answeredCalls,
        displayedAiSummary.convertedCalls,
        isShowingLastActiveAiDay ? null : aiTodayScope?.recentComparison,
      )
      : null),
    [
      aiTodayScope?.recentComparison,
      displayedAiConsumption?.aiConsumedSeconds,
      displayedAiSummary,
      isShowingLastActiveAiDay,
    ],
  );
  const todayAiCostReturn = useMemo(
    () => (displayedAiSummary
      ? buildTodayAiCostReturn(
        displayedAiSummary.totalEstimatedCostUsd,
        displayedAiSummary.answeredCalls,
        displayedAiSummary.convertedCalls,
      )
      : null),
    [displayedAiSummary],
  );
  const todayAiComparisonWindowDays = aiTodayScope?.recentComparison?.windowDays ?? HISTORY_DAYS;
  const todayReverseIvrComparisonWindowDays = reverseIvrToday?.recentComparison?.windowDays ?? HISTORY_DAYS;
  const todayHumanAttendanceComparisonWindowDays = displayedHumanAttendance?.recentComparison?.windowDays ?? HISTORY_DAYS;
  const isTodayComparisonLoading = todayComparisonStatus.status === 'loading';
  const isTodayComparisonError = todayComparisonStatus.status === 'error';
  const isTodayAiComparisonLoading = (
    !selectedTodayAiAgentOption
    && !isShowingLastActiveAiDay
    && isTodayComparisonLoading
  );
  const isTodayAiComparisonError = (
    !selectedTodayAiAgentOption
    && !isShowingLastActiveAiDay
    && isTodayComparisonError
  );
  const isTodayReverseIvrComparisonLoading = !isShowingLastActiveReverseIvrDay && isTodayComparisonLoading;
  const isTodayReverseIvrComparisonError = !isShowingLastActiveReverseIvrDay && isTodayComparisonError;
  const isTodayHumanAttendanceComparisonLoading = (
    !isShowingHistoricalHumanAttendance
    && isTodayComparisonLoading
  );
  const isTodayHumanAttendanceComparisonError = (
    !isShowingHistoricalHumanAttendance
    && isTodayComparisonError
  );
  const shouldRenderTodayAiEmptyState = Boolean(isTodayAiEmpty && !isShowingLastActiveAiDay);
  const shouldRenderTodayReverseIvrEmptyState = Boolean(
    isTodayReverseIvrEmpty && !isShowingLastActiveReverseIvrDay,
  );
  const shouldRenderTodayAiRichSnapshot = Boolean(
    displayedAiSummary && (!isTodayAiEmpty || isShowingLastActiveAiDay),
  );
  const shouldRenderTodayReverseIvrRichSnapshot = Boolean(
    displayedReverseIvrSummary && (!isTodayReverseIvrEmpty || isShowingLastActiveReverseIvrDay),
  );
  const shouldLoadAiAnalytics = isAiProductView && Boolean(productMix?.hasAiProduct);
  const shouldLoadReverseIvrAnalytics = isReverseIvrProductView && Boolean(productMix?.hasReverseIvrProduct);
  const shouldLoadHumanAttendanceAnalytics = isAiProductView && Boolean(productMix?.hasTelephonyPack);
  const hasAiPatternSliceLoading = (
    isHistoryAnalyticsLoading
    || isHumanAttendancePatternsLoading
    || isCampaignBaseHealthLoading
    || isConversationQualityLoading
    || isAiPatternsLoading
  );
  const hasAiPatternSliceError = (
    isHistoryAnalyticsError
    || isHumanAttendancePatternsError
    || isCampaignBaseHealthError
    || isConversationQualityError
    || isAiPatternsError
  );
  const hasVisiblePatternSliceLoading = isCarrierAnalyticsLoading || (
    isAiProductView
      ? hasAiPatternSliceLoading
      : isReverseIvrPatternsLoading
  );
  const hasVisiblePatternSliceError = isCarrierAnalyticsError || (
    isAiProductView
      ? hasAiPatternSliceError
      : isReverseIvrPatternsError
  );
  const telephonyBudget = cockpit?.budget.telephony ?? null;

  const telephonyBlockedLabel = resolveOperationalBlockLabel(
    telephonyBudget?.blockReason,
    'pages:dashboard.budget.telephonyBlocked.generic',
    t,
    TELEPHONY_BLOCK_REASON_LABEL_KEYS,
  );
  const shouldShowHumanBlockedState = Boolean(
    hasTelephonyPack && telephonyBudget && !telephonyBudget.isAvailable,
  ) && canViewAttendanceMetrics;
  useEffect(() => {
    if (!isTodayAiEmpty || !hasLastActiveAiDay) {
      setIsTodayAiFallbackOpen(false);
    }
  }, [hasLastActiveAiDay, isTodayAiEmpty]);
  useEffect(() => {
    if (
      selectedTodayAiAgentId !== DASHBOARD_TODAY_AI_AGENT_ALL
      && !todayAiAgentSummaries.some((agent) => agent.id === selectedTodayAiAgentId)
    ) {
      setSelectedTodayAiAgentId(DASHBOARD_TODAY_AI_AGENT_ALL);
    }
  }, [selectedTodayAiAgentId, todayAiAgentSummaries]);
  useEffect(() => {
    setTodayAiAgentDetailsById({});
    setLoadingTodayAiAgentId(null);
    setTodayAiAgentErrorId(null);
  }, [cockpit?.snapshotAt, currentTenant?.id]);
  useEffect(() => {
    if (
      !cockpit
      || selectedTodayAiAgentId === DASHBOARD_TODAY_AI_AGENT_ALL
      || !selectedTodayAiAgentOption
      || todayAiAgentDetailsById[selectedTodayAiAgentId]
    ) {
      return undefined;
    }

    let isActive = true;
    setLoadingTodayAiAgentId(selectedTodayAiAgentId);
    setTodayAiAgentErrorId(null);
    fetchDashboardCockpitTodayAiAgent(selectedTodayAiAgentId, HISTORY_DAYS)
      .then((response) => {
        if (!isActive) {
          return;
        }
        setTodayAiAgentDetailsById((current) => ({
          ...current,
          [response.agent.id]: response.agent,
        }));
        setLoadingTodayAiAgentId((current) => (
          current === selectedTodayAiAgentId ? null : current
        ));
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setTodayAiAgentErrorId(selectedTodayAiAgentId);
        setLoadingTodayAiAgentId((current) => (
          current === selectedTodayAiAgentId ? null : current
        ));
      });

    return () => {
      isActive = false;
    };
  }, [
    cockpit,
    selectedTodayAiAgentId,
    selectedTodayAiAgentOption,
    todayAiAgentDetailsById,
  ]);
  useEffect(() => {
    if (!isTodayReverseIvrEmpty || !hasLastActiveReverseIvrDay) {
      setIsTodayReverseIvrFallbackOpen(false);
    }
  }, [hasLastActiveReverseIvrDay, isTodayReverseIvrEmpty]);
  useEffect(() => {
    if (
      selectedReverseIvrAudioId !== 'all'
      && !reverseIvrAudioSeries.some((series) => series.id === selectedReverseIvrAudioId)
    ) {
      setSelectedReverseIvrAudioId('all');
    }
  }, [reverseIvrAudioSeries, selectedReverseIvrAudioId]);
  const shouldShowCampaignsSection = Boolean(cockpit?.actions.canViewCampaigns && cockpit.campaigns.topActive.length);

  const todayDateLabel = useMemo(() => {
    if (!cockpit?.today.date) {
      return '';
    }
    return formatDateOnly(`${cockpit.today.date}T12:00:00`, {
      locale,
      fallback: cockpit.today.date,
    });
  }, [cockpit?.today.date, locale]);
  const lastActiveAiDayDateLabel = useMemo(() => {
    if (!lastActiveAiDay?.date) {
      return '';
    }
    return formatDateOnly(`${lastActiveAiDay.date}T12:00:00`, {
      locale,
      fallback: lastActiveAiDay.date,
    });
  }, [lastActiveAiDay?.date, locale]);
  const lastActiveAiDayLastActivityLabel = useMemo(() => {
    if (!lastActiveAiDay?.lastActivityAt) {
      return null;
    }
    return formatDateTime(lastActiveAiDay.lastActivityAt, {
      locale,
      fallback: lastActiveAiDay.lastActivityAt,
    });
  }, [lastActiveAiDay?.lastActivityAt, locale]);
  const lastActiveReverseIvrDayDateLabel = useMemo(() => {
    if (!lastActiveReverseIvrDay?.date) {
      return '';
    }
    return formatDateOnly(`${lastActiveReverseIvrDay.date}T12:00:00`, {
      locale,
      fallback: lastActiveReverseIvrDay.date,
    });
  }, [lastActiveReverseIvrDay?.date, locale]);
  const lastActiveReverseIvrDayLastActivityLabel = useMemo(() => {
    if (!lastActiveReverseIvrDay?.lastActivityAt) {
      return null;
    }
    return formatDateTime(lastActiveReverseIvrDay.lastActivityAt, {
      locale,
      fallback: lastActiveReverseIvrDay.lastActivityAt,
    });
  }, [lastActiveReverseIvrDay?.lastActivityAt, locale]);
  const todayAiSubsectionSubtitle = isShowingLastActiveAiDay
    ? (
        selectedTodayAiAgent
          ? t('pages:dashboard.todayAi.empty.fallbackAgentSubtitle', {
              agentName: selectedTodayAiAgent.name,
              date: lastActiveAiDayDateLabel || lastActiveAiDay?.date,
            })
          : t('pages:dashboard.todayAi.empty.fallbackSubtitle', {
              date: lastActiveAiDayDateLabel || lastActiveAiDay?.date,
            })
      )
    : (
        selectedTodayAiAgent
          ? t('pages:dashboard.todayAi.agentScope.subtitle', {
              agentName: selectedTodayAiAgent.name,
            })
          : t('pages:dashboard.todayPanels.aiSubtitle')
      );
  const todayReverseIvrSubsectionSubtitle = isShowingLastActiveReverseIvrDay
    ? t('pages:dashboard.todayReverseIvr.empty.fallbackSubtitle', {
        date: lastActiveReverseIvrDayDateLabel || lastActiveReverseIvrDay?.date,
      })
    : t('pages:dashboard.todayPanels.reverseIvrSubtitle');
  const displayedHumanAttendanceDateLabel = (
    isShowingLastActiveAiDay
      ? lastActiveAiDayDateLabel || lastActiveAiDay?.date || ''
      : ''
  );
  const todayHumanHandoffSubsectionSubtitle = isShowingHistoricalHumanAttendance
    ? (
        selectedTodayAiAgent
          ? t('pages:dashboard.todayHumanAttendance.fallbackAgentSubtitle', {
              agentName: selectedTodayAiAgent.name,
              date: displayedHumanAttendanceDateLabel,
            })
          : t('pages:dashboard.todayHumanAttendance.fallbackSubtitle', {
              date: displayedHumanAttendanceDateLabel,
            })
      )
    : (
        selectedTodayAiAgent
          ? t('pages:dashboard.todayHumanAttendance.agentSubtitle', {
              agentName: selectedTodayAiAgent.name,
            })
          : t('pages:dashboard.todayPanels.humanHandoffSubtitle')
      );

  const pageBackground = {
    position: 'relative',
    display: 'grid',
    gap: 3,
    px: { xs: 0, lg: 0.5 },
  } as const;

  if (selectedDashboardTab === 'system') {
    return (
      <>
        {isSuperuser ? (
          <ButtonTabs
            tabs={dashboardTabs}
            useUrl
            tabParam="tab"
            defaultValue="tenant"
            renderContent={false}
            sx={{ mb: 3 }}
            contentSx={{ mt: 0 }}
          />
        ) : null}
        <SystemCockpitTab />
      </>
    );
  }

  return (
    <>
      {isSuperuser ? (
        <ButtonTabs
          tabs={dashboardTabs}
          useUrl
          tabParam="tab"
          defaultValue="tenant"
          renderContent={false}
          sx={{ mb: 3 }}
          contentSx={{ mt: 0 }}
        />
      ) : null}
      <Box sx={pageBackground}>
        {(isLoading || isRefreshing || hasVisiblePatternSliceLoading) && (
          <LinearProgress sx={{ position: 'relative', zIndex: 1, borderRadius: 999 }} />
        )}

        <Paper
          sx={[
            buildPanelSx(theme, 'neutral', 72),
            {
              p: { xs: 1.5, md: 2 },
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
            },
          ]}
        >
          {hasMultipleProductViews ? (
            <ButtonTabs
              tabs={productViewTabs}
              useUrl
              tabParam={DASHBOARD_PRODUCT_VIEW_PARAM}
              defaultValue={selectedProductViewMode}
              renderContent={false}
              sx={{ minWidth: 0, flex: 1 }}
              tabsSx={{
                mt: 0,
                minHeight: 42,
                '& .MuiTabs-flexContainer': {
                  gap: 1,
                },
              }}
              tabButtonSx={{
                minHeight: 40,
                px: 1.6,
                borderRadius: 999,
              }}
            />
          ) : (
            <Box sx={{ display: { xs: 'none', sm: 'block' }, flex: 1 }} />
          )}

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={refreshDashboard}
            sx={{
              alignSelf: { xs: 'stretch', sm: 'center' },
              justifyContent: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {cockpit
              ? t('pages:dashboard.refreshWithSnapshot', {
                  value: formatDateTime(cockpit.snapshotAt, { locale, fallback: cockpit.snapshotAt }),
                })
              : t('common:actions.refresh')}
          </Button>
        </Paper>

        {error ? (
          <Alert
            severity="error"
            action={(
              <Button color="inherit" size="small" onClick={refreshDashboard}>
                {t('pages:dashboard.retryLoad')}
              </Button>
            )}
            sx={{ position: 'relative', zIndex: 1 }}
          >
            {t('pages:dashboard.loadFailed')}
          </Alert>
        ) : null}

        {!cockpit && isLoading ? (
          <DashboardLoadingState />
        ) : cockpit ? (
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'grid',
              gap: 3,
              alignItems: 'start',
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'minmax(300px, 0.62fr) minmax(0, 1.9fr)',
              },
            }}
          >
            <Stack
              gap={3}
              sx={{
                order: { xs: 2, xl: 2 },
                gridColumn: { xs: '1 / -1', xl: '2 / 3' },
                minWidth: 0,
              }}
            >
		            <Paper
		              sx={[
		                buildPanelSx(theme, 'rose'),
		                {
		                  p: { xs: 2.5, md: 3 },
		                },
		              ]}
	            >
              <DashboardSectionHeader
                eyebrow={t('pages:dashboard.sections.patterns.eyebrow')}
                title={t('pages:dashboard.sections.patterns.title')}
                subtitle={t('pages:dashboard.sections.patterns.subtitle', {
                  days: formatNumber(cockpitPatterns?.windowDays ?? HISTORY_DAYS),
                })}
              />

              <Stack gap={2} sx={{ position: 'relative', zIndex: 1 }}>
                {/*
                  Carrier analytics is temporarily hidden while we redesign the
                  dashboard panel and optimize its heavy tenant aggregate.
                */}
                {isCarrierAnalyticsLoading ? (
                  <DashboardAnalyticsSliceSkeleton height={260} />
                ) : null}

                {isCarrierAnalyticsError && !carrierAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('carrier');
                    }}
                  />
                ) : null}

                {carrierAnalytics && hasCarrierAnalytics ? (
                  <CarrierAnalyticsPanel
                    data={carrierAnalytics}
                    t={t}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                  />
                ) : null}

                {isHistoryAnalyticsLoading && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={360} />
                ) : null}

                {isHistoryAnalyticsError && shouldLoadAiAnalytics && !consumptionEvolution && !performanceEvolution && !efficiencyEvolution ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('history');
                    }}
                  />
                ) : null}

                {isAiProductView && hasConsumptionEvolution && consumptionEvolution ? (
                  <ConsumptionEvolutionChart
                    title={t('pages:dashboard.patterns.consumptionEvolution.title')}
                    subtitle={t('pages:dashboard.patterns.consumptionEvolution.subtitle', {
                      days: formatNumber(consumptionEvolution.windowDays),
                    })}
                    titleIcon={<DataUsage />}
                    accent={theme.palette.success.main}
                    data={consumptionEvolution}
                    aiLabel={t('pages:dashboard.patterns.consumptionEvolution.ai')}
                    telephonyLabel={t('pages:dashboard.patterns.consumptionEvolution.telephony')}
                    agentLabel={(agentName) => (
                      t('pages:dashboard.patterns.consumptionEvolution.agent', { agentName })
                    )}
                    minutesUnitLabel={t('pages:dashboard.patterns.consumptionEvolution.minutes')}
                    emptyLabel={t('pages:dashboard.patterns.consumptionEvolution.empty')}
                    locale={locale}
                  />
                ) : null}

                {isAiProductView && hasPerformanceEvolution && performanceEvolution ? (
                  <PerformanceEvolutionChart
                    title={t('pages:dashboard.patterns.performanceEvolution.title')}
                    subtitle={t('pages:dashboard.patterns.performanceEvolution.subtitle', {
                      days: formatNumber(performanceEvolution.windowDays),
                    })}
                    titleIcon={<QueryStats />}
                    accent={theme.palette.warning.main}
                    data={performanceEvolution}
                    answeredLabel={t('pages:dashboard.patterns.performanceEvolution.answered')}
                    convertedLabel={t('pages:dashboard.patterns.performanceEvolution.converted')}
                    agentConvertedLabel={(agentName) => (
                      t('pages:dashboard.patterns.performanceEvolution.agentConverted', { agentName })
                    )}
                    callsUnitLabel={t('pages:dashboard.patterns.performanceEvolution.calls')}
                    emptyLabel={t('pages:dashboard.patterns.performanceEvolution.empty')}
                    locale={locale}
                  />
                ) : null}

                {isHumanAttendancePatternsLoading && shouldLoadHumanAttendanceAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={360} />
                ) : null}

                {isHumanAttendancePatternsError && shouldLoadHumanAttendanceAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('patterns-human-attendance');
                    }}
                  />
                ) : null}

                {isAiProductView && hasHumanAttendancePatterns && humanAttendancePatterns ? (
                  <>
                    <HumanAttendancePerformancePanel data={humanAttendancePatterns.performanceEvolution} />
                    <HumanAttendanceOperatorHealthPanel data={humanAttendancePatterns} />
                  </>
                ) : null}

                {isCampaignBaseHealthLoading && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={360} />
                ) : null}

                {isCampaignBaseHealthError && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('campaign-base-health');
                    }}
                  />
                ) : null}

                {isAiProductView && hasCampaignBaseHealth && campaignBaseHealth ? (
                  <CampaignBaseHealthPanel data={campaignBaseHealth} />
                ) : null}

                {isAiProductView && hasConsumptionEfficiency && efficiencyEvolution ? (
                  <AiConsumptionEfficiencyChart
                    title={t('pages:dashboard.patterns.consumptionEfficiency.title')}
                    subtitle={t('pages:dashboard.patterns.consumptionEfficiency.subtitle', {
                      days: formatNumber(efficiencyEvolution.windowDays),
                    })}
                    titleIcon={<Speed />}
                    data={efficiencyEvolution}
                    metricLabels={{
                      aiMinutesPerAnswer: t('pages:dashboard.patterns.consumptionEfficiency.aiMinutesPerAnswer'),
                      aiMinutesPerConversion: t('pages:dashboard.patterns.consumptionEfficiency.aiMinutesPerConversion'),
                      answersPer100AiMinutes: t('pages:dashboard.patterns.consumptionEfficiency.answersPer100AiMinutes'),
                      conversionsPer100AiMinutes: t('pages:dashboard.patterns.consumptionEfficiency.conversionsPer100AiMinutes'),
                      telephonyMinutesPerConversion: t('pages:dashboard.patterns.consumptionEfficiency.telephonyMinutesPerConversion'),
                    }}
                    metricHelps={{
                      aiMinutesPerAnswer: t('pages:dashboard.patterns.consumptionEfficiency.aiMinutesPerAnswerHelp'),
                      aiMinutesPerConversion: t('pages:dashboard.patterns.consumptionEfficiency.aiMinutesPerConversionHelp'),
                      answersPer100AiMinutes: t('pages:dashboard.patterns.consumptionEfficiency.answersPer100AiMinutesHelp'),
                      conversionsPer100AiMinutes: t('pages:dashboard.patterns.consumptionEfficiency.conversionsPer100AiMinutesHelp'),
                      telephonyMinutesPerConversion: t('pages:dashboard.patterns.consumptionEfficiency.telephonyMinutesPerConversionHelp'),
                    }}
                    agentLabel={(agentName) => (
                      t('pages:dashboard.patterns.consumptionEfficiency.agent', { agentName })
                    )}
                    agentSelectorLabel={t('pages:dashboard.patterns.consumptionEfficiency.agents')}
                    emptyLabel={t('pages:dashboard.patterns.consumptionEfficiency.empty')}
                    contextLabels={{
                      aggregateSeries: t('pages:dashboard.patterns.consumptionEfficiency.aggregateSeries'),
                      baseOfDay: t('pages:dashboard.patterns.consumptionEfficiency.baseOfDay'),
                      aiMinutes: t('pages:dashboard.patterns.consumptionEfficiency.aiMinutes'),
                      telephonyConvertedMinutes: t('pages:dashboard.patterns.consumptionEfficiency.telephonyConvertedMinutes'),
                      answered: t('pages:dashboard.patterns.performanceEvolution.answered'),
                      converted: t('pages:dashboard.patterns.performanceEvolution.converted'),
                      minutesUnit: t('pages:dashboard.patterns.consumptionEvolution.minutes'),
                    }}
                    locale={locale}
                  />
                ) : null}

                {isConversationQualityLoading && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={340} />
                ) : null}

                {isConversationQualityError && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('conversation-quality');
                    }}
                  />
                ) : null}

                {isAiProductView && hasConversationQuality && conversationQualityEvolution ? (
                  <ConversationQualityChart
                    title={t('pages:dashboard.patterns.conversationQuality.title')}
                    subtitle={t('pages:dashboard.patterns.conversationQuality.subtitle', {
                      days: formatNumber(conversationQualityEvolution.windowDays),
                    })}
                    titleIcon={<GraphicEq />}
                    data={conversationQualityEvolution}
                    metricLabels={{
                      averageConvertedSeconds: t('pages:dashboard.patterns.conversationQuality.averageConvertedSeconds'),
                      shortInterruptionRate: t('pages:dashboard.patterns.conversationQuality.shortInterruptionRate'),
                      longUnconvertedRate: t('pages:dashboard.patterns.conversationQuality.longUnconvertedRate'),
                    }}
                    metricHelps={{
                      averageConvertedSeconds: t('pages:dashboard.patterns.conversationQuality.averageConvertedSecondsHelp'),
                      shortInterruptionRate: t('pages:dashboard.patterns.conversationQuality.shortInterruptionRateHelp'),
                      longUnconvertedRate: t('pages:dashboard.patterns.conversationQuality.longUnconvertedRateHelp'),
                    }}
                    agentLabel={(agentName) => (
                      t('pages:dashboard.patterns.conversationQuality.agent', { agentName })
                    )}
                    agentSelectorLabel={t('pages:dashboard.patterns.conversationQuality.agents')}
                    emptyLabel={t('pages:dashboard.patterns.conversationQuality.empty')}
                    contextLabels={{
                      operation: t('pages:dashboard.patterns.conversationQuality.operation'),
                      baseUsed: t('pages:dashboard.patterns.conversationQuality.baseUsed'),
                      answered: t('pages:dashboard.patterns.performanceEvolution.answered'),
                      eligible: t('pages:dashboard.patterns.conversationQuality.eligible'),
                      converted: t('pages:dashboard.patterns.performanceEvolution.converted'),
                      shortCalls: t('pages:dashboard.patterns.conversationQuality.shortCalls'),
                      longCalls: t('pages:dashboard.patterns.conversationQuality.longCalls'),
                      convertedBase: t('pages:dashboard.patterns.conversationQuality.convertedBase', { value: '__value__' }),
                      shortBase: t('pages:dashboard.patterns.conversationQuality.shortBase', { itemCount: '__itemCount__', base: '__base__' }),
                      longBase: t('pages:dashboard.patterns.conversationQuality.longBase', { itemCount: '__itemCount__', base: '__base__' }),
                      thresholdShort: t('pages:dashboard.patterns.conversationQuality.thresholdShort', { seconds: '__seconds__' }),
                      thresholdLong: t('pages:dashboard.patterns.conversationQuality.thresholdLong', { seconds: '__seconds__' }),
                    }}
                    locale={locale}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                    formatDuration={formatDuration}
                  />
                ) : null}

                {isAiPatternsLoading && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={360} />
                ) : null}

                {isAiPatternsError && shouldLoadAiAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('patterns-ai');
                    }}
                  />
                ) : null}

                {isAiProductView && aiPatterns ? (
                  <TimePatternHeatmap
                    data={aiPatterns.heatmap ?? []}
                    aiAgentSeries={aiPatterns.aiAgentSeries ?? []}
                    title={t('pages:dashboard.patterns.timeHeatmap.title')}
                    subtitle={t('pages:dashboard.patterns.timeHeatmap.subtitle')}
                    titleIcon={<AccessTime />}
                    metricOptions={[
                      {
                        key: 'answerRate',
                        label: t('pages:dashboard.patterns.answerRate'),
                        accent: theme.palette.info.main,
                      },
                      {
                        key: 'conversionRate',
                        label: t('pages:dashboard.patterns.conversionRate'),
                        accent: theme.palette.primary.main,
                      },
                    ]}
                    weekdayLabelMap={weekdayLabelMap}
                    emptyLabel={t('pages:dashboard.patterns.timeHeatmap.empty')}
                    lowSampleLabel={t('pages:dashboard.patterns.lowSample')}
                    attemptsLabel={t('pages:dashboard.patterns.attempts')}
                    answeredLabel={t('pages:dashboard.todayMetrics.answeredCalls')}
                    convertedLabel={t('pages:dashboard.todayMetrics.convertedCalls')}
                    heardToEndLabel={t('pages:dashboard.todayMetrics.heardToEndCalls')}
                    topSlotsLabel={t('pages:dashboard.patterns.timeHeatmap.topSlots')}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                  />
                ) : null}

                {isAiProductView && aiPatterns?.territory ? (
                  <TerritoryPerformancePanel
                    territory={aiPatterns.territory}
                    product="ai"
                    aiAgentSeries={aiPatterns.aiAgentSeries ?? []}
                  />
                ) : null}

                {isReverseIvrPatternsLoading && shouldLoadReverseIvrAnalytics ? (
                  <DashboardAnalyticsSliceSkeleton height={360} />
                ) : null}

                {isReverseIvrPatternsError && shouldLoadReverseIvrAnalytics ? (
                  <DashboardAnalyticsSliceErrorState
                    label={t('pages:dashboard.loadFailed')}
                    retryLabel={t('pages:dashboard.retryLoad')}
                    onRetry={() => {
                      void retryAnalyticsSlice('patterns-reverse-ivr');
                    }}
                  />
                ) : null}

                {isReverseIvrProductView && reverseIvrPatterns ? (
                  <ReverseIvrAudioScopeSelector
                    audioSeries={reverseIvrAudioSeries}
                    selectedAudioId={selectedReverseIvrAudioId}
                    onSelect={setSelectedReverseIvrAudioId}
                    t={t}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                  />
                ) : null}

                {isReverseIvrProductView && reverseIvrPatterns?.performanceEvolution ? (
                  <ReverseIvrPerformanceEvolutionPanel
                    points={reverseIvrPerformancePoints}
                    windowDays={reverseIvrPatterns.performanceEvolution.windowDays}
                    scopeLabel={reverseIvrScopeLabel}
                    t={t}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                    locale={locale}
                  />
                ) : null}

                {isReverseIvrProductView && reverseIvrPatterns ? (
                  <TimePatternHeatmap
                    data={reverseIvrHeatmap}
                    title={t('pages:dashboard.patterns.reverseIvrTimeHeatmap.title')}
                    subtitle={t('pages:dashboard.patterns.reverseIvrTimeHeatmap.subtitle', {
                      scope: reverseIvrScopeLabel,
                    })}
                    titleIcon={<AccessTime />}
                    metricOptions={[
                      {
                        key: 'answerRate',
                        label: t('pages:dashboard.patterns.answerRate'),
                        accent: theme.palette.info.main,
                      },
                      {
                        key: 'heardToEndRate',
                        label: t('pages:dashboard.patterns.heardToEndRate'),
                        accent: theme.palette.warning.main,
                      },
                    ]}
                    weekdayLabelMap={weekdayLabelMap}
                    emptyLabel={t('pages:dashboard.patterns.timeHeatmap.empty')}
                    lowSampleLabel={t('pages:dashboard.patterns.lowSample')}
                    attemptsLabel={t('pages:dashboard.patterns.attempts')}
                    answeredLabel={t('pages:dashboard.todayMetrics.answeredCalls')}
                    convertedLabel={t('pages:dashboard.todayMetrics.convertedCalls')}
                    heardToEndLabel={t('pages:dashboard.todayMetrics.heardToEndCalls')}
                    topSlotsLabel={t('pages:dashboard.patterns.timeHeatmap.topSlots')}
                    formatNumber={formatNumber}
                    formatPercent={formatPercent}
                  />
                ) : null}

                {isReverseIvrProductView && reverseIvrPatterns?.territory ? (
                  <TerritoryPerformancePanel territory={reverseIvrPatterns.territory} product="reverseIvr" />
                ) : null}

                {isReverseIvrProductView && hasReverseIvrCampaignBaseHealth && reverseIvrCampaignBaseHealth ? (
                  <CampaignBaseHealthPanel data={reverseIvrCampaignBaseHealth} />
                ) : null}

                {isReverseIvrProductView && reverseIvrPatterns ? (
                  <ReverseIvrDigitDistributionPanel
                    rows={reverseIvrDigitDistribution}
                    scopeLabel={reverseIvrScopeLabel}
                    t={t}
                    formatNumber={formatNumber}
                  />
                ) : null}

                {(
                  isAiProductView
                  && !hasConsumptionEvolution
                  && !hasPerformanceEvolution
                  && !hasHumanAttendancePatterns
                  && !hasCampaignBaseHealth
                  && !hasConsumptionEfficiency
                  && !hasConversationQuality
                  && !aiPatterns
                  && !hasCarrierAnalytics
                  && !hasVisiblePatternSliceLoading
                  && !hasVisiblePatternSliceError
                ) || (
                  isReverseIvrProductView
                  && !hasReverseIvrCampaignBaseHealth
                  && !reverseIvrPatterns
                  && !hasCarrierAnalytics
                  && !hasVisiblePatternSliceLoading
                  && !hasVisiblePatternSliceError
                ) ? (
                  <EmptyPanelState label={t('pages:dashboard.empty.patterns')} />
                ) : null}
              </Stack>
            </Paper>

	            {shouldShowCampaignsSection ? (
		              <Paper
		                sx={[
		                  buildPanelSx(theme, 'teal'),
		                  {
		                    p: { xs: 2.5, md: 3 },
		                  },
		                ]}
	              >
              <DashboardSectionHeader
                eyebrow={t('pages:dashboard.sections.campaigns.eyebrow')}
                title={t('pages:dashboard.sections.campaigns.title')}
                subtitle={t('pages:dashboard.sections.campaigns.subtitle')}
                action={cockpit.actions.canViewCampaigns ? (
                  <Button component={RouterLink} to="/campaigns" size="small" endIcon={<ArrowOutward />}>
                    {t('pages:dashboard.header.openCampaigns')}
                  </Button>
                ) : null}
              />

              <Stack gap={1.4} sx={{ position: 'relative', zIndex: 1 }}>
                {cockpit.campaigns.topActive.map((campaign: DashboardCockpitTopCampaign) => {
                  const metricColumns = buildCampaignMetricColumns(
                    campaign,
                    t,
                    formatNumber,
                    formatPercent,
                    formatCurrency,
                  );

                  return (
                    <Box
                      key={campaign.id}
                      component={RouterLink}
                      to={campaign.path}
                      sx={{
                        p: 2,
                        borderRadius: '18px',
                        textDecoration: 'none',
                        color: 'inherit',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.05),
                        transition: 'transform 160ms ease, border-color 160ms ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          borderColor: alpha(theme.palette.success.main, 0.38),
                        },
                      }}
                    >
                      <Stack gap={1.2}>
                        <Stack direction="row" justifyContent="space-between" gap={1.5} alignItems="center">
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700 }} noWrap>
                              {campaign.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {t('pages:dashboard.campaigns.pressure', {
                                value: formatNumber(campaign.activePressure),
                              })}
                            </Typography>
                          </Box>
                          <CampaignStatusChip status={campaign.operationalStatus} />
                        </Stack>
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1,
                            gridTemplateColumns: {
                              xs: 'repeat(2, minmax(0, 1fr))',
                              md: `repeat(${Math.min(metricColumns.length, 4)}, minmax(0, 1fr))`,
                            },
                          }}
                        >
                          {metricColumns.map((column) => (
                            <Box key={column.key}>
                              <Typography variant="caption" color="text.secondary">
                                {column.label}
                              </Typography>
                              <Typography sx={{ fontFamily: DISPLAY_FONT, fontSize: '1.4rem', lineHeight: 1 }}>
                                {column.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
	              </Paper>
	            ) : null}
            </Stack>

		          <Stack
                gap={3}
                sx={{
                  order: { xs: 1, xl: 1 },
                  gridColumn: { xs: '1 / -1', xl: '1 / 2' },
                  minWidth: 0,
                }}
              >
		            <Paper
		              sx={[
		                buildPanelSx(theme, 'amber'),
		                {
		                  p: { xs: 2.5, md: 3 },
		                },
		              ]}
	            >
              <DashboardSectionHeader
                eyebrow={t('pages:dashboard.sections.today.eyebrow')}
                title={t('pages:dashboard.sections.today.title')}
                subtitle={t('pages:dashboard.sections.today.subtitle', {
                  date: todayDateLabel || cockpit.today.date,
                })}
              />

		              <Stack
		                gap={2.2}
		                sx={{
		                  position: 'relative',
		                  zIndex: 1,
		                }}
		              >
                {todayConsumptionRows.length ? (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        mb: 0.8,
                        textTransform: 'uppercase',
                      }}
                    >
                      {t('pages:dashboard.todayBreakdowns.consumption')}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 1.4,
                        gridTemplateColumns: todayConsumptionRows.length > 1
                          ? 'repeat(2, minmax(0, 1fr))'
                          : '1fr',
                      }}
                    >
                      {todayConsumptionRows.map((row) => (
                        <Box
                          key={row.key}
                          sx={{
                            p: 1.6,
                            borderRadius: '18px',
                            border: `1px solid ${alpha(row.accent, 0.18)}`,
                            backgroundColor: alpha(row.accent, 0.08),
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {row.label}
                          </Typography>
                          <Typography sx={{ mt: 0.6, fontFamily: DISPLAY_FONT, fontSize: '1.7rem', lineHeight: 0.9 }}>
                            {row.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                {isAiProductView && aiToday ? (
                  <Box>
                    <ProductSubsectionTitle
                      title={t('pages:dashboard.products.ai')}
                      subtitle={todayAiSubsectionSubtitle}
                      action={hasTodayAiAgentSelector ? (
                        <TodayAiAgentScopeSelector
                          agents={todayAiAgentSummaries}
                          selectedAgentId={selectedTodayAiAgentId}
                          onSelectAgent={(agentId) => {
                            setSelectedTodayAiAgentId(agentId);
                            setIsTodayAiFallbackOpen(false);
                          }}
                          t={t}
                          formatNumber={formatNumber}
                        />
                      ) : null}
                    />

                    {isTodayAiAgentDetailLoading ? (
                      <Box sx={{ mt: 1.4 }}>
                        <LinearProgress
                          sx={{
                            height: 6,
                            borderRadius: 999,
                            backgroundColor: alpha(theme.palette.success.main, 0.12),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 999,
                              backgroundColor: theme.palette.success.main,
                            },
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.8 }}>
                          {t('pages:dashboard.todayAi.agentScope.loading', {
                            agentName: selectedTodayAiAgent?.name,
                          })}
                        </Typography>
                      </Box>
                    ) : null}

                    {isTodayAiAgentDetailError ? (
                      <Alert severity="error" sx={{ mt: 1.4 }}>
                        {t('pages:dashboard.todayAi.agentScope.error')}
                      </Alert>
                    ) : null}

                    {shouldRenderTodayAiEmptyState ? (
                      <TodayAiEmptyStatePanel
                        dateLabel={todayDateLabel || cockpit.today.date}
                        activeCampaignsCount={activeAiCampaignsCount}
                        activeCallsCount={activeAiCallsCount}
                        lastActivityLabel={lastActiveAiDayLastActivityLabel}
                        hasLastActiveDay={hasLastActiveAiDay}
                        onOpenLastActiveDay={() => setIsTodayAiFallbackOpen(true)}
                        t={t}
                        formatNumber={formatNumber}
                      />
                    ) : null}

                    {shouldRenderTodayAiRichSnapshot && displayedAiSummary ? (
                      <Stack gap={1.3} sx={{ mt: 1.4 }}>
                        {isShowingLastActiveAiDay ? (
                          <TodayAiLastActiveDayBanner
                            dateLabel={lastActiveAiDayDateLabel || lastActiveAiDay?.date || ''}
                            onBackToToday={() => setIsTodayAiFallbackOpen(false)}
                            t={t}
                          />
                        ) : null}

                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1.1,
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          }}
                        >
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.attemptedCalls')}
                            value={formatNumber(displayedAiSummary.attemptedCalls)}
                            accent={theme.palette.info.main}
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.answeredCalls')}
                            value={formatNumber(displayedAiSummary.answeredCalls)}
                            accent={theme.palette.success.main}
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.convertedCalls')}
                            value={formatNumber(displayedAiSummary.convertedCalls)}
                            accent={theme.palette.primary.main}
                            highlighted
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.conversionRate')}
                            value={formatPercent(displayedAiSummary.conversionRate)}
                            accent={theme.palette.primary.main}
                            helper={t('pages:dashboard.todayMetrics.answerRateWithValue', {
                              value: formatPercent(displayedAiSummary.answerRate),
                            })}
                            highlighted
                          />
                        </Box>

                        {!isShowingLastActiveAiDay ? (
                          <TodayAiRecentComparisonPanel
                            rows={todayAiComparisonRows}
                            loading={isTodayAiComparisonLoading}
                            error={isTodayAiComparisonError}
                            windowDays={todayAiComparisonWindowDays}
                            t={t}
                            formatNumber={formatNumber}
                            formatDecimal={formatDecimal}
                            formatPercent={formatPercent}
                          />
                        ) : null}

                        {todayAiEfficiency ? (
                          <TodayAiEfficiencyPanel
                            metrics={todayAiEfficiency}
                            t={t}
                            formatDuration={formatDuration}
                            formatDecimal={formatDecimal}
                            formatPercent={formatPercent}
                            notAvailableLabel={t('pages:dashboard.notAvailable')}
                          />
                        ) : null}

                        {todayAiCostReturn ? (
                          <TodayAiCostReturnPanel
                            metrics={todayAiCostReturn}
                            t={t}
                            formatCurrency={formatCurrency}
                            notAvailableLabel={t('pages:dashboard.notAvailable')}
                          />
                        ) : null}

                        <TodayAiResultFunnelPanel
                          todayAi={displayedAiSummary}
                          t={t}
                          formatNumber={formatNumber}
                        />
                      </Stack>
                    ) : null}
                  </Box>
                ) : null}

                {isReverseIvrProductView && reverseIvrToday ? (
                  <>
                    <Box>
                      <ProductSubsectionTitle
                        title={t('pages:dashboard.products.reverseIvr')}
                        subtitle={todayReverseIvrSubsectionSubtitle}
                      />

                      {shouldRenderTodayReverseIvrEmptyState ? (
                        <TodayReverseIvrEmptyStatePanel
                          dateLabel={todayDateLabel || cockpit.today.date}
                          activeCampaignsCount={activeReverseIvrCampaignsCount}
                          activeCallsCount={activeReverseIvrCallsCount}
                          lastActivityLabel={lastActiveReverseIvrDayLastActivityLabel}
                          hasLastActiveDay={hasLastActiveReverseIvrDay}
                          onOpenLastActiveDay={() => setIsTodayReverseIvrFallbackOpen(true)}
                          t={t}
                          formatNumber={formatNumber}
                        />
                      ) : null}

                      {shouldRenderTodayReverseIvrRichSnapshot && displayedReverseIvrSummary ? (
                        <Stack gap={1.3} sx={{ mt: 1.4 }}>
                          {isShowingLastActiveReverseIvrDay ? (
                            <TodayReverseIvrLastActiveDayBanner
                              dateLabel={lastActiveReverseIvrDayDateLabel || lastActiveReverseIvrDay?.date || ''}
                              onBackToToday={() => setIsTodayReverseIvrFallbackOpen(false)}
                              t={t}
                            />
                          ) : null}

                          <Box
                            sx={{
                              display: 'grid',
                              gap: 1.1,
                              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                            }}
                          >
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.attemptedCalls')}
                              value={formatNumber(displayedReverseIvrSummary.attemptedCalls)}
                              accent={theme.palette.info.main}
                            />
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.answeredCalls')}
                              value={formatNumber(displayedReverseIvrSummary.answeredCalls)}
                              accent={theme.palette.info.main}
                            />
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.heardToEndRate')}
                              value={formatPercent(displayedReverseIvrSummary.heardToEndRate)}
                              accent={theme.palette.warning.main}
                              helper={t('pages:dashboard.todayReverseIvr.metrics.heardToEndHelper', {
                                value: formatNumber(displayedReverseIvrSummary.heardToEndCalls),
                              })}
                              highlighted
                            />
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.mappedRate')}
                              value={formatPercent(displayedReverseIvrSummary.mappedRate)}
                              accent={theme.palette.success.main}
                              helper={t('pages:dashboard.todayReverseIvr.metrics.mappedHelper', {
                                value: formatNumber(displayedReverseIvrSummary.mappedCalls),
                              })}
                              highlighted
                            />
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.dtmfRate')}
                              value={formatPercent(displayedReverseIvrSummary.dtmfRate)}
                              accent={theme.palette.primary.main}
                              helper={t('pages:dashboard.todayReverseIvr.metrics.dtmfHelper', {
                                value: formatNumber(displayedReverseIvrSummary.dtmfCalls),
                              })}
                            />
                            <TodayAiMetricCard
                              label={t('pages:dashboard.todayMetrics.timeoutRate')}
                              value={formatPercent(displayedReverseIvrSummary.timeoutRate)}
                              accent={theme.palette.error.main}
                              helper={t('pages:dashboard.todayReverseIvr.metrics.timeoutHelper', {
                                value: formatNumber(displayedReverseIvrSummary.timeoutCalls),
                              })}
                            />
                          </Box>

                          {!isShowingLastActiveReverseIvrDay ? (
                            <TodayReverseIvrRecentComparisonPanel
                              rows={todayReverseIvrComparisonRows}
                              loading={isTodayReverseIvrComparisonLoading}
                              error={isTodayReverseIvrComparisonError}
                              windowDays={todayReverseIvrComparisonWindowDays}
                              t={t}
                              formatNumber={formatNumber}
                              formatDecimal={formatDecimal}
                              formatPercent={formatPercent}
                            />
                          ) : null}

                          <TodayReverseIvrResultFunnelPanel
                            todayReverseIvr={displayedReverseIvrSummary}
                            t={t}
                            formatNumber={formatNumber}
                          />

                          <TodayReverseIvrDigitPanel
                            todayReverseIvr={displayedReverseIvrSummary}
                            t={t}
                            formatNumber={formatNumber}
                          />
                        </Stack>
                      ) : null}
                    </Box>
                  </>
                ) : null}

                {isAiProductView && shouldShowHumanBlockedState ? (
                  <>
                    {aiTodayScope ? <Divider /> : null}
                    <OperationalStatePanel
                      title={t('pages:dashboard.products.humanHandoff')}
                      description={telephonyBlockedLabel}
                    />
                  </>
                ) : null}

                {isAiProductView && displayedHumanAttendance ? (
                  <>
                    {(aiTodayScope || shouldShowHumanBlockedState) ? <Divider /> : null}
                    <Box>
                      <ProductSubsectionTitle
                        title={t('pages:dashboard.products.humanHandoff')}
                        subtitle={todayHumanHandoffSubsectionSubtitle}
                      />
                      <Stack gap={1.3} sx={{ mt: 1.4 }}>
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 1.1,
                            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                          }}
                        >
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.transfersStarted')}
                            value={formatNumber(displayedHumanAttendance.transfersStarted)}
                            accent={theme.palette.info.main}
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.humanAnsweredCalls')}
                            value={formatNumber(displayedHumanAttendance.humanAnsweredCalls)}
                            accent={theme.palette.success.main}
                            helper={t('pages:dashboard.todayMetrics.transfersAnsweredHelper', {
                              value: formatNumber(displayedHumanAttendance.transfersAnswered),
                            })}
                            highlighted
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.humanAnswerRate')}
                            value={formatNullablePercent(displayedHumanAttendance.humanAnswerRate, formatPercent, t('pages:dashboard.notAvailable'))}
                            accent={theme.palette.success.main}
                            highlighted
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.averageWait')}
                            value={formatNullableDuration(displayedHumanAttendance.averageWaitSeconds, formatDuration, t('pages:dashboard.notAvailable'))}
                            accent={theme.palette.warning.main}
                          />
                          <TodayAiMetricCard
                            label={t('pages:dashboard.todayMetrics.averageHandle')}
                            value={formatNullableDuration(displayedHumanAttendance.averageHandleSeconds, formatDuration, t('pages:dashboard.notAvailable'))}
                            accent={theme.palette.primary.main}
                          />
                          {displayedHumanAttendance.telephonyConsumedSeconds !== null ? (
                            <TodayAiMetricCard
                              label={
                                isShowingHistoricalHumanAttendance
                                  ? t('pages:dashboard.todayMetrics.telephonyMinutes')
                                  : t('pages:dashboard.todayMetrics.telephonyMinutesConsumed')
                              }
                              value={formatDuration(displayedHumanAttendance.telephonyConsumedSeconds)}
                              accent={theme.palette.info.main}
                            />
                          ) : null}
                        </Box>
                        {!isShowingHistoricalHumanAttendance ? (
                          <TodayHumanAttendanceComparisonPanel
                            humanAttendance={displayedHumanAttendance}
                            loading={isTodayHumanAttendanceComparisonLoading}
                            error={isTodayHumanAttendanceComparisonError}
                            windowDays={todayHumanAttendanceComparisonWindowDays}
                            t={t}
                            formatNumber={formatNumber}
                            formatDecimal={formatDecimal}
                            formatDuration={formatDuration}
                            formatPercent={formatPercent}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                  </>
                ) : null}

                {(
                  isAiProductView
                  && !aiTodayScope
                  && !displayedHumanAttendance
                  && !shouldShowHumanBlockedState
                ) || (
                  isReverseIvrProductView
                  && !reverseIvrToday
                ) ? (
                  <EmptyPanelState label={t('pages:dashboard.empty.today')} />
                ) : null}
              </Stack>
            </Paper>

	          </Stack>
	          </Box>
	        ) : null}
      </Box>
    </>
  );
}
